import moment from 'moment-timezone';
import { DateRangeInt } from 'src/dash/utils/dateTimeRanges';

import { apiFactory, apiFactory2, headers } from '~/src/dash/api/api-factory';
import { getToken } from '~/src/dash/api/auth/auth';
import {
  IBidderGroup,
  transformBidderGroup,
  transformBidderGroupWrapper,
} from '~/src/dash/api/bidder-group';
import {
  IAdserverCampaign,
  marshalAdserverCampaign,
  transformCampaign,
} from '~/src/dash/api/campaign/campaign';
import { transformChannelGroup, transformChannelGroupWrapper } from '~/src/dash/api/channel-group';
import { IChannelGroupLabel } from '~/src/dash/api/channel-group-label';
import {
  ESuperSiteTargetingLogicOperator,
  IChannelCronSummary,
  IChannelPerformanceSortByOptions,
  IEndpointRequestScore,
  ISuperSiteTargetingObject,
} from '~/src/dash/api/channels/channels_models';
import {
  transformChannelCronSummary,
  transformChannelPerformanceSortByOptions,
  transformEndpointRequestScore,
} from '~/src/dash/api/channels/channels_utils';
import { transformCountryGroup } from '~/src/dash/api/country-group';
import { IDeal, transformDealFromApi } from '~/src/dash/api/deals';
import { ECurrencyType, IExchangeRate, transformExchangeRate } from '~/src/dash/api/exchange-rates';
import {
  EHeaderBidder_DealAtTargeting,
  EHeaderBidder_InventoryType,
  EHeaderBidder_PodSequenceTargeting,
  EHeaderBidder_PositionTargeting,
  EHeaderBidder_ShareOfVoiceType,
  EHeaderBidder_TargetedAdBreak,
} from '~/src/dash/api/headerbidders/headerbidders_models';
import { IInventoryRoute, transformInventoryRoute } from '~/src/dash/api/inventory_routes';
import { INetworkPublisherMap, transformNetworkPublisherMapWrapper } from '~/src/dash/api/network';
import { IRole as IPermissionRole } from '~/src/dash/api/permissions/model';
import { uploadResourceFactory } from '~/src/dash/api/resource';
import {
  parseArray,
  parseBoolFalse,
  parseBoolStringTrue,
  parseBoolTrue,
  parseFloat,
  parseJSON,
  parseNumber,
  parseObject,
  parseObjectV2,
  parseString,
  toArray,
  toString,
  transformModel,
  transformOnlyIds,
} from '~/src/dash/api/resource-transforms';
import {
  addDomainAndTokenToUrl,
  addQueryParams,
  createAnalyticsUrl,
  createUrl,
  createUrlWithParam,
  IHttpResponse,
  restDelete,
  restGet,
  restPatch,
  restPost,
  restPut,
} from '~/src/dash/api/rest';
import { IBidSaverConfig, transformBidSaverConfig } from '~/src/dash/api/site';
import { Auth, MasqueradeAuth } from '~/src/dash/api/transform-auth';
import { appendResellerID } from '~/src/dash/api/transform-reseller';
import { EBool } from '~/src/dash/api/types/bool';
import {
  FlexibleBiddingType,
  IFlexibleBiddingThreshold,
  transformFlexibleBiddingThreshold,
} from '~/src/dash/api/types/flexible_bidding';
import { TimeUnit } from '~/src/dash/api/types/time_unit';
import { ReportTypeID } from '~/src/dash/apiV2/reporting/reportTypes';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { getApiDomain, getPbsUS } from '~/src/dash/resellers/utils/importResellerConfig';
import { IMenuList } from '~/src/dash/types/menuList';
import { ELogicalOperator } from '~/src/dash/utils/options';
import { EPublisherType } from '~/src/dash/views/platform-connect/common';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { ct } from '~/src/dash/publicaUI';
import { ESupportTier } from '~/src/dash/api/publisher';

// ===================================================================
// Common Types:
//    Use these over redefining the type
// ===================================================================

export type TargetBoolType = number;
export const TargetBoolTypeAll: TargetBoolType = 0;
export const TargetBoolTypeFalse: TargetBoolType = 1;
export const TargetBoolTypeTrue: TargetBoolType = 2;

export type ResourceEnable = string;
export const ResourceEnableUnknown: ResourceEnable = '0';
export const ResourceEnableDisabled: ResourceEnable = '1';
export const ResourceEnableEnabled: ResourceEnable = '2';

export type ResourceActive = number;
export const ResourceActiveUnknown: ResourceActive = 0;
export const ResourceActiveDisabled: ResourceActive = 1;
export const ResourceActiveEnabled: ResourceActive = 2;

export type ResourceOn = number;
export const ResourceOnUnknown: RewriteAttrStrategy = 0;
export const ResourceOnOff: RewriteAttrStrategy = 1;
export const ResourceOnOn: RewriteAttrStrategy = 2;

// IMPORTANT: make sure the following enum match what's on platform → model.proto → HeaderBidder → PrivateAuctionType.
export type PrivateAuctionType = number;
export const PrivateAuctionTypeUnknown: PrivateAuctionType = 0;
export const PrivateAuctionTypeDisabled: PrivateAuctionType = 1;
export const PrivateAuctionTypeEnabled: PrivateAuctionType = 2;

export type MatchDealIDType = number;
export const MatchDealIDTypeUnknown: MatchDealIDType = 0;
export const MatchDealIDTypeDisabled: MatchDealIDType = 1;
export const MatchDealIDTypeEnabled: MatchDealIDType = 2;

// Headerbidder IsPublicaTagFallbackEnabled
export type PublicaTagFallback = number;
export const PublicaTagFallbackUnknown: PublicaTagFallback = 0;
export const PublicaTagFallbackDisabled: PublicaTagFallback = 1;
export const PublicaTagFallbackEnabled: PublicaTagFallback = 2;

// Headerbidder IsFallbackBackfillEnabled
export type IsFallbackBackfillEnabled = number;
export const FallbackBackfillUnknown: IsFallbackBackfillEnabled = 0;
export const FallbackBackfillDisabled: IsFallbackBackfillEnabled = 1;
export const FallbackBackfillEnabled: IsFallbackBackfillEnabled = 2;

export type TargetingLogic = number;
export const TargetingLogicOR: TargetingCoppa = 0;
export const TargetingLogicAND: TargetingCoppa = 1;

export type TargetingCCPA = number;
export const TargetingCCPAAll: TargetingCCPA = 0;
export const TargetingCCPAOptOut: TargetingCCPA = 1;
export const TargetingCCPANonOptOut: TargetingCCPA = 2;

export type ExtendedIDTargetingType = number;
export const ExtendedIDTargetingTypeDisabled: ExtendedIDTargetingType = 1;
export const ExtendedIDTargetingTypeEnabledIfPresent: ExtendedIDTargetingType = 2;
export const ExtendedIDTargetingTypeEnabledIfMissing: ExtendedIDTargetingType = 3;

// ===================================================================
// ===================================================================

type FIXTYPE = any;

/**
 * To remove confusion from checking types, all model should specify their property types as either string, string[], object, or object[].
 * Whenever working with the object, this will remove the ambiguity whether and ID is a string or a number.
 */

function transformJSON(fromObj: any) {
  try {
    if (fromObj) {
      return JSON.parse(fromObj);
    }
  } catch (e) {
    console.error(e.stack);
  }
  return [];
}

export interface IUser {
  ID: string;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  Role: IRole;
  RoleID: number;
  Username: string;
  FirstName: string;
  LastName: string;
  JobPosition: string;
  Title: string;
  EstimatedCPM: number;
  EstimatedCPMInstream: number;
  EstimatedCPMOutstream: number;
  EstimatedCPMDisplay: number;
  PermissionRole: IPermissionRole;
  Timezone: string;
  HasMultiplePublisherAssociations: EBool;
}

export interface IUserInvite {
  ID: string;
  Email: string;
  Token: string;
}

export interface INameID {
  ID: number;
  Name: string;
}

export interface IUserFromUserToPublisherAssociation {
  UserID: number;
  PublisherID: number;
  PublisherName: string;
  Email: string;
  FirstName: string;
  LastName: string;
  PermissionsRoleID: number;
  PermissionsRoleName: string;
  RoleID: number;
  RoleName: string;
  Timezone: string;
}

export const transformUser = transformModel<IUser>({
  ID: parseString(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  Publisher: parseObject(['Publisher', 'publisher'], transformPublisherWrapper),

  Role: parseObject(['Role', 'role'], transformRoleWrapper),
  RoleID: parseNumber(['RoleID', 'roleid']),

  Username: ['Username', 'username'],
  FirstName: ['FirstName', 'firstname'],
  LastName: ['LastName', 'lastname'],
  JobPosition: ['JobPosition', 'jobposition'],
  Title: ['Title', 'title'],
  EstimatedCPM: parseNumber(['EstimatedCPM', 'estimatedcpm'], 7),
  EstimatedCPMInstream: parseNumber(['EstimatedCPMInstream', 'estimatedcpminstream'], 7),
  EstimatedCPMOutstream: parseNumber(['EstimatedCPMOutstream', 'estimatedcpmoutstream'], 3),
  EstimatedCPMDisplay: parseNumber(['EstimatedCPMDisplay', 'estimatedcpmdisplay'], 1),
  PermissionRole: ['PermissionRole', 'permission_role'],
  Timezone: ['Timezone', 'timezone'],
  HasMultiplePublisherAssociations: parseNumber(['HasMultiplePublisherAssociations'], 1),
});

export const transformUserInvite = transformModel<IUserInvite>({
  ID: parseString(['ID', 'id']),
  Email: parseString(['Email', 'email']),
  Token: parseString(['Token', 'token']),
});

export const transformUsersFromUserToPublisherAssociation =
  transformModel<IUserFromUserToPublisherAssociation>({
    UserID: parseNumber(['UserID']),
    PublisherID: parseNumber(['PublisherID']),
    PublisherName: parseString(['PublisherName']),
    Email: parseString(['Email']),
    FirstName: parseString(['FirstName']),
    LastName: parseString(['LastName']),
    PermissionsRoleID: parseNumber(['PermissionsRoleID']),
    PermissionsRoleName: parseString(['PermissionsRoleName']),
    RoleID: parseNumber(['RoleID']),
    RoleName: parseString(['RoleName']),
    Timezone: ['Timezone', 'timezone'],
  });

export enum MRMEnrichmentMode {
  MRM_ENRICHMENT_MODE_UNKNOWN = 0,
  MRM_ENRICHMENT_MODE_DISABLED = 1,
  MRM_ENRICHMENT_MODE_STANDARD = 2, // everything on: labeling + decisioning
  MRM_ENRICHMENT_MODE_LABELING = 3,
  MRM_ENRICHMENT_MODE_DECISIONING = 4,
}

export interface IPublisher {
  [key: string]: any;

  ID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  NetworkPublisherMaps: INetworkPublisherMap[];
  BidderGroups: IBidderGroup[];
  ChannelGroups: IChannelGroup[];
  Name: string;
  Domain: string;
  UUID: string;
  BillingCompanyName: string;
  BillingAddress: string;
  BillingCity: string;
  BillingPostalCode: string;
  BillingCountry: string;
  BillingContact: string;
  BillingEmail: string;
  BillingTel: string;
  Reports: IReport[];
  RecoveryTerms1: boolean;
  RecoveryTerms2: boolean;
  CustomContract: boolean;
  RecoveryTermsEffectiveDate: string;
  DefaultRegion: string;
  HeaderBiddingEnabled: number;
  Timezone: string;
  LiverampEnabled: EBool;
  LiverampClientNames: string;
  LiverampClientNamesFirstParty: string;
  LiverampDataStore: string;
  LiverampOTTHubCustomerID: string;
  LiverampDistributionManagerID: string;
  CadentEnabled: EBool;
  CadentTag: string;
  TransunionEnabled: EBool;
  TransunionAPIKey: string;
  FreeTrialActive: string;
  FreeTrialStartDate: string;
  FreeTrialEndDate: string;
  RokuSeatIDStr: string;
  DDMonitoring: boolean;
  Localization: ILocalization;
  NativeVastEnabled: EBool;
  NetworkEnabled: EBool;
  ResellerID: number;
  CurrencyDisplayed: ECurrencyType;
  ExchangeRate: IExchangeRate;
  OptInCadent: EBool;
  OptInLiveramp: EBool;
  OptInTransunion: EBool;
  OptInSharingDataCadent: EBool;
  OptInSharingDataLiveramp: EBool;
  OptInSharingDataTransunion: EBool;
  OptInStoringDataCadent: EBool;
  OptInStoringDataLiveramp: EBool;
  OptInStoringDataTransunion: EBool;
  Active: ResourceActive;
  BidderTier1CustomName: string;
  BidderTier2CustomName: string;
  BidderTier3CustomName: string;
  BidderTier4CustomName: string;
  BidderTier5CustomName: string;
  BidderTier6CustomName: string;
  BidderTier7CustomName: string;
  BidderTier8CustomName: string;
  BidderTier9CustomName: string;
  BidderTier10CustomName: string;
  EnableIASDataSharing: EBool;
  IASDataSharing: string[];
  EnableDoubleVerifyDataSharing: EBool;
  DoubleVerifyDataSharing: string[];
  ExcludedReportingDimensions: string[];
  PublisherDevelopmentManager: string;
  SupportTier: ESupportTier;
  SolutionsArchitect: string;
  ContextualSegmentEnabled: EBool;
  AllowPublisherIrisAltContentID: EBool;
  AllowPublisherToEnableEleaAI: EBool;
  PremiumFeaturesSelectionEleaAI: string;
  EnableIASFirstPartyVASTTracking: EBool;
  IASAnID: number;
  IASTeamID: number;
  EnableIASThirdPartyVASTTracking: EBool;
  IrisClientToken: string;
  IrisContextualSegmentsEnabled: EBool;
  IASContextualSegmentsEnabled: EBool;
  ComscoreContextualSegmentsEnabled: EBool;
  EnableAllCreativeScanForEleaAI: EBool;
  EnablePlatformMarketplace: EBool;
  PublisherType: EPublisherType;
  LogSamplingEnabled: EBool;
  EnableDirectLogin: EBool;
  OpenPathEnabled: EBool;
  SamsungDMPEnabled: EBool;
  SamsungDMPDataStore: string;
  EnableGoogleAdxSSLCert: EBool;
  DisableCreativeBlocking: EBool;
  EnableAPSDedup: EBool;
  PublisherAccessKey: string;
  PublisherSecretKey: string;
  RateLimitTier: number;
  OnePlusXDMPEnabled: EBool;
  SmartFloorsEnabled: EBool;
  IsResellerPublisher: EBool;
  EnableCreativeQualityAssurance: EBool;
  EnableOtherCurrency: EBool;
  EnableAPSDemand: EBool;
  EnableBulkAPIs: EBool;
  EnablePublicaVault: EBool;
  EnableTrafficShaping: EBool;
  EnableSponsorshipMode: EBool;
  DefaultPublicaVaultConfig: IPublicaVaultConfig;
  MRMEnrichmentMode: MRMEnrichmentMode;
  EnableIASIVTBlocklist: EBool;
  IASIVTBlocklistTargetingAllInventory: EBool;
  IASIVTBlocklistTargetingHeaderbidder: EBool;
  IASIVTBlocklistSelectedBidderIDs: number[];
  EnableGatewayBidders: EBool;
  OptableTenantName: string;
  OptableDCNSource: string;
  OptableAPIKey: string;
  IsParentPublisher: EBool;
  ParentPublisherID: number;
  InventoryRuleVersion: number;
  InventorySmartFloorsEnabled: EBool;
}

export type FreeTrialStatus = string;
export const FreeTrialStatusUnknown: FreeTrialStatus = '0';
export const FreeTrialStatusDisabled: FreeTrialStatus = '1';
export const FreeTrialStatusEnabled: FreeTrialStatus = '2';

export const convertStringToPublisherType = (type: string): EPublisherType => {
  switch (type) {
    case '1':
      return EPublisherType.PUBLISHER_TYPE_BUYER;
    case '2':
      return EPublisherType.PUBLISHER_TYPE_SELLER;
    case '3':
      return EPublisherType.PUBLISHER_TYPE_BOTH;
    default:
      return EPublisherType.PUBLISHER_TYPE_NONE;
  }
};

export interface IDemandSourceUIToDBName {
  ID: number;
  DBName: string;
  UIName: string;
}

export interface IDemandSourceNameMap {
  [key: string]: string;
}

export const transformDemandSourceUIToDBName = transformModel<IDemandSourceUIToDBName>({
  ID: 'ID',
  DBName: 'DBName',
  UIName: 'UIName',
});

export interface ISitesInfo {
  ID: number;
  Name: string;
  BidFloor?: number;
}

export interface IChannelGroup {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;
  PublisherID: string;
  Name: string;
  ChannelIDs: string;
  Sites: ISite[];
  SitesInfo: ISitesInfo[];
  DefaultWeight: number;
  ChannelGroupLabels: IChannelGroupLabel[];
  HeaderBidders: IHeaderBidder[];
  Campaigns: IAdserverCampaign[];
}

export interface IChannelList {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;
  PublisherID: string;
  Name: string;
  ChannelIDs: string;
  ChannelGroupIDs: string;
  Sites: ISite[];
  HeaderBidders: IHeaderBidder[];
  SitesInfo: ISitesInfo[];
}

export interface ICountryGroup {
  ID: number;
  PublisherID: number;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;
  Name: string;
  CountryIsos: string;
}

export const transformPublicaVaultConfig = transformModel<IPublicaVaultConfig>({
  EnableByDefault: parseBoolTrue(['EnableByDefault']),
  ExcludedDemandSources: parseString(['ExcludedDemandSources']),
  ExcludedBidderIDs: parseString(['ExcludedBidderIDs']),
  DeviceId: parseBoolTrue(['DeviceId']),
  SiteId: parseBoolTrue(['SiteId']),
  IP: parseBoolTrue(['IP']),
  TTL: parseNumber(['TTL']),
  ContentRating: parseBoolTrue(['ContentRating']),
  ContentGenre: parseBoolTrue(['ContentGenre']),
  ContentId: parseBoolTrue(['ContentId']),
  AppBundle: parseBoolTrue(['AppBundle']),
  StoreBelowFloorBids: parseBoolTrue(['StoreBelowFloorBids']),
  MaxUnderFloorType: parseNumber(['MaxUnderFloorType']),
  MaxUnderFloorValue: parseFloat(['MaxUnderFloorValue']),
  MinBidFloor: parseFloat(['MinBidFloor']),
});

export const transformPublisher = transformModel<IPublisher>({
  ID: parseString(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  Reseller: parseObject(['Reseller', 'reseller'], transformResellerWrapper),
  BidderGroups: parseArray(['BidderGroups', 'bidder_groups'], transformBidderGroupWrapper),
  ChannelGroups: parseArray(['ChannelGroups', 'channel_groups'], transformChannelGroupWrapper),
  NetworkPublisherMaps: parseArray(
    ['NetworkPublisherMaps', 'network_publisher_maps'],
    transformNetworkPublisherMapWrapper,
  ),
  Name: parseString(['Name', 'name']),
  Domain: parseString(['Domain', 'domain']),
  UUID: parseString(['UUID', 'uuid']),
  BillingCompanyName: parseString(['BillingCompanyName', 'billingcompanyname']),
  BillingAddress: parseString(['BillingAddress', 'billingaddress']),
  BillingCity: parseString(['BillingCity', 'billingcity']),
  BillingPostalCode: parseString(['BillingPostalCode', 'billingpostalcode']),
  BillingCountry: parseString(['BillingCountry', 'billingcountry']),
  BillingContact: parseString(['BillingContact', 'billingcontact']),
  BillingEmail: parseString(['BillingEmail', 'billingemail']),
  BillingTel: parseString(['BillingTel', 'billingtel']),
  Reports: parseArray(['Reports', 'reports'], (_fromObjValue: any) => {
    return transformReport(_fromObjValue);
  }),
  RecoveryTerms1: parseBoolTrue(['RecoveryTerms1', 'recoveryterms1']),
  RecoveryTerms2: parseBoolTrue(['RecoveryTerms2', 'recoveryterms2']),
  CustomContract: parseBoolTrue(['CustomContract', 'customcontract']),
  RecoveryTermsEffectiveDate: parseString([
    'RecoveryTermsEffectiveDate',
    'recoverytermseffectivedate',
  ]),
  DefaultRegion: parseString(['DefaultRegion', 'defaultregion']),
  HeaderBiddingEnabled: parseNumber(['HeaderBiddingEnabled', 'headerbiddingenabled'], -1),
  Timezone: parseString(['Timezone', 'timezone']),
  LiverampEnabled: parseNumber(['LiverampEnabled', 'liverampenabled']),
  LiverampClientNames: parseString(['LiverampClientNames', 'liverampclientnames']),
  LiverampClientNamesFirstParty: parseString([
    'LiverampClientNamesFirstParty',
    'liverampclientnamesfirstparty',
  ]),
  LiverampDataStore: parseString(['LiverampDataStore', 'liverampdatastore']),
  LiverampOTTHubCustomerID: parseString(['LiverampOTTHubCustomerID', 'liverampotthubcustomerid']),
  LiverampDistributionManagerID: parseString([
    'LiverampDistributionManagerID',
    'liverampdistributionmanagerid',
  ]),
  SamsungDMPEnabled: parseNumber(['SamsungDMPEnabled', 'samsungdmpenabled']),
  SamsungDMPDataStore: parseString(['SamsungDMPDataStore', 'samsungdmpdatastore']),
  CadentEnabled: parseNumber(['CadentEnabled']),
  CadentTag: parseString(['CadentTag']),
  TransunionEnabled: parseNumber(['TransunionEnabled']),
  TransunionAPIKey: parseString(['TransunionAPIKey']),
  FreeTrialActive: parseString(['FreeTrialActive', 'freetrialactive']),
  FreeTrialStartDate: parseString(['FreeTrialStartDate']),
  FreeTrialEndDate: parseString(['FreeTrialEndDate']),
  RokuSeatIDStr: parseString(['RokuSeatIDStr', 'rokuseatidstr']),
  DDMonitoring: parseBoolTrue(['DDMonitoring', 'ddmonitoring']),
  Localization: parseObject(['Localization', 'localization'], transformLocalizationWrapper),
  NativeVastEnabled: parseNumber('NativeVastEnabled'),
  NetworkEnabled: parseNumber('NetworkEnabled'),
  ResellerID: parseNumber('ResellerID'),
  CurrencyDisplayed: parseNumber('CurrencyDisplayed'),
  ExchangeRate: parseObject(['ExchangeRate'], transformExchangeRate),
  OptInCadent: parseNumber('OptInCadent'),
  OptInLiveramp: parseNumber('OptInLiveramp'),
  OptInTransunion: parseNumber('OptInTransunion'),
  OptInSharingDataCadent: parseNumber('OptInSharingDataCadent'),
  OptInSharingDataLiveramp: parseNumber('OptInSharingDataLiveramp'),
  OptInSharingDataTransunion: parseNumber('OptInSharingDataTransunion'),
  OptInStoringDataCadent: parseNumber('OptInStoringDataCadent'),
  OptInStoringDataLiveramp: parseNumber('OptInStoringDataLiveramp'),
  OptInStoringDataTransunion: parseNumber('OptInStoringDataTransunion'),
  Active: parseNumber(['Active']),
  BidderTier1CustomName: parseString(['BidderTier1CustomName']),
  BidderTier2CustomName: parseString(['BidderTier2CustomName']),
  BidderTier3CustomName: parseString(['BidderTier3CustomName']),
  BidderTier4CustomName: parseString(['BidderTier4CustomName']),
  BidderTier5CustomName: parseString(['BidderTier5CustomName']),
  BidderTier6CustomName: parseString(['BidderTier6CustomName']),
  BidderTier7CustomName: parseString(['BidderTier7CustomName']),
  BidderTier8CustomName: parseString(['BidderTier8CustomName']),
  BidderTier9CustomName: parseString(['BidderTier9CustomName']),
  BidderTier10CustomName: parseString(['BidderTier10CustomName']),
  EnableIASDataSharing: parseNumber('EnableIASDataSharing', EBool.EBool_FALSE),
  IASDataSharing: parseString(['IASDataSharing', 'iasdatasharing'], ','),
  EnableDoubleVerifyDataSharing: parseNumber('EnableDoubleVerifyDataSharing', EBool.EBool_FALSE),
  DoubleVerifyDataSharing: parseString(['DoubleVerifyDataSharing', 'doubleverifydatasharing'], ','),
  ExcludedReportingDimensions: parseString(['ExcludedReportingDimensions'], ','),
  PublisherDevelopmentManager: parseString([
    'PublisherDevelopmentManager',
    'publisher_development_manager',
  ]),
  SupportTier: parseNumber('SupportTier'),
  SolutionsArchitect: parseString(['SolutionsArchitect', 'solutions_architect']),
  ContextualSegmentEnabled: parseNumber('ContextualSegmentEnabled'),
  AllowPublisherIrisAltContentID: parseNumber('AllowPublisherIrisAltContentID'),
  AllowPublisherToEnableEleaAI: parseNumber('AllowPublisherToEnableEleaAI'),
  PremiumFeaturesSelectionEleaAI: parseString('PremiumFeaturesSelectionEleaAI'),
  EnableIASFirstPartyVASTTracking: parseNumber(
    'EnableIASFirstPartyVASTTracking',
    EBool.EBool_FALSE,
  ),
  IASAnID: parseNumber('IASAnID'),
  IASTeamID: parseNumber('IASTeamID'),
  EnableIASThirdPartyVASTTracking: parseNumber(
    'EnableIASThirdPartyVASTTracking',
    EBool.EBool_FALSE,
  ),
  IrisClientToken: parseString('IrisClientToken'),
  IrisContextualSegmentsEnabled: parseNumber('IrisContextualSegmentsEnabled'),
  IASContextualSegmentsEnabled: parseNumber('IASContextualSegmentsEnabled'),
  ComscoreContextualSegmentsEnabled: parseNumber('ComscoreContextualSegmentsEnabled'),
  EnableExtendedRewrites: parseNumber('EnableExtendedRewrites'),
  EnableAllCreativeScanForEleaAI: parseNumber('EnableAllCreativeScanForEleaAI'),
  EnableExtendedRewritesPostBid: parseNumber('EnableExtendedRewritesPostBid'),
  EnablePlatformMarketplace: parseNumber('EnablePlatformMarketplace', EBool.EBool_FALSE),
  PublisherType: parseNumber('PublisherType'),
  LogSamplingEnabled: parseNumber(['LogSamplingEnabled'], EBool.EBool_UNKNOWN),
  EnableDirectLogin: parseNumber(['EnableDirectLogin', 'enabledirectlogin']),
  OpenPathEnabled: parseNumber('OpenPathEnabled', EBool.EBool_FALSE),
  EnableGoogleAdxSSLCert: parseNumber('EnableGoogleAdxSSLCert', EBool.EBool_FALSE),
  DisableCreativeBlocking: parseNumber('DisableCreativeBlocking', EBool.EBool_FALSE),
  EnableAPSDedup: parseNumber('EnableAPSDedup', EBool.EBool_FALSE),
  PublisherAccessKey: parseString(['PublisherAccessKey', 'publisheraccesskey']),
  PublisherSecretKey: parseString(['PublisherSecretKey', 'publishersecretkey']),
  RateLimitTier: parseNumber(['RateLimitTier', 'rate_limit_tier']),
  OnePlusXDMPEnabled: parseNumber(['OnePlusXDMPEnabled', 'onepluxdmpenabled'], EBool.EBool_FALSE),
  SmartFloorsEnabled: parseNumber('SmartFloorsEnabled', EBool.EBool_FALSE),
  IsResellerPublisher: parseNumber('IsResellerPublisher', EBool.EBool_FALSE),
  EnableCreativeQualityAssurance: parseNumber('EnableCreativeQualityAssurance', EBool.EBool_FALSE),
  EnableOtherCurrency: parseNumber('EnableOtherCurrency', EBool.EBool_FALSE),
  EnableAPSDemand: parseNumber('EnableAPSDemand', EBool.EBool_FALSE),
  EnableBulkAPIs: parseNumber('EnableBulkAPIs', EBool.EBool_FALSE),
  EnablePublicaVault: parseNumber('EnablePublicaVault', EBool.EBool_FALSE),
  EnableTrafficShaping: parseNumber('EnableTrafficShaping', EBool.EBool_FALSE),
  EnableSponsorshipMode: parseNumber('EnableSponsorshipMode', EBool.EBool_FALSE),
  MRMEnrichmentMode: parseNumber(
    'MRMEnrichmentMode',
    MRMEnrichmentMode.MRM_ENRICHMENT_MODE_DISABLED,
  ),
  DefaultPublicaVaultConfig: parseObject(
    ['DefaultPublicaVaultConfig'],
    transformPublicaVaultConfig,
  ),
  EnableIASIVTBlocklist: parseNumber('EnableIASIVTBlocklist', EBool.EBool_FALSE),
  IASIVTBlocklistTargetingAllInventory: parseNumber(
    'IASIVTBlocklistTargetingAllInventory',
    EBool.EBool_FALSE,
  ),
  IASIVTBlocklistTargetingHeaderbidder: parseNumber(
    'IASIVTBlocklistTargetingHeaderbidder',
    EBool.EBool_FALSE,
  ),
  IASIVTBlocklistSelectedBidderIDs: parseArray(
    'IASIVTBlocklistSelectedBidderIDs',
    (obj: unknown): number => {
      return obj as number;
    },
  ),
  EnableGatewayBidders: parseNumber('EnableGatewayBidders', EBool.EBool_FALSE),
  OptableTenantName: parseString('OptableTenantName'),
  OptableDCNSource: parseString('OptableDCNSource'),
  OptableAPIKey: parseString('OptableAPIKey'),
  IsParentPublisher: parseNumber('IsParentPublisher', EBool.EBool_FALSE),
  ParentPublisherID: parseNumber('ParentPublisherID'),
  InventoryRuleVersion: parseNumber('InventoryRuleVersion'),
  InventorySmartFloorsEnabled: parseNumber('InventorySmartFloorsEnabled', EBool.EBool_FALSE),
});

export function transformPublisherWrapper(fromObj: any): IPublisher {
  return transformPublisher(fromObj);
}

export interface IDistribution {
  ID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  Domain: IDomain;
  Redirects: IRedirect[];
  Deterrents: IDeterrents[];
  CloudfrontDomain: string;
  CloudfrontID: string;
  UUID: string;
  Status: string;
  Compromised: boolean;
  State: string;

  PendingAssocToSiteID: string;
  PendingAssocToSiteTime: string;
  PendingAssocToAdunitID: string;
  PendingAssocToAdunitTime: string;
}

export const transformDistribution = transformModel<IDistribution>({
  ID: parseString(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  Domain: parseObject(['Domain', 'domain'], transformDomainWrapper),
  Redirects: parseArray(['Redirects', 'redirects'], transformRedirectWrapper),
  Deterrents: parseArray(['Deterrents', 'deterrents'], transformDeterrentsWrapper),
  CloudfrontDomain: parseString(['CloudfrontDomain', 'cloudfrontdomain']),
  CloudfrontID: parseString(['CloudfrontID', 'cloudfrontid']),
  UUID: parseString(['UUID', 'uuid']),
  Status: parseString(['Status', 'status']),
  Compromised: parseBoolTrue(['Compromised', 'compromised']),
  State: parseString(['State', 'state']),

  PendingAssocToSiteID: parseNumber(['PendingAssocToSiteID', 'pendingassoctositeid'], -1),
  PendingAssocToSiteTime: parseString(['PendingAssocToSiteTime', 'pendingassoctositetime']),
  PendingAssocToAdunitID: parseNumber(['PendingAssocToAdunitID', 'pendingassoctoadunitid'], -1),
  PendingAssocToAdunitTime: parseString(['PendingAssocToAdunitTime', 'pendingassoctoadunittime']),
});
function transformDistributionWrapper(_fromObjValue: any) {
  return transformDistribution(_fromObjValue);
}

export interface IDomain {
  ID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;
  // TODO: FIXME
}

export const transformDomain = transformModel<IDomain>({
  ID: parseString(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  // TODO: FIXME
});
function transformDomainWrapper(_fromObjValue: any) {
  return transformDomain(_fromObjValue);
}

export interface IRedirect {
  ID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  // TODO: FIXME
}

export const transformRedirect = transformModel<IRedirect>({
  ID: parseString(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  // TODO: FIXME
});
function transformRedirectWrapper(_fromObjValue: any) {
  return transformRedirect(_fromObjValue);
}

export interface IDeterrents {
  ID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  // TODO: FIXME
}

export const transformDeterrents = transformModel<IDeterrents>({
  ID: parseString(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  // TODO: FIXME
});
function transformDeterrentsWrapper(_fromObjValue: any) {
  return transformDeterrents(_fromObjValue);
}

export interface IGPTUnit {
  ID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  // TODO:  FIXME
}

export const transformIGPTUnit = transformModel<IGPTUnit>({
  ID: parseString(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  // TODO:  FIXME
});
function transformIGPTUnitWrapper(_fromObjValue: any) {
  return transformIGPTUnit(_fromObjValue);
}

export interface ITarget {
  ID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  // TODO:  FIXME
}

export const transformTarget = transformModel<ITarget>({
  ID: parseString(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  // TODO:  FIXME
});
function transformTargetWrapper(_fromObjValue: any) {
  return transformTarget(_fromObjValue);
}

export interface IAdunit {
  ID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  SiteID: string;
  UUID: string;
  Name: string;
  AdSources: IAdSource[];
  Selector: string;
  SelectorVideo: string;
  SelectorAdcontainer: string;
  ClickToPlay: boolean;
  PlayerType: string;
  PlayerPlay: string;
  PlayerPause: string;
  PlayerHide: string;
  PlayerShow: string;
  PlayerWidth: string;
  PlayerHeight: string;
  PlayerSwap: string;
  RecoveryPercentage: number;
  BilledCPM: number;
  TimeupdateTimeout: number;
  ViewportCheck: boolean;
  FocusCheck: boolean;
  AllowFullscreen: boolean;
  VideoMuted: boolean;
  VideoWidth: number;
  VideoHeight: number;
  Position: number;
  VpaidControls: boolean;
  Enabled: boolean;
  VpaidInitadTimeout: number;
  FbPlacementID: string;
  VpaidErroradTimeout: number;
  RewriterPath: string;
  DisableMetrics: boolean;
  WatcherFunc: string;
  BackfillScript: string;
  Onload: string;
  Waterfall: boolean;
  ProxyOverride: string;
  TestModuleURL: string;
  CustomParamsFunc: string;
  AutoAd: number;
  PlayAdFunc: string;
  GetDOMElement: string;
  IgnoreMuteCheck: boolean;
  WriteBuffer: number;
  Region: string;
  RegionPrefix: string;
  RegionPrefixEnabled: number;
  DistributionID: string;
  Distribution: IDistribution;
  CookieStrategy: number;
  DynamicHostname: number;
}

export const transformAdunit = transformModel<IAdunit>({
  ID: parseString(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  SiteID: parseNumber(['SiteID', 'siteid'], -1),
  UUID: parseString(['UUID', 'uuid']),
  Name: parseString(['Name', 'name']),
  AdSources: parseArray(['AdSources', 'adsources'], transformAdSourceWrapper),
  Selector: parseString(['Selector', 'selector']),
  SelectorVideo: parseString(['SelectorVideo', 'selectorvideo']),
  SelectorAdcontainer: parseString(['SelectorAdcontainer', 'selectoradcontainer']),
  ClickToPlay: parseBoolTrue(['ClickToPlay', 'clicktoplay']),
  PlayerType: parseString(['PlayerType', 'playertype']),
  PlayerPlay: parseString(['PlayerPlay', 'playerplay']),
  PlayerPause: parseString(['PlayerPause', 'playerpause']),
  PlayerHide: parseString(['PlayerHide', 'playerhide']),
  PlayerShow: parseString(['PlayerShow', 'playershow']),
  PlayerWidth: parseString(['PlayerWidth', 'playerwidth']),
  PlayerHeight: parseString(['PlayerHeight', 'playerheight']),
  PlayerSwap: parseString(['PlayerSwap', 'playerswap']),
  RecoveryPercentage: parseNumber(['RecoveryPercentage', 'recoverypercentage'], 100),
  BilledCPM: parseNumber(['BilledCPM', 'billedcpm'], 7),
  TimeupdateTimeout: parseNumber(['TimeupdateTimeout', 'timeupdatetimeout'], 10000),
  ViewportCheck: parseBoolTrue(['ViewportCheck', 'viewportcheck']),
  FocusCheck: parseBoolTrue(['FocusCheck', 'focuscheck']),
  AllowFullscreen: parseBoolTrue(['AllowFullscreen', 'allowfullscreen']),
  VideoMuted: parseBoolTrue(['VideoMuted', 'videomuted']),
  VideoWidth: parseNumber(['VideoWidth', 'videowidth'], 0),
  VideoHeight: parseNumber(['VideoHeight', 'videoheight'], 0),
  Position: parseNumber(['Position', 'position'], 200),
  VpaidControls: parseBoolTrue(['VpaidControls', 'vpaidcontrols']),
  Enabled: parseBoolTrue(['Enabled', 'enabled']),
  VpaidInitadTimeout: parseNumber(['VpaidInitadTimeout', 'vpaidinitadtimeout'], 10000),
  FbPlacementID: parseString(['FbPlacementID', 'fbplacementid']),
  VpaidErroradTimeout: parseNumber(['VpaidErroradTimeout', 'vpaiderroradtimeout'], 10000),
  RewriterPath: parseString(['RewriterPath', 'rewriterpath']),
  DisableMetrics: parseBoolTrue(['DisableMetrics', 'disablemetrics']),
  WatcherFunc: parseString(['WatcherFunc', 'watcherfunc']),
  BackfillScript: parseString(['BackfillScript', 'backfillscript']),
  Onload: parseString(['Onload', 'onload']),
  Waterfall: parseBoolTrue(['Waterfall', 'waterfall']),
  ProxyOverride: parseString(['ProxyOverride', 'proxyoverride']),
  TestModuleURL: parseString(['TestModuleURL', 'testmoduleurl']),
  CustomParamsFunc: parseString(['CustomParamsFunc', 'customparamsfunc']),
  AutoAd: parseNumber(['AutoAd', 'autoad'], 0),
  PlayAdFunc: parseString(['PlayAdFunc', 'playadfunc']),
  GetDOMElement: parseString(['GetDOMElement', 'getdomelement']),
  IgnoreMuteCheck: parseBoolTrue(['IgnoreMuteCheck', 'ignoremutecheck']),
  WriteBuffer: parseNumber(['WriteBuffer', 'writebuffer'], 0),
  Region: parseString(['Region', 'region']),
  RegionPrefix: parseString(['RegionPrefix', 'regionprefix']),
  RegionPrefixEnabled: parseNumber(['RegionPrefixEnabled', 'regionprefixenabled'], 0),
  DistributionID: parseNumber(['DistributionID', 'distributionid'], -1),
  Distribution: parseArray(['AdSources', 'adsources'], transformDistributionWrapper),
  CookieStrategy: parseNumber(['CookieStrategy', 'cookiestrategy'], 0),
  DynamicHostname: parseNumber(['DynamicHostname', 'dynamichostname'], 0),
});

export interface IAdSource {
  ID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  AdunitID: string;
  Name: string;
  VastURL: string;
  VmapURL: string;
  PlaylistURL: string;
  JSTag: string;
  Priority: number;
  DisplayTag: string;
  EstimatedCPM: number;
  IsPublicaDemand: boolean;
  CountryTargeting: string;
  HeaderHTML: string;
  FooterHTML: string;
  GPTUnits: IGPTUnit[];
  Targets: ITarget[];
}

export const transformAdSource = transformModel<IAdSource>({
  ID: parseString(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  AdunitID: parseNumber(['AdunitID', 'adunitid'], -1),
  Name: parseString(['Name', 'name']),
  VastURL: parseString(['VastURL', 'vasturl']),
  VmapURL: parseString(['VmapURL', 'vmapurl']),
  PlaylistURL: parseString(['PlaylistURL', 'playlisturl']),
  JSTag: parseString(['JSTag', 'jstag']),
  Priority: parseNumber(['Priority', 'priority'], 0),
  DisplayTag: parseString(['DisplayTag', 'displaytag']),
  EstimatedCPM: parseNumber(['EstimatedCPM', 'estimatedcpm'], 7),
  IsPublicaDemand: parseBoolTrue(['IsPublicaDemand', 'ispublicademand']),
  CountryTargeting: parseString(['CountryTargeting', 'countrytargeting']),
  HeaderHTML: parseString(['HeaderHTML', 'headerhtml']),
  FooterHTML: parseString(['FooterHTML', 'footerhtml']),
  GPTUnits: parseArray(['GPTUnits', 'gptunits'], transformIGPTUnitWrapper),
  Targets: parseArray(['Targets', 'targets'], transformTargetWrapper),
});
function transformAdSourceWrapper(_fromObjValue: any) {
  return transformAdSource(_fromObjValue);
}

export interface IDeviceAwareModel {
  [key: string]: any;

  ID: string;
  SiteID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  All: string;
  AirTV: string;
  Amazon: string;
  Apple: string;
  ATT: string;
  Bravia: string;
  Chromecast: string;
  Comcast: string;
  Dish: string;
  Generic: string;
  Google: string;
  Hisense: string;
  Huawei: string;
  LG: string;
  Nvidia: string;
  Panasonic: string;
  Philips: string;
  Playstation: string;
  Plex: string;
  Roku: string;
  Samsung: string;
  Sanyo: string;
  Sharp: string;
  SkyQ: string;
  TCL: string;
  TIVO: string;
  Vizio: string;
  Web: string;
  Xbox: string;
  Xiaomi: string;
}

export const transformDeviceAwareModel = transformModel<IDeviceAwareModel>({
  ID: parseString(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  SiteID: parseNumber(['SiteID', 'siteid'], -1),
  All: parseString(['All', 'all']),
  AirTV: parseString(['AirTV', 'airtv']),
  Amazon: parseString(['Amazon', 'amazon']),
  Apple: parseString(['Apple', 'apple']),
  ATT: parseString(['ATT', 'att']),
  Bravia: parseString(['Bravia', 'bravia']),
  Chromecast: parseString(['Chromecast', 'chromecast']),
  Comcast: parseString(['Comcast', 'comcast']),
  Dish: parseString(['Dish', 'dish']),
  Generic: parseString(['Generic', 'generic']),
  Google: parseString(['Google', 'google']),
  Hisense: parseString(['Hisense', 'hisense']),
  Huawei: parseString(['Huawei', 'huawei']),
  LG: parseString(['LG', 'lg']),
  Nvidia: parseString(['Nvidia', 'nvidia']),
  Panasonic: parseString(['Panasonic', 'panasonic']),
  Philips: parseString(['Philips', 'philips']),
  Playstation: parseString(['Playstation', 'playstation']),
  Plex: parseString(['Plex', 'plex']),
  Roku: parseString(['Roku', 'roku']),
  Samsung: parseString(['Samsung', 'samsung']),
  Sanyo: parseString(['Sanyo', 'sanyo']),
  Sharp: parseString(['Sharp', 'sharp']),
  SkyQ: parseString(['SkyQ', 'skyq']),
  TCL: parseString(['TCL', 'tcl']),
  TIVO: parseString(['TIVO', 'tivo']),
  Vizio: parseString(['Vizio', 'vizio']),
  Web: parseString(['Web', 'web']),
  Xbox: parseString(['Xbox', 'xbox']),
  Xiaomi: parseString(['Xiaomi', 'xiaomi']),
});

export interface IHeaderBidderDeviceAwareModel {
  ID: string;
  HeaderBidderID: string;

  All: string;
  AirTV: string;
  Amazon: string;
  Apple: string;
  ATT: string;
  Bravia: string;
  Chromecast: string;
  Comcast: string;
  Dish: string;
  Generic: string;
  Google: string;
  Hisense: string;
  Huawei: string;
  LG: string;
  Nvidia: string;
  Panasonic: string;
  Philips: string;
  Playstation: string;
  Plex: string;
  Roku: string;
  Samsung: string;
  Sanyo: string;
  Sharp: string;
  SkyQ: string;
  TCL: string;
  TIVO: string;
  Vizio: string;
  Web: string;
  Xbox: string;
  Xiaomi: string;
}

export const transformHeaderBidderDeviceAwareModel = transformModel<IHeaderBidderDeviceAwareModel>({
  ID: parseString(['ID', 'id']),

  HeaderBidderID: parseNumber(['HeaderBidderID', 'headerbidderid'], -1),
  All: parseString(['All', 'all']),
  AirTV: parseString(['AirTV', 'airtv']),
  Amazon: parseString(['Amazon', 'amazon']),
  Apple: parseString(['Apple', 'apple']),
  ATT: parseString(['ATT', 'att']),
  Bravia: parseString(['Bravia', 'bravia']),
  Chromecast: parseString(['Chromecast', 'chromecast']),
  Comcast: parseString(['Comcast', 'comcast']),
  Dish: parseString(['Dish', 'dish']),
  Generic: parseString(['Generic', 'generic']),
  Google: parseString(['Google', 'google']),
  Hisense: parseString(['Hisense', 'hisense']),
  Huawei: parseString(['Huawei', 'huawei']),
  LG: parseString(['LG', 'lg']),
  Nvidia: parseString(['Nvidia', 'nvidia']),
  Panasonic: parseString(['Panasonic', 'panasonic']),
  Philips: parseString(['Philips', 'philips']),
  Playstation: parseString(['Playstation', 'playstation']),
  Plex: parseString(['Plex', 'plex']),
  Roku: parseString(['Roku', 'roku']),
  Samsung: parseString(['Samsung', 'samsung']),
  Sanyo: parseString(['Sanyo', 'sanyo']),
  Sharp: parseString(['Sharp', 'sharp']),
  SkyQ: parseString(['SkyQ', 'skyq']),
  TCL: parseString(['TCL', 'tcl']),
  TIVO: parseString(['TIVO', 'tivo']),
  Vizio: parseString(['Vizio', 'vizio']),
  Web: parseString(['Web', 'web']),
  Xbox: parseString(['Xbox', 'xbox']),
  Xiaomi: parseString(['Xiaomi', 'xiaomi']),
});

export enum ESite_HeaderBidderWinnerModel {
  Site_HB_WINNER_MODEL_UNKNOWN = 0,
  Site_HB_WINNER_MODEL_PRICE_PER_SECOND = 1,
  Site_HB_WINNER_MODEL_PRICE = 2,
  Site_HB_WINNER_MODEL_BOTH = 3,
}

export enum EAllowStatus {
  UNKNOWN = 0,
  DENY = 1,
  ALLOW = 2,
}

export function reverseEAllowStatus(src: EAllowStatus): EAllowStatus {
  switch (src) {
    // Unknown = Allow
    case EAllowStatus.UNKNOWN:
      return EAllowStatus.DENY;
    case EAllowStatus.ALLOW:
      return EAllowStatus.DENY;
    case EAllowStatus.DENY:
      return EAllowStatus.ALLOW;
  }
}

export function makeTargetingChangeDescription(isAllowing: boolean): string {
  if (isAllowing) {
    return ct('MessageTargetingFieldFromIncludingToExcluding');
  }
  return ct('MessageTargetingFieldFromExcludingToIncluding');
}

export function makeCampaignDaypartingTargetingChangeDescription(isAllowing: boolean): string {
  if (isAllowing) {
    return "This will change the campaign's targeting for dayparting from including the selected time periods to excluding them.";
  }
  return "This will change the campaign's targeting for dayparting from excluding the selected time periods to including them.";
}

export function makeTargetingChangeInfoHover(isAllowing: boolean): string {
  if (isAllowing) {
    return ct('TooltipIncludedToggleToExclude');
  }
  return ct('TooltipExcludedToggleToInclude');
}

export interface IHeaderBidder {
  ID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;
  SuperOnly: boolean;
  Active: number;
  Name: string;
  Bidder: string;
  Params: string;
  Settings: string;
  Slots: string[];
  EnforceSlotTargeting: EBool;
  AdbreakTargeting: string;
  BidFloor: string;
  BidFloorUSD: string;
  BidFloorCurrency: ECurrencyType;
  BidAdjustmentFactor: number;
  BidAdjustmentValue: number;
  BuyTypeID: number;
  ShareOfVoice: number;
  ShareOfVoiceType: EHeaderBidder_ShareOfVoiceType;
  AuctionParticipationPercentage: number;
  QPS: number;
  CurrencyDisplayed: ECurrencyType;
  DeviceEnv: string;
  DeviceType: string;
  DeviceTypeEnabled: number;
  IPFilter: string;
  Priority: number;
  Timeout: number;
  TmaxOffset: number;
  ParamOptions: IHeaderBidderParamOption[];
  TargetingDevices: string;
  TargetingDevicesIsAllowed: EAllowStatus;
  TargetingDeviceSubTypes: string;
  TargetingDeviceSubTypesIsAllowed: EAllowStatus;
  TargetingDeviceModels: string;
  TargetingDeviceModelsIsAllowed: EAllowStatus;
  TargetingLivestream: number;
  LivestreamTargeting: string;
  AudienceDeals: IAudienceDeal[];
  TargetingAudienceSegmentID: string;
  TargetingAudienceSegmentIDIsAllowed: EAllowStatus;
  TargetingDeviceCountry: string;
  TargetingCountryGroupIDs: string;
  TargetingCountryGroupIDsIsAllowed: EAllowStatus;
  LocalTargeting: string;
  LocalTargetingIsAllowed: boolean;
  TargetingDealID: string;
  TargetingDealIDIsAllowed: EBool;
  TargetingAppBundleID: string;
  TargetingAppBundleIDIsAllowed: boolean;
  TargetingAppBundleSitePagesLogic: ELogicalOperator;
  TargetingSitePages: string;
  TargetingSitePagesIsAllowed: EAllowStatus;
  TargetingSiteDomain: string;
  TargetingSiteDomainIsAllowed: EAllowStatus;
  MinAdDurationOverride: number;
  MaxAdDurationOverride: number;
  StartDelayOverride: number;
  AppStoreURL: IHeaderBidderDeviceAwareModel;
  AppStoreUrlOverrideIfEmpty: EBool;
  AppBundleOverride: string;
  AppBundleOverrideIfEmpty: EBool;
  AppIDOverride: string;
  AppIDOverrideIfEmpty: EBool;
  AppNameOverride: string;
  AppNameOverrideIfEmpty: EBool;
  AppDomainOverride: string;
  AppDomainOverrideIfEmpty: EBool;
  ContentChannelOverride: string;
  ContentChannelOverrideIfEmpty: EBool;
  ContentNetworkOverride: string;
  ContentNetworkOverrideIfEmpty: EBool;
  ContentSeriesOverride: string;
  ContentSeriesOverrideIfEmpty: EBool;
  ContentTitleOverride: string;
  ContentTitleOverrideIfEmpty: EBool;
  ContentGenreOverride: string;
  ContentGenreOverrideIfEmpty: EBool;
  ContentDistribNameOverride: string;
  ContentDistribNameOverrideIfEmpty: EBool;
  ContentRatingOverride: string;
  ContentRatingOverrideIfEmpty: EBool;
  ContentIDOverride: string;
  ContentIDOverrideIfEmpty: EBool;
  ContentSeasonOverride: string;
  ContentSeasonOverrideIfEmpty: EBool;
  ContentProdqualOverride: string;
  ContentProdqualOverrideIfEmpty: EBool;
  ContentProducerNameOverride: string;
  ContentProducerNameOverrideIfEmpty: EBool;
  ContentProducerIDOverride: string;
  ContentProducerIDOverrideIfEmpty: EBool;
  ContentKeywordsOverride: string;
  ContentKeywordsOverrideIfEmpty: EBool;
  ContentLanguageOverride: string;
  ContentLanguageOverrideIfEmpty: EBool;
  ClearDefaultBitrateLimits: EBool;
  SiteIDS: number[];
  UIBidderType: string;
  PoddedRequestActive: number;
  PoddedRequestType: EHeaderBidder_PoddedRequestType;
  HybridStructuredSlotDurations: string;
  BrandCategoryUniquenessActive: number;
  MultiBidActive: number;
  HonorChannelFloor: EBool;
  AnonymizeDeviceID: EBool;
  AnonymizeIP: EBool;
  SegmentTargeting: number;
  CampaignTargeting: number;
  CampaignIDS: number[];
  OrderIDS: number[];
  abTests: IABTesting[];
  ChannelGroupIDS: number[];
  ChannelGroups: IChannelGroup[];
  ChannelListIDS: number[];
  CountryGroups: ICountryGroup[];
  EndpointDealIDPassthroughActive: EBool;
  RemoveContentFromBidRequest: EBool;
  RemoveContentChannelFromBidRequest: EBool;
  RemoveContentNetworkFromBidRequest: EBool;
  RemoveContentSeriesFromBidRequest: EBool;
  RemoveContentTitleFromBidRequest: EBool;
  RemoveContentGenreFromBidRequest: EBool;
  RemoveContentDistribNameFromBidRequest: EBool;
  RemoveContentRatingFromBidRequest: EBool;
  RemoveContentIDFromBidRequest: EBool;
  RemoveContentSeasonFromBidRequest: EBool;
  RemoveContentProdqualFromBidRequest: EBool;
  RemoveContentProducerNameFromBidRequest: EBool;
  RemoveContentProducerIDFromBidRequest: EBool;
  RemoveContentKeywordsFromBidRequest: EBool;
  RemoveContentLanguageFromBidRequest: EBool;
  RemoveInventoryPartnerDomainFromBidRequest: EBool;
  RemoveAppStoreURLFromBidRequest: EBool;
  SiteDomainOverride: string;
  SiteDomainOverrideIfEmpty: EBool;
  CustomTargeting: string;
  CustomTargetingInclusion: EAllowStatus;
  Custom4: string;
  Custom4Inclusion: EAllowStatus;
  Custom5: string;
  Custom5Inclusion: EAllowStatus;
  Custom6: string;
  Custom6Inclusion: EAllowStatus;
  Custom7: string;
  Custom7Inclusion: EAllowStatus;
  Custom8: string;
  Custom8Inclusion: EAllowStatus;
  Custom9: string;
  Custom9Inclusion: EAllowStatus;
  Custom10: string;
  Custom10Inclusion: EAllowStatus;
  Custom11: string;
  Custom11Inclusion: EAllowStatus;
  Custom12: string;
  Custom12Inclusion: EAllowStatus;
  Custom13: string;
  Custom13Inclusion: EAllowStatus;
  Custom14: string;
  Custom14Inclusion: EAllowStatus;
  Custom15: string;
  Custom15Inclusion: EAllowStatus;
  Custom16: string;
  Custom16Inclusion: EAllowStatus;
  Custom17: string;
  Custom17Inclusion: EAllowStatus;
  Custom18: string;
  Custom18Inclusion: EAllowStatus;
  Skip: EBool;
  SkipMin: number;
  SkipAfter: number;
  TargetingContentChannel: string;
  TargetingContentChannelIsAllowed: EAllowStatus;
  TargetingContentDistributor: string;
  TargetingContentDistributorIsAllowed: EAllowStatus;
  TargetingContentGenre: string;
  TargetingContentGenreIsAllowed: EAllowStatus;
  TargetingContentID: string;
  TargetingContentIDIsAllowed: EAllowStatus;
  TargetingContentNetwork: string;
  TargetingContentNetworkIsAllowed: EAllowStatus;
  TargetingContentProducer: string;
  TargetingContentProducerIsAllowed: EAllowStatus;
  TargetingContentRating: string;
  TargetingContentRatingIsAllowed: EAllowStatus;
  TargetingContentSeries: string;
  TargetingContentSeriesIsAllowed: EAllowStatus;
  TargetingContentTitle: string;
  TargetingContentTitleIsAllowed: EAllowStatus;
  TargetingContentCategory: string;
  TargetingContentLanguageIsAllowed: EAllowStatus;
  TargetingContentLanguage: string;
  TargetingContentCategoryIsAllowed: EAllowStatus;
  TargetingDeviceTypes: string;
  TargetingDeviceTypesIsAllowed: EAllowStatus;
  TargetingPrice: string;
  TargetingPriceIsAllowed: EAllowStatus;
  TargetingCoppa: TargetingCoppa;
  TargetingLimitedAdTracking: TargetBoolType;
  FixedPrice: number;
  DailyRequestCap: number;
  PacingType: number;
  PlaybackMethod: string;
  Deals: IDeal[];
  EnableDealPriorityOverride: EBool;
  InventoryType: EHeaderBidder_InventoryType;
  IsPublicaTagFallbackEnabled: PublicaTagFallback;
  IsFallbackBackfillEnabled: IsFallbackBackfillEnabled;
  FallbackAuctionParticipationEnabled: EBool;
  FrequencyActive: ResourceActive;
  FrequencyTTL: number;
  FrequencyLimit: number;
  PrivateAuction: PrivateAuctionType;
  MatchDealID: MatchDealIDType;
  FlexibleBiddingEnabled: EBool;
  FlexibleBiddingThresholds: IFlexibleBiddingThreshold[];
  FlexibleBiddingType: FlexibleBiddingType;
  ContextualSegmentIDS: number[];
  TotalCount: number;
  ContentCategoryOverride: string;
  ContentCategoryOverrideIfEmpty: EBool;
  RemoveContentCategoryFromBidRequest: EBool;
  StartDate?: string;
  EndDate?: string;
  Quantity: number;
  CRONQuantityCount: number;
  CRONQuantityCountSinceLastTest: number;
  UnpaidBidderEnabled: EBool;
  DOOHEnabled: EBool;
  DOOHDeviceTypeRewriteEnabled: EBool;
  RemoveFmtJSON: EBool;
  PositionTargeting: EHeaderBidder_PositionTargeting;
  EnableSpotxBidderAssociation: EBool;
  EnableMagniteCTVBidderAssociation: EBool;
  EnableSamsungSSPBidderAssociation: EBool;
  EnableIrisSignal: EBool;
  ExtendedContentIDVendors: string;
  TargetedAdBreak: EHeaderBidder_TargetedAdBreak;
  EnableIASDataSharing: EBool; // deprecated(?)
  EnableOptableEIDs: EBool;
  IASDataSharing: string[];
  DoubleVerifyDataSharing: string[];
  PodSequenceTargeting: EHeaderBidder_PodSequenceTargeting;
  LocalTargetingLogic: TargetingLogic;
  TargetingFWSectionIDs: string;
  TargetingFWSectionIDsIsAllowed: EAllowStatus;
  VastExtensionPriceEnabled: EBool;
  SubPriority: number;
  Dayparting: string;
  DaypartingIsAllowed: EAllowStatus;
  Timezone: string;
  MimeTypeOverride: string;
  DealAtTargeting: EHeaderBidder_DealAtTargeting;
  TargetingCCPA: TargetingCCPA;
  EnableRemoveGPPFromBidRequest: EBool;
  OptionalMessage: string;
  EnableSecondaryRequests: EBool;
  BidderLabels: IBidderLabel[];
  YahooConnectIDEnabled: EBool;
  TargetingRequestedPodDurationMin: number;
  TargetingRequestedPodDurationMax: number;
  IsAutoCreatedByPlatformConnect: EBool;
  TargetingGvinParamExists: EBool;
  EnableTargetingFirstAdbreakInUserSession: EBool;
  PresetIDs: number[];
  TargetingGDPR: TargetingGDPR;
  TargetingGDPRConsent: TargetBoolType;
  InventoryPartnerDomainOverride: string;
  InventoryPartnerDomainOverrideIfEmpty: EBool;
  EnableTrafficShaping: EBool;
  EnableSponsorshipMode: EBool;
  SamplingPercentageDisabledUntil: string;
  SamplingPercentage: number;
  UID2ExtendedIDSetting: ExtendedIDSetting;
  LiverampExtendedIDSetting: ExtendedIDSetting;
  GooglePairExtendedIDSetting: ExtendedIDSetting;
  TargetingDeviceIDType: ETargetingDeviceIDTypes;
  TargetingUID20TargetingType: ExtendedIDTargetingType;
  TargetingGooglePairIDTargetingType: ExtendedIDTargetingType;
  TargetingLiveRampATSTargetingType: ExtendedIDTargetingType;
  TargetingSamsungIdentityTargetingType: ExtendedIDTargetingType;
  TargetingIrisSignalTargetingType: ExtendedIDTargetingType;
  TargetingSamsungEPGMetadataTargetingType: ExtendedIDTargetingType;
}

export interface IBidderWithTableData extends IHeaderBidder {
  tableData: { [checked: string]: boolean };
}

export enum ETargetingDeviceIDTypes {
  ALL = 0,
  ORIGINAL,
  SYNTHETIC,
}

export type ExtendedIDSetting = number;
export const ExtendedIDSettingInherit: ExtendedIDSetting = 0;
export const ExtendedIDSettingPass: ExtendedIDSetting = 1;
export const ExtendedIDSettingDoNotPass: ExtendedIDSetting = 2;

export type TargetingRequestedPodDuration = number;
export const TargetingRequestedPodDurationNoMin = 0;
export const TargetingRequestedPodDurationNoMax = 1000;

export type TargetingCoppa = number;
export const TargetingCoppaAll: TargetingCoppa = 0;
export const TargetingCoppa0: TargetingCoppa = 1;
export const TargetingCoppa1: TargetingCoppa = 2;

export const ConvertTargetingCoppaToText: { [key: number]: string } = {
  0: 'All',
  1: '0',
  2: '1',
};

export type TargetingGDPR = number;
export const TargetingGDPRAll: TargetingGDPR = 0;
export const TargetingGDPR0: TargetingGDPR = 1;
export const TargetingGDPR1: TargetingGDPR = 2;

export const sortHeaderBidders = (a: IHeaderBidder, b: IHeaderBidder): number => {
  if (parseInt(a.Active as FIXTYPE, 10) < parseInt(b.Active as FIXTYPE, 10)) {
    return 1;
  }
  if (parseInt(b.Active as FIXTYPE, 10) < parseInt(a.Active as FIXTYPE, 10)) {
    return -1;
  }
  if (parseInt(a.Priority as FIXTYPE, 10) < parseInt(b.Priority as FIXTYPE, 10)) {
    return 1;
  }
  if (parseInt(b.Priority as FIXTYPE, 10) < parseInt(a.Priority as FIXTYPE, 10)) {
    return -1;
  }
  if (a.Name.toLowerCase() > b.Name.toLowerCase()) {
    return 1;
  }
  if (b.Name.toLowerCase() > a.Name.toLowerCase()) {
    return -1;
  }
  return 0;
};

export interface IHeaderBidderParamOption {
  ID: number;
  Bidder: string;
  Name: string;
  Description: string;
  Value: any;
  Type: string;
}

export const transformHeaderBidderParamOptions = transformModel<IHeaderBidderParamOption>({
  ID: parseNumber(['ID', 'id']),
  Bidder: parseString(['Bidder', 'bidder']),
  Name: parseString(['Name', 'name']),
  Description: parseString(['Description', 'description']),
  Type: parseString(['Type', 'type']),
});

export interface IABTest {
  ID: number;
  Name: string;
  Parameters: string;
  Share: number;
}

export const transformABTest = transformModel<IABTest>({
  ID: parseNumber(['ID']),
  Name: parseString(['Name', 'name']),
  Parameters: parseString(['Parameters', 'parameters']),
  Share: parseNumber(['Share', 'share']),
});

function transformABTestWrapper(_fromObjValue: any) {
  return transformABTest(_fromObjValue);
}

export interface IChannelABTestMA {
  ID: number;
  Name: string;
  MaxAuctionCap: number;
}

export const transformChannelABTestMA = transformModel<IChannelABTestMA>({
  ID: parseNumber(['ID']),
  Name: parseString(['Name', 'name']),
  MaxAuctionCap: parseNumber(['MaxAuctionCap', 'max_auction_cap']),
});

function transformChannelABTestMAWrapper(_fromObjValue: any) {
  return transformChannelABTestMA(_fromObjValue);
}

export const sortABTests = (a: IABTest, b: IABTest): number => {
  if (a.Name.toLowerCase() > b.Name.toLowerCase()) {
    return 1;
  }
  if (b.Name.toLowerCase() > a.Name.toLowerCase()) {
    return -1;
  }
  return 0;
};

export interface IABTesting {
  ID: number;
  Active: number;
  BidderID: number;
  Name: string;
  StartDate: string | undefined;
  EndDate: string | undefined;
  Tests: IABTest[];
}

export const transformABTesting = transformModel<IABTesting>({
  ID: parseNumber(['ID']),
  Active: parseNumber(['Active', 'active']),
  BidderID: parseNumber(['BidderID', 'bidderID']),
  Name: parseString(['Name', 'name']),
  StartDate: parseString(['StartDate', 'startdate']),
  EndDate: parseString(['EndDate', 'enddate']),
  Tests: parseArray(['Tests', 'tests'], transformABTestWrapper, sortABTests),
});

export interface IChannelABTestingMA {
  ID: number;
  Active: number;
  ChannelID: number;
  Name: string;
  Tests: IChannelABTestMA[];
}

export const transformChannelABTesting = transformModel<IChannelABTestingMA>({
  ID: parseNumber(['ID']),
  Active: parseNumber(['Active', 'active']),
  ChannelID: parseNumber(['ChannelID', 'channelID']),
  Name: parseString(['Name', 'name']),
  StartDate: parseString(['StartDate', 'startdate']),
  EndDate: parseString(['EndDate', 'enddate']),
  Tests: parseArray(['Tests', 'tests'], transformChannelABTestMAWrapper, sortABTests),
});

export type ABTestingActive = number;
export const ABTestingActiveInactive: ABTestingActive = 1;
export const ABTestingActiveActive: ABTestingActive = 2;

export const fromAPIHeaderBidder = transformModel<IHeaderBidder>({
  ID: parseString(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: parseString(['DeletedAt']),

  SuperOnly: parseBoolTrue(['SuperOnly', 'superonly']),
  Active: parseNumber(['Active', 'active']),
  SiteID: parseString(['SiteID', 'siteid']),
  Name: parseString(['Name', 'name']),
  Bidder: parseString(['Bidder', 'bidder']),
  Params: parseString(['Params', 'params']),
  Settings: parseString(['Settings', 'settings']),
  Slots: parseString(['Slots', 'slots', 'Pods', 'pods'], ','),
  EnforceSlotTargeting: parseNumber(
    ['EnforceSlotTargeting', 'enforceslottargeting'],
    EBool.EBool_FALSE,
  ),
  AdbreakTargeting: parseString(['AdbreakTargeting']),
  BidFloor: parseString(['BidFloor', 'bidfloor']),
  BidFloorUSD: parseString(['BidFloorUSD', 'bidfloorusd']),
  BidFloorCurrency: parseNumber(['BidFloorCurrency', 'bidfloorcurrency']),
  MaxAuctionCap: parseString(['MaxAuctionCap', 'maxauctioncap']),
  BidAdjustmentFactor: parseFloat(
    ['BidAdjustmentFactor', 'bidadjustmentfactor'],
    1,
    (val: number) => {
      return val * 100;
    },
  ),
  BidAdjustmentValue: parseFloat(['BidAdjustmentValue', 'bidadjustmentvalue']),
  BuyTypeID: parseNumber(['BuyTypeID']),
  ShareOfVoice: parseFloat('ShareOfVoice', 1, (val: number) => {
    // toFixed(4): truncate to 4 decimals to void sth like this: 0.55 * 100 = 55.0000001 due to floats being imprecisely represented in binary
    // Number.parseFloat: truncate all trailing zero in the decimals
    return Number.parseFloat((val * 100).toFixed(4));
  }),
  ShareOfVoiceType: parseNumber(['ShareOfVoiceType']),
  AuctionParticipationPercentage: parseNumber(['AuctionParticipationPercentage']),
  QPS: parseNumber(['QPS', 'qps']),
  CurrencyDisplayed: parseNumber(['CurrencyDisplayed', 'currency']),
  DeviceEnv: parseString(['DeviceEnv', 'deviceenv']),
  DeviceType: parseString(['DeviceType', 'devicetype']),
  DeviceTypeEnabled: parseNumber(['DeviceTypeEnabled', 'devicetypeenabled']),
  IPFilter: parseString(['IPFilter', 'ipfilter']),
  TargetingDeviceMake: parseString(['TargetingDeviceMake', 'targetingdevicemake']),
  TargetingDeviceModel: parseString(['TargetingDeviceModel', 'targetingdevicemodel']),
  TargetingDeviceOs: parseString(['TargetingDeviceOs', 'targetingdeviceos']),
  TargetingDeviceType: parseString(['TargetingDeviceType', 'targetingdevicetype']),
  TargetingDevices: parseString(['TargetingDevices', 'targetingdevices']),
  TargetingDevicesIsAllowed: parseNumber('TargetingDevicesIsAllowed'),
  TargetingDeviceSubTypes: parseString(['TargetingDeviceSubTypes']),
  TargetingDeviceSubTypesIsAllowed: parseNumber('TargetingDeviceSubTypesIsAllowed'),
  TargetingDeviceModels: parseString(['TargetingDeviceModels']),
  TargetingDeviceModelsIsAllowed: parseNumber('TargetingDeviceModelsIsAllowed'),
  TargetingAudienceSegmentID: parseString([
    'TargetingAudienceSegmentID',
    'targetingaudiencesegmentid',
  ]),
  TargetingAudienceSegmentIDIsAllowed: parseNumber('TargetingAudienceSegmentIDIsAllowed'),
  TargetingAppBundleID: parseString(['TargetingAppBundleID', 'targeting_app_bundle_id']),
  TargetingAppBundleIDIsAllowed: parseBoolTrue('TargetingAppBundleIDIsAllowed'),
  TargetingAppBundleSitePagesLogic: parseNumber('TargetingAppBundleSitePagesLogic'),
  TargetingDealID: parseString(['TargetingDealID', 'targeting_deal_id']),
  TargetingDealIDIsAllowed: parseNumber('TargetingDealIDIsAllowed'),
  TargetingLivestream: parseNumber(['TargetingLivestream', 'targetinglivestream']),
  LivestreamTargeting: parseString(['LivestreamTargeting']),
  Priority: parseNumber(['Priority', 'priority']),
  SubPriority: parseNumber(['SubPriority', 'sub_priority']),
  Timeout: parseNumber(['Timeout', 'timeout']),
  TmaxOffset: 'TmaxOffset',
  ParamOptions: parseArray(['ParamOptions', 'paramoptions'], (fromObj: any) => {
    return transformHeaderBidderParamOptions(fromObj);
  }),
  AudienceDeals: parseArray(['AudienceDeals'], (fromObj: any) => {
    return transformAudienceDeal(fromObj);
  }),
  TargetingDeviceCountry: parseString(['TargetingDeviceCountry', 'targetingdevicecountry']),
  TargetingCountryGroupIDs: parseString(['TargetingCountryGroupIDs']),
  TargetingCountryGroupIDsIsAllowed: parseNumber('TargetingCountryGroupIDsIsAllowed'),
  LocalTargeting: parseString(['LocalTargeting', 'localtargeting']),
  LocalTargetingIsAllowed: parseBoolTrue('LocalTargetingIsAllowed'),
  MinAdDurationOverride: parseNumber(['MinAdDurationOverride', 'minaddurationoverride']),
  MaxAdDurationOverride: parseNumber(['MaxAdDurationOverride', 'maxaddurationoverride']),
  StartDelayOverride: parseNumber(['StartDelayOverride']),
  AppStoreURL: parseObject(
    ['HeaderBidderAppStoreURL', 'appstoreurl'],
    transformHeaderBidderDeviceAwareModel,
  ),
  AppStoreUrlOverrideIfEmpty: parseNumber(
    ['AppStoreURLOverrideIfEmpty', 'AppStoreUrlOverrideIfEmpty', 'appstoreurloverrideifempty'],
    EBool.EBool_FALSE,
  ),
  AppBundleOverride: parseString(['AppBundleOverride', 'app_bundle_override']),
  AppBundleOverrideIfEmpty: parseNumber(['AppBundleOverrideIfEmpty'], EBool.EBool_FALSE),
  AppIDOverride: parseString(['AppIDOverride', 'app_id_override']),
  AppIDOverrideIfEmpty: parseNumber(['AppIDOverrideIfEmpty'], EBool.EBool_FALSE),
  AppNameOverride: parseString(['AppNameOverride', 'app_name_override']),
  AppNameOverrideIfEmpty: parseNumber(['AppNameOverrideIfEmpty'], EBool.EBool_FALSE),
  AppDomainOverride: parseString(['AppDomainOverride', 'app_domain_override']),
  AppDomainOverrideIfEmpty: parseNumber(['AppDomainOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentChannelOverride: parseString(['ContentChannelOverride']),
  ContentChannelOverrideIfEmpty: parseNumber(['ContentChannelOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentNetworkOverride: parseString(['ContentNetworkOverride']),
  ContentNetworkOverrideIfEmpty: parseNumber(['ContentNetworkOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentSeriesOverride: parseString(['ContentSeriesOverride']),
  ContentSeriesOverrideIfEmpty: parseNumber(['ContentSeriesOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentTitleOverride: parseString(['ContentTitleOverride']),
  ContentTitleOverrideIfEmpty: parseNumber(['ContentTitleOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentGenreOverride: parseString(['ContentGenreOverride']),
  ContentGenreOverrideIfEmpty: parseNumber(['ContentGenreOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentDistribNameOverride: parseString(['ContentDistribNameOverride']),
  ContentDistribNameOverrideIfEmpty: parseNumber(
    ['ContentDistribNameOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentRatingOverride: parseString(['ContentRatingOverride']),
  ContentRatingOverrideIfEmpty: parseNumber(['ContentRatingOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentProdqualOverride: parseString(['ContentProdqualOverride']),
  ContentProdqualOverrideIfEmpty: parseNumber(
    ['ContentProdqualOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentProducerNameOverride: parseString(['ContentProducerNameOverride']),
  ContentProducerNameOverrideIfEmpty: parseNumber(
    ['ContentProducerNameOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentProducerIDOverride: parseString(['ContentProducerIDOverride']),
  ContentProducerIDOverrideIfEmpty: parseNumber(
    ['ContentProducerIDOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentKeywordsOverride: parseString(['ContentKeywordsOverride']),
  ContentKeywordsOverrideIfEmpty: parseNumber(
    ['ContentKeywordsOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentLanguageOverride: parseString(['ContentLanguageOverride']),
  ContentLanguageOverrideIfEmpty: parseNumber(
    ['ContentLanguageOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentCategoryOverride: parseString(['ContentCategoryOverride']),
  ContentCategoryOverrideIfEmpty: parseNumber(
    ['ContentCategoryOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentIDOverride: parseString(['ContentIDOverride']),
  ContentIDOverrideIfEmpty: parseNumber(['ContentIDOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentSeasonOverride: parseString(['ContentSeasonOverride']),
  ContentSeasonOverrideIfEmpty: parseNumber(['ContentSeasonOverrideIfEmpty'], EBool.EBool_FALSE),
  ClearDefaultBitrateLimits: parseNumber(['ClearDefaultBitrateLimits'], EBool.EBool_FALSE),
  SiteDomainOverride: parseString(['SiteDomainOverride', 'site_domain_override']),
  SiteDomainOverrideIfEmpty: parseNumber(['SiteDomainOverrideIfEmpty'], EBool.EBool_FALSE),
  SiteIDS: parseArray(['SiteIDS', 'site_ids'], (obj: any) => {
    return obj;
  }),
  UIBidderType: parseString(['UIBidderType', 'uibiddertype']),
  PoddedRequestActive: parseNumber(['PoddedRequestActive']),
  PoddedRequestType: parseNumber(['PoddedRequestType']),
  HybridStructuredSlotDurations: parseString(['HybridStructuredSlotDurations']),
  BrandCategoryUniquenessActive: parseNumber(['BrandCategoryUniquenessActive']),
  MultiBidActive: parseNumber(['MultiBidActive']),
  HonorChannelFloor: parseNumber(['HonorChannelFloor'], EBool.EBool_UNKNOWN),
  AnonymizeDeviceID: parseNumber(['AnonymizeDeviceID'], EBool.EBool_UNKNOWN),
  AnonymizeIP: parseNumber(['AnonymizeIP'], EBool.EBool_UNKNOWN),
  SegmentTargeting: parseNumber(['SegmentTargeting', 'segment_targeting']),
  CampaignTargeting: parseNumber(['CampaignTargeting', 'campaigntargeting']),
  CampaignIDS: parseArray(['CampaignIDS', 'campaign_ids'], (obj: any) => {
    return obj;
  }),
  OrderIDS: parseArray(['OrderIDS', 'order_ids'], (obj: any) => {
    return obj;
  }),
  abTests: parseArray(['abTests', 'ab_tests', 'AbTests'], (fromObj: any) => {
    return transformABTesting(fromObj);
  }),
  ChannelGroupIDS: parseArray(['ChannelGroupIDS', 'channel_group_ids'], (obj: any) => {
    return obj;
  }),
  ChannelGroups: parseArray(['ChannelGroups', 'channelgroups'], transformChannelGroupWrapper),
  ChannelListIDS: parseArray(['ChannelListIDS', 'channel_list_ids'], (obj: any) => {
    return obj;
  }),
  CountryGroups: parseArray(['CountryGroups'], transformCountryGroup),
  EndpointDealIDPassthroughActive: parseNumber(
    ['EndpointDealIDPassthroughActive'],
    EBool.EBool_FALSE,
  ),
  RemoveContentFromBidRequest: parseNumber(['RemoveContentFromBidRequest'], EBool.EBool_FALSE),
  RemoveContentChannelFromBidRequest: parseNumber(
    ['RemoveContentChannelFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentNetworkFromBidRequest: parseNumber(
    ['RemoveContentNetworkFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentSeriesFromBidRequest: parseNumber(
    ['RemoveContentSeriesFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentTitleFromBidRequest: parseNumber(
    ['RemoveContentTitleFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentGenreFromBidRequest: parseNumber(
    ['RemoveContentGenreFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentDistribNameFromBidRequest: parseNumber(
    ['RemoveContentDistribNameFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentCategoryFromBidRequest: parseNumber(
    ['RemoveContentCategoryFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentRatingFromBidRequest: parseNumber(
    ['RemoveContentRatingFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentProdqualFromBidRequest: parseNumber(
    ['RemoveContentProdqualFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentProducerNameFromBidRequest: parseNumber(
    ['RemoveContentProducerNameFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentProducerIDFromBidRequest: parseNumber(
    ['RemoveContentProducerIDFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentKeywordsFromBidRequest: parseNumber(
    ['RemoveContentKeywordsFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentLanguageFromBidRequest: parseNumber(
    ['RemoveContentLanguageFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentIDFromBidRequest: parseNumber(['RemoveContentIDFromBidRequest'], EBool.EBool_FALSE),
  RemoveContentSeasonFromBidRequest: parseNumber(
    ['RemoveContentSeasonFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveInventoryPartnerDomainFromBidRequest: parseNumber(
    ['RemoveInventoryPartnerDomainFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveAppStoreURLFromBidRequest: parseNumber(
    ['RemoveAppStoreURLFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  CustomTargeting: parseString(['CustomTargeting', 'customtargeting']),
  CustomTargetingInclusion: parseNumber(['CustomTargetingInclusion', 'custom_targeting_inclusion']),
  Custom4: parseString(['Custom4', 'custom4']),
  Custom4Inclusion: parseNumber(['Custom4Inclusion', 'custom4_inclusion']),
  Custom5: parseString(['Custom5', 'custom5']),
  Custom5Inclusion: parseNumber(['Custom5Inclusion', 'custom5_inclusion']),
  Custom6: parseString(['Custom6', 'custom6']),
  Custom6Inclusion: parseNumber(['Custom6Inclusion', 'custom6_inclusion']),
  Custom7: parseString(['Custom7', 'custom7']),
  Custom7Inclusion: parseNumber(['Custom7Inclusion', 'custom7_inclusion']),
  Custom8: parseString(['Custom8', 'custom8']),
  Custom8Inclusion: parseNumber(['Custom8Inclusion', 'custom8_inclusion']),
  Custom9: parseString(['Custom9', 'custom9']),
  Custom9Inclusion: parseNumber(['Custom9Inclusion', 'custom9_inclusion']),
  Custom10: parseString(['Custom10', 'custom10']),
  Custom10Inclusion: parseNumber(['Custom10Inclusion', 'custom10_inclusion']),
  Custom11: parseString(['Custom11', 'custom11']),
  Custom11Inclusion: parseNumber(['Custom11Inclusion', 'custom11_inclusion']),
  Custom12: parseString(['Custom12', 'custom12']),
  Custom12Inclusion: parseNumber(['Custom12Inclusion', 'custom12_inclusion']),
  Custom13: parseString(['Custom13', 'custom13']),
  Custom13Inclusion: parseNumber(['Custom13Inclusion', 'custom13_inclusion']),
  Custom14: parseString(['Custom14', 'custom14']),
  Custom14Inclusion: parseNumber(['Custom14Inclusion', 'custom14_inclusion']),
  Custom15: parseString(['Custom15', 'custom15']),
  Custom15Inclusion: parseNumber(['Custom15Inclusion', 'custom15_inclusion']),
  Custom16: parseString(['Custom16', 'custom16']),
  Custom16Inclusion: parseNumber(['Custom16Inclusion', 'custom16_inclusion']),
  Custom17: parseString(['Custom17', 'custom17']),
  Custom17Inclusion: parseNumber(['Custom17Inclusion', 'custom17_inclusion']),
  Custom18: parseString(['Custom18', 'custom18']),
  Custom18Inclusion: parseNumber(['Custom18Inclusion', 'custom18_inclusion']),
  Deals: parseArray(['Deals'], transformDealFromApi),
  EnableDealPriorityOverride: parseNumber(['EnableDealPriorityOverride'], EBool.EBool_TRUE),
  TargetingContentChannel: parseString(['TargetingContentChannel']),
  TargetingContentChannelIsAllowed: parseNumber('TargetingContentChannelIsAllowed'),
  TargetingContentDistributor: parseString(['TargetingContentDistributor']),
  TargetingContentDistributorIsAllowed: parseNumber(['TargetingContentDistributorIsAllowed']),
  TargetingContentGenre: parseString(['TargetingContentGenre']),
  TargetingContentGenreIsAllowed: parseNumber(['TargetingContentGenreIsAllowed']),
  TargetingContentID: parseString(['TargetingContentID']),
  TargetingContentIDIsAllowed: parseNumber('TargetingContentIDIsAllowed'),
  TargetingContentNetwork: parseString(['TargetingContentNetwork']),
  TargetingContentNetworkIsAllowed: parseNumber('TargetingContentNetworkIsAllowed'),
  TargetingContentProducer: parseString(['TargetingContentProducer']),
  TargetingContentProducerIsAllowed: parseNumber(['TargetingContentProducerIsAllowed']),
  TargetingContentRating: parseString(['TargetingContentRating']),
  TargetingContentRatingIsAllowed: parseNumber(['TargetingContentRatingIsAllowed']),
  TargetingContentSeries: parseString(['TargetingContentSeries']),
  TargetingContentSeriesIsAllowed: parseNumber(['TargetingContentSeriesIsAllowed']),
  TargetingContentTitle: parseString(['TargetingContentTitle']),
  TargetingContentTitleIsAllowed: parseNumber(['TargetingContentTitleIsAllowed']),
  TargetingContentCategory: parseString(['TargetingContentCategory']),
  TargetingContentCategoryIsAllowed: parseNumber(['TargetingContentCategoryIsAllowed']),
  TargetingContentLanguage: parseString(['TargetingContentLanguage']),
  TargetingContentLanguageIsAllowed: parseNumber(['TargetingContentLanguageIsAllowed']),
  Skip: parseNumber(['Skip'], EBool.EBool_UNKNOWN),
  SkipMin: parseNumber(['SkipMin'], 0),
  SkipAfter: parseNumber(['SkipAfter'], 0),
  TargetingDeviceTypes: parseString(['TargetingDeviceTypes', 'targeting_device_types']),
  TargetingDeviceTypesIsAllowed: parseNumber([
    'TargetingDeviceTypesIsAllowed',
    'targeting_device_types_is_allowed',
  ]),
  TargetingPrice: parseString(['TargetingPrice', 'targeting_price']),
  TargetingPriceIsAllowed: parseNumber(['TargetingPriceIsAllowed', 'targeting_price_is_allowed']),
  TargetingCoppa: parseNumber(['TargetingCoppa', 'targeting_coppa']),
  TargetingLimitedAdTracking: parseNumber(['TargetingLimitedAdTracking']),
  TargetingSitePages: parseString(['TargetingSitePages', 'targeting_site_pages']),
  TargetingSitePagesIsAllowed: parseNumber([
    'TargetingSitePagesIsAllowed',
    'targeting_site_pages_is_allowed',
  ]),
  TargetingSiteDomain: parseString(['TargetingSiteDomain', 'targeting_site_domain']),
  TargetingSiteDomainIsAllowed: parseNumber([
    'TargetingSiteDomainIsAllowed',
    'targeting_site_domain_is_allowed',
  ]),
  FixedPrice: parseFloat(['FixedPrice']),
  DailyRequestCap: parseNumber(['DailyRequestCap', 'daily_request_cap']),
  PacingType: parseNumber(['PacingType', 'pacing_type']),
  PlaybackMethod: parseString(['PlaybackMethod', 'playback_method']),
  InventoryType: parseNumber(['InventoryType']),
  IsPublicaTagFallbackEnabled: parseNumber(['IsPublicaTagFallbackEnabled']),
  IsFallbackBackfillEnabled: parseNumber(['IsFallbackBackfillEnabled']),
  FrequencyActive: parseNumber(['FrequencyActive'], ResourceActiveDisabled),
  FallbackAuctionParticipationEnabled: parseNumber(['FallbackAuctionParticipationEnabled']),
  FrequencyLimit: parseNumber(['FrequencyLimit']),
  FrequencyTTL: parseNumber(['FrequencyTTL']),
  PrivateAuction: parseNumber(['PrivateAuction']),
  MatchDealID: parseNumber(['MatchDealID']),
  StartDate: ['StartDate'],
  EndDate: ['EndDate'],
  Quantity: parseNumber(['Quantity']),
  CRONQuantityCount: parseNumber(['CRONQuantityCount']),
  CRONQuantityCountSinceLastTest: parseNumber(['CRONQuantityCountSinceLastTest']),
  UnpaidBidderEnabled: parseNumber(['UnpaidBidderEnabled']),
  DOOHEnabled: parseNumber(['DOOHEnabled']),
  DOOHDeviceTypeRewriteEnabled: parseNumber(['DOOHDeviceTypeRewriteEnabled']),
  RemoveFmtJSON: parseNumber(['RemoveFmtJSON']),
  EnableOptableEIDs: parseNumber(['EnableOptableEIDs']),
  FlexibleBiddingEnabled: parseNumber(['FlexibleBiddingEnabled'], EBool.EBool_FALSE),
  FlexibleBiddingThresholds: parseArray(
    ['FlexibleBiddingThresholds'],
    transformFlexibleBiddingThreshold,
  ),
  FlexibleBiddingType: parseNumber(['FlexibleBiddingType'], FlexibleBiddingType.CPM),
  PositionTargeting: parseNumber(['PositionTargeting']),
  DealAtTargeting: parseNumber(['DealAtTargeting']),
  PodSequenceTargeting: parseNumber(['PodSequenceTargeting']),
  EnableSpotxBidderAssociation: parseNumber(['EnableSpotxBidderAssociation']),
  EnableMagniteCTVBidderAssociation: parseNumber(['EnableMagniteCTVBidderAssociation']),
  EnableSamsungSSPBidderAssociation: parseNumber(['EnableSamsungSSPBidderAssociation']),
  EnableIrisSignal: parseNumber(['EnableIrisSignal']),
  ExtendedContentIDVendors: parseString(['ExtendedContentIDVendors']),
  TargetedAdBreak: parseNumber(['TargetedAdBreak']),
  ContextualSegmentIDS: parseArray(['ContextualSegments'], (obj: any) => {
    return obj.ID;
  }),
  EnableIASDataSharing: parseNumber(['EnableIASDataSharing'], EBool.EBool_FALSE),
  IASDataSharing: parseString(['IASDataSharing', 'iasdatasharing'], ','),
  DoubleVerifyDataSharing: parseString(['DoubleVerifyDataSharing', 'doubleverifydatasharing'], ','),
  LocalTargetingLogic: parseNumber(['LocalTargetingLogic']),
  TargetingFWSectionIDs: parseString(['TargetingFWSectionIDs']),
  TargetingFWSectionIDsIsAllowed: parseNumber(['TargetingFWSectionIDsIsAllowed']),
  VastExtensionPriceEnabled: parseNumber(['VastExtensionPriceEnabled']),
  Dayparting: parseString(['Dayparting']),
  DaypartingIsAllowed: parseNumber(['DaypartingIsAllowed']),
  Timezone: parseString(['Timezone', 'timezone']),
  MimeTypeOverride: parseString(['MimeTypeOverride']),

  TargetingCCPA: ['TargetingCCPA'],
  EnableRemoveGPPFromBidRequest: ['EnableRemoveGPPFromBidRequest'],
  EnableSecondaryRequests: ['EnableSecondaryRequests'],
  EnableSponsorshipMode: ['EnableSponsorshipMode'],
  SamplingPercentageDisabledUntil: ['SamplingPercentageDisabledUntil'],
  SamplingPercentage: parseFloat(['SamplingPercentage']),
  OptionalMessage: ['OptionalMessage'],
  BidderLabels: parseArray(['BidderLabels', 'bidderlabels'], transformBidderLabelWrapperFromAPI),
  YahooConnectIDEnabled: parseNumber(['YahooConnectIDEnabled'], EBool.EBool_FALSE),
  TargetingRequestedPodDurationMin: ['TargetingRequestedPodDurationMin'],
  TargetingRequestedPodDurationMax: ['TargetingRequestedPodDurationMax'],
  IsAutoCreatedByPlatformConnect: ['IsAutoCreatedByPlatformConnect'],
  TargetingGvinParamExists: ['TargetingGvinParamExists'],
  EnableTargetingFirstAdbreakInUserSession: ['EnableTargetingFirstAdbreakInUserSession'],
  PresetIDs: ['PresetIDs'],
  TargetingGDPR: parseNumber(['TargetingGDPR', 'targeting_gdpr']),
  TargetingGDPRConsent: parseNumber(['TargetingGDPRConsent', 'targeting_gdpr_consent']),
  InventoryPartnerDomainOverride: parseString([
    'InventoryPartnerDomainOverride',
    'inventory_partner_domain_override',
  ]),
  InventoryPartnerDomainOverrideIfEmpty: parseNumber(
    ['InventoryPartnerDomainOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  EnableTrafficShaping: parseNumber(['EnableTrafficShaping'], EBool.EBool_FALSE),
  UID2ExtendedIDSetting: parseNumber(['UID2ExtendedIDSetting']),
  LiverampExtendedIDSetting: parseNumber(['LiverampExtendedIDSetting']),
  GooglePairExtendedIDSetting: parseNumber(['GooglePairExtendedIDSetting']),
  TargetingDeviceIDType: parseNumber(['TargetingDeviceIDType']),
  TargetingUID20TargetingType: parseNumber(['TargetingUID20TargetingType']),
  TargetingGooglePairIDTargetingType: parseNumber(['TargetingGooglePairIDTargetingType']),
  TargetingLiveRampATSTargetingType: parseNumber(['TargetingLiveRampATSTargetingType']),
  TargetingSamsungIdentityTargetingType: parseNumber(['TargetingSamsungIdentityTargetingType']),
  TargetingIrisSignalTargetingType: parseNumber(['TargetingIrisSignalTargetingType']),
  TargetingSamsungEPGMetadataTargetingType: parseNumber([
    'TargetingSamsungEPGMetadataTargetingType',
  ]),
});

export function fromAPIHeaderBidderWrapper(_fromObjValue: any): IHeaderBidder {
  return fromAPIHeaderBidder(_fromObjValue);
}

export type HeaderBidderActive = number;
export const HeaderBidderActiveInactive: HeaderBidderActive = 1;
export const HeaderBidderActiveActive: HeaderBidderActive = 2;

export type HeaderBidderPoddedRequestActive = number;
export const HeaderBidderPoddedRequestActiveInactive: HeaderBidderPoddedRequestActive = 1;
export const HeaderBidderPoddedRequestActiveActive: HeaderBidderPoddedRequestActive = 2;

export enum EHeaderBidder_PoddedRequestType {
  HeaderBidderPoddedRequestTypeUnknown = 0,
  HeaderBidderPoddedRequestTypeNonPodded = 1,
  HeaderBidderPoddedRequestTypeStructured = 2,
  HeaderBidderPoddedRequestTypeDynamic = 3,
  HeaderBidderPoddedRequestTypeHybrid = 4,
}

export type DeviceTypeEnabled = number;
export const DeviceTypeEnabledDisabled: DeviceTypeEnabled = 1;
export const DeviceTypeEnabledEnabled: DeviceTypeEnabled = 2;

export type SegmentTargeting = number;
export const SegmentTargetingOR: SegmentTargeting = 0;
export const SegmentTargetingAND: SegmentTargeting = 1;

export type HeaderBidderCampaignTargeting = number;
export const HeaderBidderCampaignTargetingInactive: HeaderBidderCampaignTargeting = 0;
export const HeaderBidderCampaignTargetingAll: HeaderBidderCampaignTargeting = 1;
export const HeaderBidderCampaignTargetingSelected: HeaderBidderCampaignTargeting = 2;
export const HeaderBidderCampaignTargetingPaid: HeaderBidderCampaignTargeting = 3;
export const HeaderBidderCampaignTargetingHouse: HeaderBidderCampaignTargeting = 4;
export const HeaderBidderOrderTargetingSelected: HeaderBidderCampaignTargeting = 5;

export const toAPIHeaderBidder = transformModel<IHeaderBidder>({
  ID: parseNumber(['ID', 'id']),
  SuperOnly: parseBoolTrue(['SuperOnly', 'superonly']),
  Active: parseNumber(['Active', 'active'], 0, (v?: number) => {
    if ((v as number) >= HeaderBidderActiveActive) {
      return HeaderBidderActiveActive;
    }
    return HeaderBidderActiveInactive;
  }),
  SiteID: parseNumber(['SiteID', 'siteid'], -1),
  Name: toString(['Name', 'name']),
  Bidder: toString(['Bidder', 'bidder']),
  Params: toString(['Params', 'params']),
  Settings: toString(['Settings', 'settings']),
  BidFloor: parseFloat(['BidFloor', 'bidfloor'], 0),
  BidFloorCurrency: parseNumber(['BidFloorCurrency', 'bidfloorcurrency']),
  MaxAuctionCap: parseFloat(['MaxAuctionCap', 'maxauctioncap'], 0),
  BidAdjustmentFactor: parseFloat(
    ['BidAdjustmentFactor', 'bidadjustmentfactor'],
    0,
    (val: number) => {
      return val / 100;
    },
  ),
  BidAdjustmentValue: parseFloat(['BidAdjustmentValue', 'bidadjustmentvalue']),
  BuyTypeID: parseNumber(['BuyTypeID']),
  ShareOfVoice: parseFloat(['ShareOfVoice', 'shareofvoice'], 0, (val: number) => {
    return val / 100;
  }),
  ShareOfVoiceType: parseNumber(
    ['ShareOfVoiceType'],
    EHeaderBidder_ShareOfVoiceType.HeaderBidder_ShareOfVoiceType_UNKNOWN,
  ),
  AuctionParticipationPercentage: parseNumber(['AuctionParticipationPercentage']),
  QPS: parseNumber(['QPS', 'qps'], 0),
  CurrencyDisplayed: parseNumber(['CurrencyDisplayed', 'currency']),
  DeviceEnv: toString(['DeviceEnv', 'deviceenv']),
  DeviceType: toString(['DeviceType', 'devicetype']),
  DeviceTypeEnabled: parseNumber(['DeviceTypeEnabled', 'devicetypeenabled'], 0, (v?: number) => {
    if ((v as number) >= DeviceTypeEnabledEnabled) {
      return DeviceTypeEnabledEnabled;
    }
    return DeviceTypeEnabledDisabled;
  }),
  IPFilter: toString(['IPFilter', 'ipfilter']),
  UIBidderType: toString(['UIBidderType', 'uibiddertype']),
  PoddedRequestActive: parseNumber(['PoddedRequestActive'], 0, (v?: number) => {
    if ((v as number) >= HeaderBidderPoddedRequestActiveActive) {
      return HeaderBidderPoddedRequestActiveActive;
    }
    return HeaderBidderPoddedRequestActiveInactive;
  }),
  PoddedRequestType: parseNumber(['PoddedRequestType']),
  HybridStructuredSlotDurations: parseString([
    'HybridStructuredSlotDurations',
    'hybridstructuredslotdurations',
  ]),
  BrandCategoryUniquenessActive: parseNumber(
    ['BrandCategoryUniquenessActive'],
    EBool.EBool_UNKNOWN,
  ),
  MultiBidActive: parseNumber(['MultiBidActive'], EBool.EBool_UNKNOWN),
  HonorChannelFloor: parseNumber(['HonorChannelFloor'], EBool.EBool_UNKNOWN),
  AnonymizeDeviceID: parseNumber(['AnonymizeDeviceID'], EBool.EBool_UNKNOWN),
  AnonymizeIP: parseNumber(['AnonymizeIP'], EBool.EBool_UNKNOWN),
  TargetingDeviceMake: toString(['TargetingDeviceMake', 'targetingdevicemake']),
  TargetingDeviceModel: toString(['TargetingDeviceModel', 'targetingdevicemodel']),
  TargetingDeviceOs: toString(['TargetingDeviceOs', 'targetingdeviceos']),
  TargetingDeviceType: toString(['TargetingDeviceType', 'targetingdevicetype']),
  TargetingDevices: toString(['TargetingDevices', 'targetingdevices']),
  TargetingDevicesIsAllowed: parseNumber('TargetingDevicesIsAllowed'),
  TargetingAudienceSegmentID: toString([
    'TargetingAudienceSegmentID',
    'targetingaudiencesegmentid',
  ]),
  TargetingAudienceSegmentIDIsAllowed: parseNumber('TargetingAudienceSegmentIDIsAllowed'),
  TargetingCountryGroupIDs: parseString(['TargetingCountryGroupIDs']),
  TargetingCountryGroupIDsIsAllowed: parseNumber('TargetingCountryGroupIDsIsAllowed'),
  TargetingAppBundleID: parseString(['TargetingAppBundleID', 'targeting_app_bundle_id']),
  TargetingAppBundleIDIsAllowed: parseBoolTrue('TargetingAppBundleIDIsAllowed'),
  TargetingAppBundleSitePagesLogic: parseNumber('TargetingAppBundleSitePagesLogic'),
  TargetingDealID: parseString(['TargetingDealID', 'targeting_deal_id']),
  TargetingDealIDIsAllowed: parseNumber('TargetingDealIDIsAllowed'),
  TargetingLivestream: parseNumber(['TargetingLivestream', 'targetinglivestream'], 0),
  LivestreamTargeting: parseString(['LivestreamTargeting']),
  Priority: parseNumber(['Priority', 'priority'], 0),
  SubPriority: parseNumber(['SubPriority', 'sub_priority'], 0),
  Timeout: parseNumber(['Timeout', 'timeout'], 0),
  TmaxOffset: 'TmaxOffset',
  Pods: toString(['Slots', 'slots', 'Pods', 'pods'], (val: any) => {
    const slots = typeof val === 'string' ? val.split(',') : val;
    if (slots.indexOf('all') > -1) {
      return '';
    }
    return slots.join(',');
  }),
  Slots: toString(['Slots', 'slots', 'Pods', 'pods'], (val: any) => {
    const slots = typeof val === 'string' ? val.split(',') : val;
    if (slots.indexOf('all') > -1) {
      return '';
    }
    return slots.join(',');
  }),
  EnforceSlotTargeting: parseNumber(['EnforceSlotTargeting'], EBool.EBool_FALSE),
  AdbreakTargeting: toString(['AdbreakTargeting'], (val: any) => {
    if (val === '1') {
      return val;
    }
    return '';
  }),
  TargetingDeviceCountry: toString(['TargetingDeviceCountry', 'targetingdevicecountry']),
  LocalTargeting: toString(['LocalTargeting', 'targetingdevicecountry']),
  LocalTargetingIsAllowed: parseBoolTrue('LocalTargetingIsAllowed'),
  MinAdDurationOverride: parseNumber(['MinAdDurationOverride', 'minaddurationoverride'], 0),
  MaxAdDurationOverride: parseNumber(['MaxAdDurationOverride', 'maxaddurationoverride'], 0),
  StartDelayOverride: parseNumber(['StartDelayOverride']),
  AppStoreURL: parseObject(['AppStoreURL', 'appstoreurl'], transformHeaderBidderDeviceAwareModel),
  AppStoreUrlOverrideIfEmpty: parseNumber(
    ['AppStoreURLOverrideIfEmpty', 'AppStoreUrlOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  AppBundleOverride: toString(['AppBundleOverride', 'app_bundle_override']),
  AppBundleOverrideIfEmpty: parseNumber(['AppBundleOverrideIfEmpty'], EBool.EBool_FALSE),
  AppIDOverride: toString(['AppIDOverride', 'app_id_override']),
  AppIDOverrideIfEmpty: parseNumber(['AppIDOverrideIfEmpty'], EBool.EBool_FALSE),
  AppNameOverride: parseString(['AppNameOverride', 'app_name_override']),
  AppNameOverrideIfEmpty: parseNumber(['AppNameOverrideIfEmpty'], EBool.EBool_FALSE),
  AppDomainOverride: toString(['AppDomainOverride', 'app_domain_override']),
  AppDomainOverrideIfEmpty: parseNumber(['AppDomainOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentChannelOverride: parseString(['ContentChannelOverride']),
  ContentChannelOverrideIfEmpty: parseNumber(['ContentChannelOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentNetworkOverride: parseString(['ContentNetworkOverride']),
  ContentNetworkOverrideIfEmpty: parseNumber(['ContentNetworkOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentSeriesOverride: parseString(['ContentSeriesOverride']),
  ContentSeriesOverrideIfEmpty: parseNumber(['ContentSeriesOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentTitleOverride: parseString(['ContentTitleOverride']),
  ContentTitleOverrideIfEmpty: parseNumber(['ContentTitleOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentGenreOverride: parseString(['ContentGenreOverride']),
  ContentGenreOverrideIfEmpty: parseNumber(['ContentGenreOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentDistribNameOverride: parseString(['ContentDistribNameOverride']),
  ContentDistribNameOverrideIfEmpty: parseNumber(
    ['ContentDistribNameOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentCategoryOverride: parseString(['ContentCategoryOverride']),
  ContentCategoryOverrideIfEmpty: parseNumber(
    ['ContentCategoryOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentRatingOverride: parseString(['ContentRatingOverride']),
  ContentRatingOverrideIfEmpty: parseNumber(['ContentRatingOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentProdqualOverride: parseString(['ContentProdqualOverride']),
  ContentProdqualOverrideIfEmpty: parseNumber(
    ['ContentProdqualOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentProducerNameOverride: parseString(['ContentProducerNameOverride']),
  ContentProducerNameOverrideIfEmpty: parseNumber(
    ['ContentProducerNameOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentProducerIDOverride: parseString(['ContentProducerIDOverride']),
  ContentProducerIDOverrideIfEmpty: parseNumber(
    ['ContentProducerIDOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentKeywordsOverride: parseString(['ContentKeywordsOverride']),
  ContentKeywordsOverrideIfEmpty: parseNumber(
    ['ContentKeywordsOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentLanguageOverride: parseString(['ContentLanguageOverride']),
  ContentLanguageOverrideIfEmpty: parseNumber(
    ['ContentLanguageOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentIDOverride: parseString(['ContentIDOverride']),
  ContentIDOverrideIfEmpty: parseNumber(['ContentIDOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentSeasonOverride: parseString(['ContentSeasonOverride']),
  ContentSeasonOverrideIfEmpty: parseNumber(['ContentSeasonOverrideIfEmpty'], EBool.EBool_FALSE),
  ClearDefaultBitrateLimits: parseNumber(['ClearDefaultBitrateLimits'], EBool.EBool_FALSE),
  SegmentTargeting: parseNumber('SegmentTargeting'),
  CampaignTargeting: parseNumber(['CampaignTargeting', 'campaign_targeting']),
  CampaignIDS: toString(['CampaignIDS', 'campaignids']),
  OrderIDS: toString(['OrderIDS', 'orderids']),
  EndpointDealIDPassthroughActive: parseNumber(
    ['EndpointDealIDPassthroughActive'],
    EBool.EBool_FALSE,
  ),
  RemoveContentFromBidRequest: parseNumber(['RemoveContentFromBidRequest'], EBool.EBool_FALSE),
  RemoveContentChannelFromBidRequest: parseNumber(
    ['RemoveContentChannelFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentNetworkFromBidRequest: parseNumber(
    ['RemoveContentNetworkFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentSeriesFromBidRequest: parseNumber(
    ['RemoveContentSeriesFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentTitleFromBidRequest: parseNumber(
    ['RemoveContentTitleFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentGenreFromBidRequest: parseNumber(
    ['RemoveContentGenreFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentDistribNameFromBidRequest: parseNumber(
    ['RemoveContentDistribNameFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentCategoryFromBidRequest: parseNumber(
    ['RemoveContentCategoryFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentRatingFromBidRequest: parseNumber(
    ['RemoveContentRatingFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentProdqualFromBidRequest: parseNumber(
    ['RemoveContentProdqualFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentProducerNameFromBidRequest: parseNumber(
    ['RemoveContentProducerNameFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentProducerIDFromBidRequest: parseNumber(
    ['RemoveContentProducerIDFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentKeywordsFromBidRequest: parseNumber(
    ['RemoveContentKeywordsFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentLanguageFromBidRequest: parseNumber(
    ['RemoveContentLanguageFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentIDFromBidRequest: parseNumber(['RemoveContentIDFromBidRequest'], EBool.EBool_FALSE),
  RemoveContentSeasonFromBidRequest: parseNumber(
    ['RemoveContentSeasonFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveInventoryPartnerDomainFromBidRequest: parseNumber(
    ['RemoveInventoryPartnerDomainFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveAppStoreURLFromBidRequest: parseNumber(
    ['RemoveAppStoreURLFromBidRequest'],
    EBool.EBool_FALSE,
  ),
  SiteDomainOverride: toString(['SiteDomainOverride', 'site_domain_override']),
  SiteDomainOverrideIfEmpty: parseNumber(['SiteDomainOverrideIfEmpty'], EBool.EBool_FALSE),
  CustomTargeting: toString(['CustomTargeting', 'customtargeting']),
  CustomTargetingInclusion: parseNumber(['CustomTargetingInclusion', 'custom_targeting_inclusion']),
  Custom4: toString(['Custom4', 'custom4']),
  Custom4Inclusion: parseNumber(['Custom4Inclusion', 'custom4_inclusion']),
  Custom5: toString(['Custom5', 'custom5']),
  Custom5Inclusion: parseNumber(['Custom5Inclusion', 'custom5_inclusion']),
  Custom6: toString(['Custom6', 'custom6']),
  Custom6Inclusion: parseNumber(['Custom6Inclusion', 'custom6_inclusion']),
  Custom7: toString(['Custom7', 'custom7']),
  Custom7Inclusion: parseNumber(['Custom7Inclusion', 'custom7_inclusion']),
  Custom8: toString(['Custom8', 'custom8']),
  Custom8Inclusion: parseNumber(['Custom8Inclusion', 'custom8_inclusion']),
  Custom9: toString(['Custom9', 'custom9']),
  Custom9Inclusion: parseNumber(['Custom9Inclusion', 'custom9_inclusion']),
  Custom10: toString(['Custom10', 'custom10']),
  Custom10Inclusion: parseNumber(['Custom10Inclusion', 'custom10_inclusion']),
  Custom11: toString(['Custom11', 'custom11']),
  Custom11Inclusion: parseNumber(['Custom11Inclusion', 'custom11_inclusion']),
  Custom12: toString(['Custom12', 'custom12']),
  Custom12Inclusion: parseNumber(['Custom12Inclusion', 'custom12_inclusion']),
  Custom13: toString(['Custom13', 'custom13']),
  Custom13Inclusion: parseNumber(['Custom13Inclusion', 'custom13_inclusion']),
  Custom14: toString(['Custom14', 'custom14']),
  Custom14Inclusion: parseNumber(['Custom14Inclusion', 'custom14_inclusion']),
  Custom15: toString(['Custom15', 'custom15']),
  Custom15Inclusion: parseNumber(['Custom15Inclusion', 'custom15_inclusion']),
  Custom16: toString(['Custom16', 'custom16']),
  Custom16Inclusion: parseNumber(['Custom16Inclusion', 'custom16_inclusion']),
  Custom17: toString(['Custom17', 'custom17']),
  Custom17Inclusion: parseNumber(['Custom17Inclusion', 'custom17_inclusion']),
  Custom18: toString(['Custom18', 'custom18']),
  Custom18Inclusion: parseNumber(['Custom18Inclusion', 'custom18_inclusion']),
  TargetingContentChannel: parseString(['TargetingContentChannel', 'targeting_content_channel']),
  TargetingContentChannelIsAllowed: parseNumber('TargetingContentChannelIsAllowed'),
  TargetingContentDistributor: parseString(['TargetingContentDistributor']),
  TargetingContentDistributorIsAllowed: parseNumber(['TargetingContentDistributorIsAllowed']),
  TargetingContentGenre: parseString(['TargetingContentGenre']),
  TargetingContentGenreIsAllowed: parseNumber(['TargetingContentGenreIsAllowed']),
  TargetingContentID: parseString(['TargetingContentID']),
  TargetingContentIDIsAllowed: parseNumber('TargetingContentIDIsAllowed'),
  TargetingContentNetwork: parseString(['TargetingContentNetwork', 'targeting_content_network']),
  TargetingContentNetworkIsAllowed: parseNumber('TargetingContentNetworkIsAllowed'),
  TargetingContentProducer: parseString(['TargetingContentProducer', 'targeting_content_producer']),
  TargetingContentProducerIsAllowed: parseNumber('TargetingContentProducerIsAllowed'),
  TargetingContentRating: parseString(['TargetingContentRating']),
  TargetingContentRatingIsAllowed: parseNumber(['TargetingContentRatingIsAllowed']),
  TargetingContentSeries: parseString(['TargetingContentSeries']),
  TargetingContentSeriesIsAllowed: parseNumber(['TargetingContentSeriesIsAllowed']),
  TargetingContentTitle: parseString(['TargetingContentTitle']),
  TargetingContentTitleIsAllowed: parseNumber(['TargetingContentTitleIsAllowed']),
  TargetingContentCategory: parseString(['TargetingContentCategory']),
  TargetingContentCategoryIsAllowed: parseNumber(['TargetingContentCategoryIsAllowed']),
  TargetingContentLanguage: parseString(['TargetingContentLanguage']),
  TargetingContentLanguageIsAllowed: parseNumber(['TargetingContentLanguageIsAllowed']),
  Skip: parseNumber(['Skip'], EBool.EBool_UNKNOWN),
  SkipMin: parseNumber(['SkipMin'], 0),
  SkipAfter: parseNumber(['SkipAfter'], 0),
  TargetingDeviceTypes: parseString(['TargetingDeviceTypes', 'targeting_device_types']),
  TargetingDeviceTypesIsAllowed: parseNumber([
    'TargetingDeviceTypesIsAllowed',
    'targeting_device_types_is_allowed',
  ]),
  TargetingDeviceSubTypes: parseString(['TargetingDeviceSubTypes']),
  TargetingDeviceSubTypesIsAllowed: parseNumber('TargetingDeviceSubTypesIsAllowed'),
  TargetingDeviceModels: parseString(['TargetingDeviceModels']),
  TargetingDeviceModelsIsAllowed: parseNumber('TargetingDeviceModelsIsAllowed'),
  TargetingPrice: parseString(['TargetingPrice', 'targeting_price']),
  TargetingPriceIsAllowed: parseNumber(['TargetingPriceIsAllowed', 'targeting_price_is_allowed']),
  TargetingCoppa: parseNumber(['TargetingCoppa', 'targeting_coppa']),
  TargetingLimitedAdTracking: parseNumber([
    'TargetingLimitedAdTracking',
    'targeting_limited_ad_tracking',
  ]),
  TargetingSitePages: parseString(['TargetingSitePages', 'targeting_site_pages']),
  TargetingSitePagesIsAllowed: parseNumber([
    'TargetingSitePagesIsAllowed',
    'targeting_site_pages_is_allowed',
  ]),
  TargetingSiteDomain: parseString(['TargetingSiteDomain', 'targeting_site_domain']),
  TargetingSiteDomainIsAllowed: parseNumber([
    'TargetingSiteDomainIsAllowed',
    'targeting_site_domain_is_allowed',
  ]),
  FixedPrice: parseFloat(['FixedPrice', 'fixed_price']),
  DailyRequestCap: parseNumber(['DailyRequestCap', 'daily_request_cap']),
  PacingType: parseNumber(['PacingType', 'pacing_type']),
  PlaybackMethod: parseString(['PlaybackMethod', 'playback_method']),
  Deals: parseArray(['Deals'], transformOnlyIds),
  EnableDealPriorityOverride: parseNumber(['EnableDealPriorityOverride'], EBool.EBool_TRUE),
  AppCatOverride: toString(['AppCatOverride', 'app_cat_override']),
  AppCatOverrideIfEmpty: parseNumber(['AppCatOverrideIfEmpty'], EBool.EBool_FALSE),
  InventoryType: parseNumber(
    ['InventoryType'],
    EHeaderBidder_InventoryType.HeaderBidder_INVENTORY_TYPE_NONE,
  ),
  IsPublicaTagFallbackEnabled: parseNumber([
    'IsPublicaTagFallbackEnabled',
    'is_publica_tag_fallback_enabled',
  ]),
  IsFallbackBackfillEnabled: parseNumber([
    'IsFallbackBackfillEnabled',
    'is_fallback_backfill_enabled',
  ]),
  FallbackAuctionParticipationEnabled: parseNumber(['FallbackAuctionParticipationEnabled']),
  FrequencyActive: parseNumber(['FrequencyActive'], ResourceActiveDisabled),
  FrequencyLimit: parseNumber(['FrequencyLimit']),
  FrequencyTTL: parseNumber(['FrequencyTTL']),
  PrivateAuction: parseNumber(['PrivateAuction']),
  MatchDealID: parseNumber(['MatchDealID']),
  StartDate: ['StartDate'],
  EndDate: ['EndDate'],
  Quantity: parseNumber(['Quantity']),
  CRONQuantityCount: parseNumber(['CRONQuantityCount']),
  ServerSideTrackersEnabled: parseNumber(['ServerSideTrackersEnabled']),
  UnpaidBidderEnabled: parseNumber(['UnpaidBidderEnabled'], EBool.EBool_FALSE),
  DOOHEnabled: parseNumber(['DOOHEnabled'], EBool.EBool_FALSE),
  DOOHDeviceTypeRewriteEnabled: parseNumber(['DOOHDeviceTypeRewriteEnabled'], EBool.EBool_FALSE),
  RemoveFmtJSON: parseNumber(['RemoveFmtJSON'], EBool.EBool_FALSE),
  FlexibleBiddingEnabled: parseNumber(['FlexibleBiddingEnabled'], EBool.EBool_FALSE),
  FlexibleBiddingThresholds: parseArray(
    ['FlexibleBiddingThresholds'],
    transformFlexibleBiddingThreshold,
  ),
  FlexibleBiddingType: parseNumber(['FlexibleBiddingType'], FlexibleBiddingType.CPM),
  PositionTargeting: parseNumber(['PositionTargeting']),
  DealAtTargeting: parseNumber(['DealAtTargeting']),
  PodSequenceTargeting: parseNumber(['PodSequenceTargeting']),
  SiteIDS: ['SiteIDS'],
  ChannelGroupIDS: ['ChannelGroupIDS'],
  ChannelListIDS: ['ChannelListIDS'],
  EnableSpotxBidderAssociation: parseNumber(['EnableSpotxBidderAssociation'], EBool.EBool_FALSE),
  EnableMagniteCTVBidderAssociation: parseNumber(
    ['EnableMagniteCTVBidderAssociation'],
    EBool.EBool_FALSE,
  ),
  EnableSamsungSSPBidderAssociation: parseNumber(
    ['EnableSamsungSSPBidderAssociation'],
    EBool.EBool_FALSE,
  ),
  EnableIrisSignal: parseNumber(['EnableIrisSignal'], EBool.EBool_FALSE),
  EnableOptableEIDs: parseNumber(['EnableOptableEIDs'], EBool.EBool_FALSE),
  ExtendedContentIDVendors: parseString(['ExtendedContentIDVendors']),
  TargetedAdBreak: parseNumber(
    ['TargetedAdBreak'],
    EHeaderBidder_TargetedAdBreak.HeaderBidder_TARGETED_ADBREAK_ALL,
  ),
  ContextualSegmentIDS: parseArray(['ContextualSegmentIDS'], (obj: any) => {
    return obj;
  }),
  EnableIASDataSharing: parseNumber(['EnableIASDataSharing'], EBool.EBool_FALSE),
  IASDataSharing: toString(['IASDataSharing'], (v: any) =>
    ((Array.isArray(v) && v) || []).join(','),
  ),
  DoubleVerifyDataSharing: toString(['DoubleVerifyDataSharing'], (v: any) =>
    ((Array.isArray(v) && v) || []).join(','),
  ),
  LocalTargetingLogic: parseNumber(['LocalTargetingLogic']),
  TargetingFWSectionIDs: parseString(['TargetingFWSectionIDs']),
  TargetingFWSectionIDsIsAllowed: parseNumber(['TargetingFWSectionIDsIsAllowed']),
  VastExtensionPriceEnabled: parseNumber(['VastExtensionPriceEnabled'], EBool.EBool_FALSE),
  Dayparting: parseString(['Dayparting']),
  DaypartingIsAllowed: parseNumber(['DaypartingIsAllowed']),
  Timezone: parseString(['Timezone', 'timezone']),
  MimeTypeOverride: parseString(['MimeTypeOverride']),
  TargetingCCPA: ['TargetingCCPA'],
  EnableRemoveGPPFromBidRequest: ['EnableRemoveGPPFromBidRequest'],
  EnableSecondaryRequests: ['EnableSecondaryRequests'],
  EnableSponsorshipMode: ['EnableSponsorshipMode'],
  SamplingPercentageDisabledUntil: ['SamplingPercentageDisabledUntil'],
  SamplingPercentage: parseFloat(['SamplingPercentage']),
  BidderLabels: parseArray(['BidderLabels', 'bidderlabels'], transformBidderLabelWrapperToAPI),
  YahooConnectIDEnabled: parseNumber(['YahooConnectIDEnabled'], EBool.EBool_FALSE),
  TargetingRequestedPodDurationMin: ['TargetingRequestedPodDurationMin'],
  TargetingRequestedPodDurationMax: ['TargetingRequestedPodDurationMax'],
  IsAutoCreatedByPlatformConnect: ['IsAutoCreatedByPlatformConnect'],
  TargetingGvinParamExists: ['TargetingGvinParamExists'],
  EnableTargetingFirstAdbreakInUserSession: ['EnableTargetingFirstAdbreakInUserSession'],
  PresetIDs: ['PresetIDs'],
  TargetingGDPR: parseNumber(['TargetingGDPR', 'targeting_gdpr']),
  TargetingGDPRConsent: parseNumber(['TargetingGDPRConsent', 'targeting_gdpr_consent']),
  InventoryPartnerDomainOverride: toString([
    'InventoryPartnerDomainOverride',
    'inventory_partner_domain_override',
  ]),
  InventoryPartnerDomainOverrideIfEmpty: parseNumber(
    ['InventoryPartnerDomainOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  EnableTrafficShaping: parseNumber(['EnableTrafficShaping'], EBool.EBool_FALSE),
  UID2ExtendedIDSetting: parseNumber(['UID2ExtendedIDSetting']),
  LiverampExtendedIDSetting: parseNumber(['LiverampExtendedIDSetting']),
  GooglePairExtendedIDSetting: parseNumber(['GooglePairExtendedIDSetting']),
  TargetingDeviceIDType: parseNumber(['TargetingDeviceIDType']),
  TargetingUID20TargetingType: parseNumber(['TargetingUID20TargetingType']),
  TargetingGooglePairIDTargetingType: parseNumber(['TargetingGooglePairIDTargetingType']),
  TargetingLiveRampATSTargetingType: parseNumber(['TargetingLiveRampATSTargetingType']),
  TargetingSamsungIdentityTargetingType: parseNumber(['TargetingSamsungIdentityTargetingType']),
  TargetingIrisSignalTargetingType: parseNumber(['TargetingIrisSignalTargetingType']),
  TargetingSamsungEPGMetadataTargetingType: parseNumber([
    'TargetingSamsungEPGMetadataTargetingType',
  ]),
});

export interface IHeaderBidderPriceIncrement {
  [key: string]: any;
  ID?: number;
  Min: string;
  Max: string;
  Increment: string;
}

export interface IBypassDedupConfig {
  ID?: number;
  BypassMediafileDedup: EBool;
  BypassAdomainDedup: EBool;
  BypassIABCatDedup: EBool;
  BypassDedupByAdAsSeparation: EBool;
  BypassDedupAdCount: number;
  BypassDedupByDurationAsSeparation: EBool;
  BypassDedupDuration: number;
  BypassDedupIfSurroundedByDedupedAds: EBool;
  BidderIDs: string;
  BidderOptions: ISelectOption[];
  HouseAdIDs: string;
  HouseAdsOptions: ISelectOption[];
  IsBypassAdomainFieldsOpen: EBool;
  IsAdomainExactMatch: EBool;
  Adomain: string[]; // adomain list - displayed in the table
  AdomainStrings: string;
  AdomainUserEntry: string; // adomain single user entry
}

export const transformHeaderBidderPriceIncrement = transformModel<IHeaderBidderPriceIncrement>({
  ID: parseNumber(['ID', 'id']),
  Min: parseFloat(['Min', 'min'], 0),
  Max: parseFloat(['Max', 'max'], 1),
  Increment: parseFloat(['Increment', 'increment'], 0.25),
});

export const transformHeaderBypassDedupConfig = transformModel<IBypassDedupConfig>({
  ID: parseNumber(['ID', 'id']),
  BypassMediafileDedup: parseNumber(['BypassMediafileDedup'], 0),
  BypassAdomainDedup: parseNumber(['BypassAdomainDedup'], 0),
  BypassIABCatDedup: parseNumber(['BypassIABCatDedup'], 0),
  BypassDedupByAdAsSeparation: parseNumber(['BypassDedupByAdAsSeparation']),
  BypassDedupAdCount: parseNumber(['BypassDedupAdCount'], 0),
  BypassDedupByDurationAsSeparation: parseNumber(['BypassDedupByDurationAsSeparation']),
  BypassDedupDuration: parseNumber(['BypassDedupDuration'], 0),
  BypassDedupIfSurroundedByDedupedAds: parseNumber(['BypassDedupIfSurroundedByDedupedAds']),
  BidderIDs: parseString(['BidderIDs', 'BidderIDs']),
  HouseAdIDs: parseString(['HouseAdIDs']),
  AdomainStrings: parseString(['AdomainStrings']),
  IsAdomainExactMatch: parseNumber(['IsAdomainExactMatch'], 0),
});

export type Subscription = number;
export const SUBSCRIPTION_UNKNOWN: Subscription = 0;
export const SUBSCRIPTION_ANALYTICS: Subscription = 1;
export const SUBSCRIPTION_DIRECT_RECOVERY: Subscription = 2;
export const SUBSCRIPTION_PUBLICA_RECOVERY: Subscription = 3;
export const SUBSCRIPTION_HEADER_BIDDER: Subscription = 4;

export type CookieStrategy = number;
export const COOKIESTRATEGY_UNKNOWN: CookieStrategy = 0;
export const COOKIESTRATEGY_SERVER: CookieStrategy = 1;
export const COOKIESTRATEGY_CLIENT: CookieStrategy = 2;

export type RewriteElemInsStrategy = number;
export const REWRITEELEMINSSTRATEGY_UNKNOWN: RewriteElemInsStrategy = 0;
export const REWRITEELEMINSSTRATEGY_OFF: RewriteElemInsStrategy = 1;
export const REWRITEELEMINSSTRATEGY_ON: RewriteElemInsStrategy = 2;

export type RewriteAttrStrategy = number;
export const REWRITEATTRSTRATEGY_UNKNOWN: RewriteAttrStrategy = 0;
export const REWRITEATTRSTRATEGY_OFF: RewriteAttrStrategy = 1;
export const REWRITEATTRSTRATEGY_ON: RewriteAttrStrategy = 2;

export type SiteHeaderBidderDeliveryType = string;
export const HEADERBIDDERDELIVERYTYPE_UNKNOWN: SiteHeaderBidderDeliveryType = '0';
export const HEADERBIDDERDELIVERYTYPE_OTT: SiteHeaderBidderDeliveryType = '1';
export const HEADERBIDDERDELIVERYTYPE_WEBSITE: SiteHeaderBidderDeliveryType = '2';
export const HEADERBIDDERDELIVERYTYPE_APP: SiteHeaderBidderDeliveryType = '3';
export const HEADERBIDDERDELIVERYTYPE_NATIVE_VAST: SiteHeaderBidderDeliveryType = '4';
export const HeaderBidderDeliveryTypesMap: { [key: string]: string } = {
  CTV: HEADERBIDDERDELIVERYTYPE_OTT,
  App: HEADERBIDDERDELIVERYTYPE_APP,
  NativeVast: HEADERBIDDERDELIVERYTYPE_NATIVE_VAST,
  Web: HEADERBIDDERDELIVERYTYPE_WEBSITE,
};
export const HeaderBidderDeliveryTypesIndexMap: { [key: string]: string } = {
  [HEADERBIDDERDELIVERYTYPE_OTT]: 'CTV',
  [HEADERBIDDERDELIVERYTYPE_APP]: 'App',
  [HEADERBIDDERDELIVERYTYPE_WEBSITE]: 'Web',
  [HEADERBIDDERDELIVERYTYPE_NATIVE_VAST]: 'NativeVast',
};

export type ESuperSiteTargetingType = number;
export const SUPER_SITE_TARGETING_TYPE_CUSTOM_4: ESuperSiteTargetingType = 0;
export const SUPER_SITE_TARGETING_TYPE_CUSTOM_5: ESuperSiteTargetingType = 1;
export const SUPER_SITE_TARGETING_TYPE_CUSTOM_6: ESuperSiteTargetingType = 2;
export const SUPER_SITE_TARGETING_TYPE_CUSTOM_7: ESuperSiteTargetingType = 3;
export const SUPER_SITE_TARGETING_TYPE_CUSTOM_8: ESuperSiteTargetingType = 4;
export const SUPER_SITE_TARGETING_TYPE_CUSTOM_9: ESuperSiteTargetingType = 5;
export const SUPER_SITE_TARGETING_TYPE_CUSTOM_10: ESuperSiteTargetingType = 6;
export const SUPER_SITE_TARGETING_TYPE_CUSTOM_11: ESuperSiteTargetingType = 7;
export const SUPER_SITE_TARGETING_TYPE_CUSTOM_12: ESuperSiteTargetingType = 8;
export const SUPER_SITE_TARGETING_TYPE_CUSTOM_13: ESuperSiteTargetingType = 9;
export const SUPER_SITE_TARGETING_TYPE_CUSTOM_14: ESuperSiteTargetingType = 10;
export const SUPER_SITE_TARGETING_TYPE_CUSTOM_15: ESuperSiteTargetingType = 11;
export const SUPER_SITE_TARGETING_TYPE_CUSTOM_16: ESuperSiteTargetingType = 12;
export const SUPER_SITE_TARGETING_TYPE_CUSTOM_17: ESuperSiteTargetingType = 13;
export const SUPER_SITE_TARGETING_TYPE_CUSTOM_18: ESuperSiteTargetingType = 14;
export const SuperSiteTargetingTypes = [
  { id: SUPER_SITE_TARGETING_TYPE_CUSTOM_4, title: 'CUSTOM_4' },
  { id: SUPER_SITE_TARGETING_TYPE_CUSTOM_5, title: 'CUSTOM_5' },
  { id: SUPER_SITE_TARGETING_TYPE_CUSTOM_6, title: 'CUSTOM_6' },
  { id: SUPER_SITE_TARGETING_TYPE_CUSTOM_7, title: 'CUSTOM_7' },
  { id: SUPER_SITE_TARGETING_TYPE_CUSTOM_8, title: 'CUSTOM_8' },
  { id: SUPER_SITE_TARGETING_TYPE_CUSTOM_9, title: 'CUSTOM_9' },
  { id: SUPER_SITE_TARGETING_TYPE_CUSTOM_10, title: 'CUSTOM_10' },
  { id: SUPER_SITE_TARGETING_TYPE_CUSTOM_11, title: 'CUSTOM_11' },
  { id: SUPER_SITE_TARGETING_TYPE_CUSTOM_12, title: 'CUSTOM_12' },
  { id: SUPER_SITE_TARGETING_TYPE_CUSTOM_13, title: 'CUSTOM_13' },
  { id: SUPER_SITE_TARGETING_TYPE_CUSTOM_14, title: 'CUSTOM_14' },
  { id: SUPER_SITE_TARGETING_TYPE_CUSTOM_15, title: 'CUSTOM_15' },
  { id: SUPER_SITE_TARGETING_TYPE_CUSTOM_16, title: 'CUSTOM_16' },
  { id: SUPER_SITE_TARGETING_TYPE_CUSTOM_17, title: 'CUSTOM_17' },
  { id: SUPER_SITE_TARGETING_TYPE_CUSTOM_18, title: 'CUSTOM_18' },
];
export const SuperSiteTargetingNumToString: { [key: number]: string } = {
  0: 'Custom_4',
  1: 'Custom_5',
  2: 'Custom_6',
  3: 'Custom_7',
  4: 'Custom_8',
  5: 'Custom_9',
  6: 'Custom_10',
  7: 'Custom_11',
  8: 'Custom_12',
  9: 'Custom_13',
  10: 'Custom_14',
  11: 'Custom_15',
  12: 'Custom_16',
  13: 'Custom_17',
  14: 'Custom_18',
};

// Must be the same as:
// https://github.com/publica-project/platform/blob/master/src/ads/prebid/server/endpoints/usersync/providers/provider.go#L15
export enum ESite_UserSync {
  Site_User_Sync_UNKNOWN = '',
  Site_User_Sync_INDEX = 'index',
  Site_User_Sync_APPNEXUS = 'appnexus',
  Site_User_Sync_CADENT = 'cadent',
  Site_User_Sync_BEACHFRONT = 'beachfront',
  Site_User_Sync_FREEWHEEL = 'freewheel',
  Site_User_Sync_PUBMATIC = 'pubmatic',
  Site_User_Sync_SMARTADSERVER = 'smartadserver',
  Site_User_Sync_VERIZON = 'verizon',
  Site_User_Sync_RICHAUDIENCE = 'richaudience',
  Site_User_Sync_RUBICON = 'rubicon',
  Site_User_Sync_TEADS = 'teads',
  Site_User_Sync_TRIPLELIFT = 'triplelift',
  Site_User_Sync_THETRADEDESK = 'thetradedesk',
  Site_User_Sync_UNRULY = 'unruly',
}

export enum EFallbackAdsMode {
  FALLBACK_ADS_MODE_UNKNOWN = 0,
  FALLBACK_ADS_MODE_ADDITIONAL_ADS = 1,
  FALLBACK_ADS_MODE_DURATION_OF_POD = 2,
  FALLBACK_ADS_MODE_DURATION_OF_ADS = 3,
}

export interface ISite {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  Publisher: IPublisher;
  AppStoreURL: IDeviceAwareModel;
  AppStoreUrlOverrideIfEmpty: EBool;
  AppBundleOverride: { [key: string]: string };
  AppBundleOverrideIfEmpty: EBool;
  AppIDOverride: { [key: string]: string };
  AppIDOverrideIfEmpty: EBool;
  AppNameOverride: { [key: string]: string };
  AppNameOverrideIfEmpty: EBool;
  AppDomainOverride: { [key: string]: string };
  AppDomainOverrideIfEmpty: EBool;
  EnableHlsTranscoder: EBool;
  EndpointURLParams: string;
  RokuProviderChannelIDStr: string;
  HeaderBidders: IHeaderBidder[];
  HeaderBidderCode: string;
  HeaderBidderDeliveryType: SiteHeaderBidderDeliveryType;
  HeaderBidderSizes: string;
  HeaderBidderMediaTypes: string;
  HeaderBidderConfig: string;
  HeaderBidderPriceIncrements: IHeaderBidderPriceIncrement[];
  HeaderBidderEndpointTimeout: number;
  HeaderBidderMimeTypes: string[];
  HeaderBidderEnforceAdomain: boolean;
  HeaderBidderSingleAuction: boolean;
  HeaderBidderDedup: boolean;
  HeaderBidderDedupCategory: boolean;
  HeaderBidderCacheMedia: boolean;
  HeaderBidderDedupAdomain: boolean;
  HeaderBidderDedupAPSKey: boolean;
  HeaderBidderWinnerModel: ESite_HeaderBidderWinnerModel;
  HeaderBidderEnforceSlotTargeting: boolean;
  HeaderBidderVastVersion: number;
  HeaderBidderGlobalDedup: EBool;
  HeaderBidderGlobalDedupExpiration: number;
  BypassDedupByAdAsSeparation: EBool;
  BypassDedupByDurationAsSeparation: EBool;
  BypassDedupIfSurroundedByDedupedAds: EBool;
  BypassDedupAdCount: string;
  BypassDedupDuration: string;
  BypassMediafileDedup: EBool;
  BypassAdomainDedup: EBool;
  BypassIabCatDedup: EBool;
  HonorSequenceOrder: EBool;
  Name: string;
  SiteDomainOverride: { [key: string]: string };
  SiteDomainOverrideIfEmpty: EBool;
  SiteNameOverride: { [key: string]: string };
  SubscriptionType: Subscription;
  UUID: string;
  Active: string;
  Debug: string;
  Dirty: boolean;
  StartOnload: boolean;
  ScriptsOnload: boolean;
  DislayOnload: boolean;
  DoNotBlock: boolean;
  PagehrefWatcherType: number;
  PagehrefWatcherFunc: string;
  DynamicHostname: boolean;
  NoTiny: boolean;
  PathEncode: string;
  Region: string;
  DDMonitoring: boolean;
  VirusMonitoring: boolean;
  BundleType: number;
  JSClientVersion: string;
  DomainID: string;
  Token: string;
  ConsoleDisable: number;
  BlockIP: boolean;
  StringEncoder: number;
  DetectFairAdblocker: number;
  IFrameStyleEncode: number;
  FixedBootloader: boolean;
  AppOpts: number;
  IFrameSrcStrategy: number;
  RenameGoogleTag: boolean;
  CookieStrategy: CookieStrategy;
  RewriteElemIns: RewriteElemInsStrategy;
  FuncsOverride: number;
  RewriteAttr: RewriteAttrStrategy;
  IFrameLoadEmulation: number;
  CSSRewriteIgnore: string;
  DetectTrackingBlockers: number;
  ABList: Array<{ [key: string]: any }>;
  ABAttrs: Array<{ [key: string]: any }>;
  SaltOffset: number;
  UTCHour: number;
  NotifySiteAssocTime: string;
  DebugHeaderBidder: number;
  DebugHeaderBidderEnabledUntil: string;
  SamplingPercentageDisabledUntil: string;
  SamplingPercentageRequests: number;
  MaxBitrate: number;
  MinBitrate: number;
  BidAdjustmentFactor: number;
  BidAdjustmentValue: number;
  BidFloor: number;
  CurrencyDisplayed: ECurrencyType;
  MaxAuctionCap: number;
  PodFloor: string;
  RejectPodsWithAllHouseAds: boolean;
  RejectNonsecureVAST: EBool;
  EnableXMLCommentWrites: EBool;
  UserSyncProviders: string;
  FrequencyLimit: number;
  FrequencyLimitDuration: number;

  // Dedup
  AdIDDedup: EBool;
  CreativeIDDedup: EBool;

  CombineCreatives: EBool;
  CombineCreativesMax: number;
  CombineCreativesPriority: number;
  EnableLiveContext: EBool;
  LiveContextStreamID: string;
  LiveContextStreamURL: string;
  LiveContextLookbackMinutes: number;
  ClickThroughAdomainRecognitionEnabled: EBool;
  BrandRecognitionEnabled: EBool;
  BrandRecognitionLimit: number;
  BrandRecognitionLimitUnit: TimeUnit;
  MachineLearningForMaxAuctionsEnabled: EBool;
  WebShrinkerCategoryEnabled: EBool;
  ServerSideTrackersEnabled: EBool;
  LanguageDetectionEnabled: EBool;

  // Auction Split
  AuctionSplit: EBool;
  AuctionSplitTimeout: number;
  ChannelLabels: IChannelLabel[];

  PodDurationOverrideEnabled: EBool;
  PodDurationOverride: number;

  MinPodSize: number;
  DefaultWeight: number;

  AppCatOverride: string;
  AppCatOverrideIfEmpty: EBool;

  // Overrides
  ContentChannelOverride: string;
  ContentChannelOverrideIfEmpty: EBool;
  ContentNetworkOverride: string;
  ContentNetworkOverrideIfEmpty: EBool;
  ContentSeriesOverride: string;
  ContentSeriesOverrideIfEmpty: EBool;
  ContentTitleOverride: string;
  ContentTitleOverrideIfEmpty: EBool;
  ContentGenreOverride: string;
  ContentGenreOverrideIfEmpty: EBool;
  ContentDistribNameOverride: string;
  ContentDistribNameOverrideIfEmpty: EBool;
  ContentRatingOverride: string;
  ContentRatingOverrideIfEmpty: EBool;
  ContentProdqualOverride: string;
  ContentProdqualOverrideIfEmpty: EBool;
  ContentProducerNameOverride: string;
  ContentProducerNameOverrideIfEmpty: EBool;
  ContentProducerIDOverride: string;
  ContentProducerIDOverrideIfEmpty: EBool;
  ContentKeywordsOverride: string;
  ContentKeywordsOverrideIfEmpty: EBool;
  ContentLanguageOverride: string;
  ContentLanguageOverrideIfEmpty: EBool;
  ContentIDOverride: string;
  ContentIDOverrideIfEmpty: EBool;
  ContentSeasonOverride: string;
  ContentSeasonOverrideIfEmpty: EBool;
  RemoveContentFromEndpointRequest: EBool;
  RemoveContentChannelFromEndpointRequest: EBool;
  RemoveContentNetworkFromEndpointRequest: EBool;
  RemoveContentSeriesFromEndpointRequest: EBool;
  RemoveContentTitleFromEndpointRequest: EBool;
  RemoveContentGenreFromEndpointRequest: EBool;
  RemoveContentDistribNameFromEndpointRequest: EBool;
  RemoveContentRatingFromEndpointRequest: EBool;
  ContentCategoryOverride: string;
  ContentCategoryOverrideIfEmpty: EBool;
  RemoveContentCategoryFromEndpointRequest: EBool;
  RemoveContentProdqualFromEndpointRequest: EBool;
  RemoveContentProducerNameFromEndpointRequest: EBool;
  RemoveContentProducerIDFromEndpointRequest: EBool;
  RemoveContentKeywordsFromEndpointRequest: EBool;
  RemoveContentLanguageFromEndpointRequest: EBool;
  InventoryPartnerDomainOverride: string;
  InventoryPartnerDomainOverrideIfEmpty: EBool;
  RemoveContentIDFromEndpointRequest: EBool;
  RemoveContentSeasonFromEndpointRequest: EBool;
  // PostBid Overrides
  ContentDistribNamePostBidOverride: string;
  ContentDistribNamePostBidOverrideIfEmpty: EBool;
  ContentChannelPostBidOverride: string;
  ContentChannelPostBidOverrideIfEmpty: EBool;
  ContentNetworkPostBidOverride: string;
  ContentNetworkPostBidOverrideIfEmpty: EBool;
  ContentSeriesPostBidOverride: string;
  ContentSeriesPostBidOverrideIfEmpty: EBool;
  ContentTitlePostBidOverride: string;
  ContentTitlePostBidOverrideIfEmpty: EBool;
  ContentCategoryPostBidOverride: string;
  ContentCategoryPostBidOverrideIfEmpty: EBool;
  ContentGenrePostBidOverride: string;
  ContentGenrePostBidOverrideIfEmpty: EBool;
  ContentRatingPostBidOverride: string;
  ContentRatingPostBidOverrideIfEmpty: EBool;
  ContentProdqualPostBidOverride: string;
  ContentProdqualPostBidOverrideIfEmpty: EBool;
  ContentProducerNamePostBidOverride: string;
  ContentProducerNamePostBidOverrideIfEmpty: EBool;
  ContentProducerIDPostBidOverride: string;
  ContentProducerIDPostBidOverrideIfEmpty: EBool;
  ContentKeywordsPostBidOverride: string;
  ContentKeywordsPostBidOverrideIfEmpty: EBool;
  ContentLanguagePostBidOverride: string;
  ContentLanguagePostBidOverrideIfEmpty: EBool;
  // Device
  AnonymizeDeviceID: EBool;
  // User
  AnonymizeIP: EBool;
  // PMP
  EndpointDealIDPassthroughActive: EBool;
  // Video
  MinAdDurationOverride: number;
  MaxAdDurationOverride: number;
  StartDelayOverride: number;
  ClearDefaultBitrateLimits: EBool;
  Skip: EBool;
  SkipMin: number;
  SkipAfter: number;
  // Privacy
  SetCoppaToOne: EBool;

  SuperSiteID: number | undefined;
  SuperSiteTargeting: string;
  SuperSiteTargetingType: ESuperSiteTargetingType;
  SuperSiteTargetingConfig: ISuperSiteTargetingObject[];
  SuperSiteTargetingLogicOperator: ESuperSiteTargetingLogicOperator;
  SubSites: ISite[];

  abTests: IChannelABTestingMA[];
  PublicaVaultEnabled: EBool;
  PublicaVaultUseDefault: EBool;
  PublicaVaultExcludedBidderIDs: string;
  PublicaVaultExcludedDemandSources: string;
  BidSaverConfig: IBidSaverConfig;
  Route?: IInventoryRoute;
  EndpointRequestScore: IEndpointRequestScore;
  ChannelPerformanceSortByOptions: IChannelPerformanceSortByOptions;

  EnablePublicaVPAID: EBool;
  EnableFallbackAds: EBool;
  FallbackAdsAdditionalAds: number;
  FallBackAdsEnableDurationOfPod: EBool;
  FallbackAdsMode: EFallbackAdsMode;

  // Marketplace
  IsMarketplaceConnection: boolean;
  IsReceivingMarketplaceTraffic: boolean;

  // Measurement
  EnableIASFirstPartyVASTTracking: EBool;
  MeasurementPercentage: number;
  EnableDynamicMeasurementStrategy: EBool;

  // Managed Platform Connection
  ReturnGrossCPMInResponse: EBool;

  TotalCount: number;

  // Schain Fields
  EnableAddSchainToBidRequest: EBool;
  SchainASI: string;
  SchainSID: string;
  SchainName: string;
  SchainDomain: string;
  SchainComplete: number;

  // Conext API Call Enabling
  ComscoreSignalEnabled: number;

  // Deal Floor Overwrite
  AllowBidsBelowDealFloor: number;
  AllowBidsBelowDealFloorValue: number;

  SitesCronSummary: IChannelCronSummary;
  EnableSecondaryRequests: EBool;
  EnableSecondaryRequestPercentage: number;
  EnableSecondaryRequestTimeout: number;

  BypassDedupConfig: IBypassDedupConfig[];
  EnforceAuctionCutoff: EBool;

  // Creative Quality Assurance
  EnableCreativeQualityAssurance: EBool;
  CreativeQualityAssuranceAudioBitrate: string;
  CreativeQualityAssuranceCodec: string;
  CreativeQualityAssuranceFrameRate: string;
  CreativeQualityAssuranceSamplingFrequency: string;
  CreativeQualityAssuranceVideoBitrate: string;
  CreativeQualityAssuranceVideoFormat: string;
  CreativeQualityAssuranceVideoResolution: string;
  SitePriceExtension: SitePriceExtensionType;

  // EPG Contextual Data
  EnableEPGMetadata: EBool;
  EnableEPGContentDuration: EBool;
  EnableEPGContentGenre: EBool;
  EnableEPGContentLanguage: EBool;
  EnableEPGContentRating: EBool;
  DisableActivationForNonSuperUser: string;
}

export const transformSite = transformModel<ISite>({
  ID: parseNumber(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: parseString(['DeletedAt']),

  Publisher: parseObject(['Publisher', 'publisher'], transformPublisherWrapper),
  Distribution: parseObject(['Distribution', 'distribution'], transformDistribution),
  AdUnits: parseArray(['AdUnits', 'adunits'], transformAdunit),
  AppStoreURL: parseObject(['AppStoreURL', 'appstoreurl'], transformDeviceAwareModel),
  AppStoreUrlOverrideIfEmpty: parseNumber(
    ['AppStoreURLOverrideIfEmpty', 'AppStoreUrlOverrideIfEmpty', 'appstoreurloverrideifempty'],
    EBool.EBool_TRUE,
  ),
  AppBundleOverride: parseJSON(['AppBundleOverride', 'appbundleoverride']),
  AppBundleOverrideIfEmpty: parseNumber(['AppBundleOverrideIfEmpty'], EBool.EBool_FALSE),
  AppIDOverride: parseJSON(['AppIDOverride', 'appidoverride']),
  AppIDOverrideIfEmpty: parseNumber(['AppIDOverrideIfEmpty'], EBool.EBool_FALSE),
  AppNameOverride: parseJSON(['AppNameOverride', 'appnameoverride']),
  AppNameOverrideIfEmpty: parseNumber(['AppNameOverrideIfEmpty'], EBool.EBool_FALSE),
  AppDomainOverride: parseJSON(['AppDomainOverride', 'appdomainoverride']),
  AppDomainOverrideIfEmpty: parseNumber(['AppDomainOverrideIfEmpty'], EBool.EBool_FALSE),
  SiteDomainOverride: parseJSON(['SiteDomainOverride', 'sitedomainoverride']),
  SiteDomainOverrideIfEmpty: parseNumber(['SiteDomainOverrideIfEmpty'], EBool.EBool_FALSE),
  SiteNameOverride: parseJSON(['SiteNameOverride', 'sitenameoverride']),
  EnableHlsTranscoder: parseNumber(
    ['EnableHlsTranscoder', 'enablehlstranscoder'],
    EBool.EBool_UNKNOWN,
  ),
  EndpointURLParams: parseString(['EndpointURLParams', 'endpointurlparams']),
  RokuProviderChannelIDStr: parseString(['RokuProviderChannelIDStr', 'rokuproviderchannelidstr']),
  HeaderBidders: parseArray(
    ['HeaderBidders', 'headerbidders'],
    fromAPIHeaderBidderWrapper,
    sortHeaderBidders,
  ),
  HeaderBidderCode: parseString(['HeaderBidderCode', 'headerbiddercode']),
  HeaderBidderDeliveryType: parseString(['HeaderBidderDeliveryType', 'headerbidderdeliverytype']),
  HeaderBidderSizes: parseString(['HeaderBidderSizes', 'headerbiddersizes']),
  HeaderBidderMediaTypes: parseString(['HeaderBidderMediaTypes', 'headerbiddermediatypes']),
  HeaderBidderConfig: parseString(['HeaderBidderConfig', 'headerbidderconfig']),
  HeaderBidderPriceIncrements: toArray(
    ['HeaderBidderPriceIncrements', 'headerbidderpriceincrements'],
    transformHeaderBidderPriceIncrement,
  ),
  HeaderBidderEndpointTimeout: parseNumber(
    ['HeaderBidderEndpointTimeout', 'headerbidderendpointtimeout'],
    0,
  ),
  HeaderBidderMimeTypes: parseString(['HeaderBidderMimeTypes'], ','),
  HeaderBidderVastVersion: parseFloat(['HeaderBidderVastVersion'], 2),
  HeaderBidderGlobalDedup: parseNumber(
    ['HeaderBidderGlobalDedup', 'header_bidder_global_dedup'],
    EBool.EBool_UNKNOWN,
  ),
  HeaderBidderGlobalDedupExpiration: parseNumber(
    ['HeaderBidderGlobalDedupExpiration', 'header_bidder_global_dedup_expiration'],
    0,
  ),
  HeaderBidderSingleAuction: parseBoolTrue(['HeaderBidderSingleAuction']),
  HeaderBidderEnforceAdomain: parseBoolTrue(['HeaderBidderEnforceAdomain']),
  HeaderBidderDedup: parseBoolTrue(['HeaderBidderDedup']),
  HeaderBidderDedupCategory: parseBoolTrue(['HeaderBidderDedupCategory']),
  HeaderBidderCacheMedia: parseBoolTrue(['HeaderBidderCacheMedia']),
  HeaderBidderDedupAdomain: parseBoolTrue(['HeaderBidderDedupAdomain']),
  HeaderBidderDedupAPSKey: parseBoolTrue(['HeaderBidderDedupAPSKey']),
  HeaderBidderWinnerModel: parseNumber(['HeaderBidderWinnerModel']),
  HeaderBidderEnforceSlotTargeting: parseBoolTrue(['HeaderBidderEnforceSlotTargeting']),
  BypassDedupByAdAsSeparation: parseNumber(['BypassDedupByAdAsSeparation']),
  BypassDedupByDurationAsSeparation: parseNumber(['BypassDedupByDurationAsSeparation']),
  BypassDedupIfSurroundedByDedupedAds: parseNumber(['BypassDedupIfSurroundedByDedupedAds']),
  BypassDedupAdCount: parseString(['BypassDedupAdCount']),
  BypassDedupDuration: parseString(['BypassDedupDuration']),
  BypassMediafileDedup: parseNumber(['BypassMediafileDedup'], 0),
  BypassAdomainDedup: parseNumber(['BypassAdomainDedup'], 0),
  BypassIabCatDedup: parseNumber(['BypassIabCatDedup'], 0),
  HonorSequenceOrder: parseNumber(['HonorSequenceOrder'], 0),
  Name: parseString(['Name', 'name']),
  SubscriptionType: parseNumber(['SubscriptionType', 'subscriptiontype'], 0),
  UUID: parseString(['UUID', 'uuid']),
  Active: parseString(['Active', 'active']),
  Debug: parseString(['Debug', 'debug']),
  Dirty: parseBoolTrue(['Dirty', 'dirty']),
  StartOnload: parseBoolTrue(['StartOnload', 'startonload']),
  ScriptsOnload: parseBoolTrue(['ScriptsOnload', 'scriptsonload']),
  DislayOnload: parseBoolTrue(['DislayOnload', 'dislayonload']),
  DoNotBlock: parseBoolTrue(['DoNotBlock', 'donotblock']),
  PagehrefWatcherType: parseNumber(['PagehrefWatcherType', 'pagehrefwatchertype'], 0),
  PagehrefWatcherFunc: parseString(['PagehrefWatcherFunc', 'pagehrefwatcherfunc']),
  DynamicHostname: parseBoolTrue(['DynamicHostname', 'dynamichostname']),
  NoTiny: parseBoolTrue(['NoTiny', 'notiny']),
  PathEncode: parseString(['PathEncode', 'pathencode']),
  Region: parseString(['Region', 'region']),
  DDMonitoring: parseBoolTrue(['DDMonitoring', 'ddmonitoring']),
  VirusMonitoring: parseBoolTrue(['VirusMonitoring', 'virusmonitoring']),
  BundleType: parseNumber(['BundleType', 'bundletype'], 0),
  JSClientVersion: parseString(['JSClientVersion', 'jsclientversion']),
  DomainID: parseNumber(['DomainID', 'domainid'], 0),
  Token: parseString(['Token', 'token']),
  ConsoleDisable: parseNumber(['ConsoleDisable', 'consoledisable'], 0),
  BlockIP: parseBoolTrue(['BlockIP', 'blockip']),
  StringEncoder: parseNumber(['StringEncoder', 'stringencoder'], 0),
  DetectFairAdblocker: parseNumber(['DetectFairAdblocker', 'detectfairadblocker'], 0),
  IFrameStyleEncode: parseNumber(['IFrameStyleEncode', 'iframestyleencode'], 0),
  FixedBootloader: parseBoolTrue(['FixedBootloader', 'fixedbootloader']),
  AppOpts: parseNumber(['AppOpts', 'appopts'], 0),
  IFrameSrcStrategy: parseNumber(['IFrameSrcStrategy', 'iframesrcstrategy'], 0),
  RenameGoogleTag: parseBoolTrue(['RenameGoogleTag', 'renamegoogletag']),
  CookieStrategy: parseNumber(['CookieStrategy', 'cookiestrategy'], 0),
  RewriteElemIns: parseNumber(['RewriteElemIns', 'rewriteelemins'], 0),
  FuncsOverride: parseNumber(['FuncsOverride', 'funcsoverride'], 0),
  RewriteAttr: parseNumber(['RewriteAttr', 'rewriteattr'], 0),
  IFrameLoadEmulation: parseNumber(['IFrameLoadEmulation', 'iframeloademulation'], 0),
  CSSRewriteIgnore: parseString(['CSSRewriteIgnore', 'cssrewriteignore']),
  DetectTrackingBlockers: parseNumber(['DetectTrackingBlockers', 'detecttrackingblockers'], 0),
  ABList: parseString(['ABList', 'ablist'], transformJSON),
  ABAttrs: parseString(['ABAttrs', 'abattrs'], transformJSON),
  SaltOffset: parseNumber(['SaltOffset', 'saltoffset'], 0),
  UTCHour: parseNumber(['UTCHour', 'utchour'], 0),
  NotifySiteAssocTime: parseString(['NotifySiteAssocTime', 'notifysiteassoctime']),
  DebugHeaderBidder: parseNumber(['DebugHeaderBidder', 'debugheaderbidder'], 0),
  DebugHeaderBidderEnabledUntil: parseString(['DebugHeaderBidderEnabledUntil']),
  SamplingPercentageDisabledUntil: parseString(['SamplingPercentageDisabledUntil']),
  SamplingPercentageRequests: parseFloat(['SamplingPercentageRequests']),
  MaxBitrate: parseNumber(['MaxBitrate', 'max_bitrate'], 0),
  MinBitrate: parseNumber(['MinBitrate', 'min_bitrate'], 0),
  BidAdjustmentFactor: parseFloat(['BidAdjustmentFactor', 'bidadjustmentfactor'], 0),
  BidAdjustmentValue: parseFloat(['BidAdjustmentValue', 'bidadjustmentvalue']),
  BidFloor: parseFloat(['BidFloor', 'bidfloor'], 0),
  CurrencyDisplayed: parseNumber(['CurrencyDisplayed', 'currency_displayed']),
  MaxAuctionCap: parseFloat(['MaxAuctionCap', 'maxauctioncap'], 0),
  PodFloor: parseString(['PodFloor', 'podfloor'], '0'),
  RejectPodsWithAllHouseAds: parseBoolTrue(['RejectPodsWithAllHouseAds']),
  RejectNonsecureVAST: parseNumber(['RejectNonsecureVAST'], EBool.EBool_FALSE),
  EnableXMLCommentWrites: parseNumber(['EnableXMLCommentWrites'], EBool.EBool_FALSE),
  UserSyncProviders: parseString(['UserSyncProviders', 'user_sync_providers']),
  FrequencyLimit: parseNumber(['FrequencyLimit', 'frequencylimit'], 0),
  FrequencyLimitDuration: parseNumber(['FrequencyLimitDuration', 'frequencylimitduration'], 60),

  // Dedup
  AdIDDedup: parseNumber(['AdIDDedup'], EBool.EBool_UNKNOWN),
  CreativeIDDedup: parseNumber(['CreativeIDDedup'], EBool.EBool_UNKNOWN),

  CombineCreatives: parseNumber(['CombineCreatives'], EBool.EBool_UNKNOWN),
  CombineCreativesMax: parseNumber(['CombineCreativesMax'], 2),
  CombineCreativesPriority: parseNumber(['CombineCreativesPriority'], 1),
  EnableLiveContext: parseNumber(['EnableLiveContext'], EBool.EBool_UNKNOWN),
  LiveContextStreamID: parseString(['LiveContextStreamID']),
  LiveContextStreamURL: parseString(['LiveContextStreamURL']),
  LiveContextLookbackMinutes: parseNumber(['LiveContextLookbackMinutes'], 2),
  ClickThroughAdomainRecognitionEnabled: parseNumber(
    ['ClickThroughAdomainRecognitionEnabled'],
    EBool.EBool_UNKNOWN,
  ),
  BrandRecognitionEnabled: parseNumber(['BrandRecognitionEnabled'], EBool.EBool_UNKNOWN),
  BrandRecognitionLimit: parseNumber(['BrandRecognitionLimit'], 0),
  BrandRecognitionLimitUnit: parseNumber(['BrandRecognitionLimitUnit'], TimeUnit.TimeUnit_UNKNOWN),
  MachineLearningForMaxAuctionsEnabled: parseNumber(
    ['MachineLearningForMaxAuctionsEnabled'],
    EBool.EBool_UNKNOWN,
  ),
  WebShrinkerCategoryEnabled: parseNumber(['WebShrinkerCategoryEnabled'], EBool.EBool_UNKNOWN),
  ServerSideTrackersEnabled: parseNumber(['ServerSideTrackersEnabled'], EBool.EBool_UNKNOWN),
  LanguageDetectionEnabled: parseNumber(['LanguageDetectionEnabled'], EBool.EBool_UNKNOWN),
  AuctionSplit: parseNumber(['AuctionSplit', 'auction_split'], EBool.EBool_UNKNOWN),
  AuctionSplitTimeout: parseNumber(['AuctionSplitTimeout', 'auction_split_timeout'], 0),
  ChannelLabels: parseArray(['ChannelLabels', 'channellabels'], transformChannelLabelWrapper),

  PodDurationOverrideEnabled: parseNumber(['PodDurationOverrideEnabled'], EBool.EBool_UNKNOWN),
  PodDurationOverride: parseNumber(['PodDurationOverride']),

  MinPodSize: parseNumber(['MinPodSize', 'min_pod_size'], 1),
  DefaultWeight: parseNumber('DefaultWeight'),

  AppCatOverride: parseString(['AppCatOverride']),
  AppCatOverrideIfEmpty: parseNumber(['AppCatOverrideIfEmpty'], EBool.EBool_FALSE),

  // Overrides
  // Content
  ContentChannelOverride: parseString(['ContentChannelOverride']),
  ContentChannelOverrideIfEmpty: parseNumber(['ContentChannelOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentNetworkOverride: parseString(['ContentNetworkOverride']),
  ContentNetworkOverrideIfEmpty: parseNumber(['ContentNetworkOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentSeriesOverride: parseString(['ContentSeriesOverride']),
  ContentSeriesOverrideIfEmpty: parseNumber(['ContentSeriesOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentTitleOverride: parseString(['ContentTitleOverride']),
  ContentTitleOverrideIfEmpty: parseNumber(['ContentTitleOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentGenreOverride: parseString(['ContentGenreOverride']),
  ContentGenreOverrideIfEmpty: parseNumber(['ContentGenreOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentDistribNameOverride: parseString(['ContentDistribNameOverride']),
  ContentDistribNameOverrideIfEmpty: parseNumber(
    ['ContentDistribNameOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentRatingOverride: parseString(['ContentRatingOverride']),
  ContentRatingOverrideIfEmpty: parseNumber(['ContentRatingOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentProdqualOverride: parseString(['ContentProdqualOverride']),
  ContentProdqualOverrideIfEmpty: parseNumber(
    ['ContentProdqualOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentProducerNameOverride: parseString(['ContentProducerNameOverride']),
  ContentProducerNameOverrideIfEmpty: parseNumber(
    ['ContentProducerNameOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentProducerIDOverride: parseString(['ContentProducerIDOverride']),
  ContentProducerIDOverrideIfEmpty: parseNumber(
    ['ContentProducerIDOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentKeywordsOverride: parseString(['ContentKeywordsOverride']),
  ContentKeywordsOverrideIfEmpty: parseNumber(
    ['ContentKeywordsOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentLanguageOverride: parseString(['ContentLanguageOverride']),
  ContentLanguageOverrideIfEmpty: parseNumber(
    ['ContentLanguageOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentIDOverride: parseString(['ContentIDOverride']),
  ContentIDOverrideIfEmpty: parseNumber(['ContentIDOverrideIfEmpty'], EBool.EBool_FALSE),
  ContentSeasonOverride: parseString(['ContentSeasonOverride']),
  ContentSeasonOverrideIfEmpty: parseNumber(['ContentSeasonOverrideIfEmpty'], EBool.EBool_FALSE),
  RemoveContentFromEndpointRequest: parseNumber(
    ['RemoveContentFromEndpointRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentChannelFromEndpointRequest: parseNumber(
    ['RemoveContentChannelFromEndpointRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentNetworkFromEndpointRequest: parseNumber(
    ['RemoveContentNetworkFromEndpointRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentSeriesFromEndpointRequest: parseNumber(
    ['RemoveContentSeriesFromEndpointRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentTitleFromEndpointRequest: parseNumber(
    ['RemoveContentTitleFromEndpointRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentGenreFromEndpointRequest: parseNumber(
    ['RemoveContentGenreFromEndpointRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentDistribNameFromEndpointRequest: parseNumber(
    ['RemoveContentDistribNameFromEndpointRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentRatingFromEndpointRequest: parseNumber(
    ['RemoveContentRatingFromEndpointRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentProdqualFromEndpointRequest: parseNumber(
    ['RemoveContentProdqualFromEndpointRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentProducerNameFromEndpointRequest: parseNumber(
    ['RemoveContentProducerNameFromEndpointRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentProducerIDFromEndpointRequest: parseNumber(
    ['RemoveContentProducerIDFromEndpointRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentKeywordsFromEndpointRequest: parseNumber(
    ['RemoveContentKeywordsFromEndpointRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentLanguageFromEndpointRequest: parseNumber(
    ['RemoveContentLanguageFromEndpointRequest'],
    EBool.EBool_FALSE,
  ),
  ContentCategoryOverride: parseString(['ContentCategoryOverride']),
  ContentCategoryOverrideIfEmpty: parseNumber(
    ['ContentCategoryOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  RemoveContentCategoryFromEndpointRequest: parseNumber(
    ['RemoveContentCategoryFromEndpointRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentIDFromEndpointRequest: parseNumber(
    ['RemoveContentIDFromEndpointRequest'],
    EBool.EBool_FALSE,
  ),
  RemoveContentSeasonFromEndpointRequest: parseNumber(
    ['RemoveContentSeasonFromEndpointRequest'],
    EBool.EBool_FALSE,
  ),
  AnonymizeDeviceID: parseNumber(['AnonymizeDeviceID'], EBool.EBool_FALSE),
  AnonymizeIP: parseNumber(['AnonymizeIP'], EBool.EBool_UNKNOWN),
  EndpointDealIDPassthroughActive: parseNumber(
    ['EndpointDealIDPassthroughActive'],
    EBool.EBool_FALSE,
  ),
  MinAdDurationOverride: parseNumber(['MinAdDurationOverride', 'minaddurationoverride']),
  MaxAdDurationOverride: parseNumber(['MaxAdDurationOverride', 'maxaddurationoverride']),
  StartDelayOverride: parseNumber(['StartDelayOverride']),
  ClearDefaultBitrateLimits: parseNumber(['ClearDefaultBitrateLimits'], EBool.EBool_FALSE),
  Skip: parseNumber(['Skip'], EBool.EBool_FALSE),
  SkipMin: parseNumber(['SkipMin'], 0),
  SkipAfter: parseNumber(['SkipAfter'], 0),
  SuperSiteID: parseNumber(['SuperSiteID'], undefined),
  SuperSiteTargeting: parseString(['SuperSiteTargeting']),
  SuperSiteTargetingType: parseNumber(['SuperSiteTargetingType'], 0),
  SuperSiteTargetingConfig: parseObjectV2(['SuperSiteTargetingConfig']),
  SuperSiteTargetingLogicOperator: parseNumber(['SuperSiteTargetingLogicOperator'], 0),
  SubSites: parseArray(['SubSites'], transformSiteWrapper),
  SetCoppaToOne: parseNumber(['SetCoppaToOne'], EBool.EBool_FALSE),
  abTests: parseArray(['abTests', 'ab_tests'], (fromObj: any) => {
    return transformChannelABTesting(fromObj);
  }),
  PublicaVaultEnabled: parseNumber(['PublicaVaultEnabled'], EBool.EBool_FALSE),
  PublicaVaultUseDefault: parseNumber(['PublicaVaultUseDefault'], EBool.EBool_FALSE),
  PublicaVaultExcludedDemandSources: parseString(['PublicaVaultExcludedDemandSources']),
  PublicaVaultExcludedBidderIDs: parseString(['PublicaVaultExcludedBidderIDs']),
  BidSaverConfig: parseObject(['PublicaVaultConfig'], transformBidSaverConfig),
  Route: parseObject(['Route'], transformInventoryRoute),
  EndpointRequestScore: parseObject(['EndpointRequestScore'], transformEndpointRequestScore),
  EnablePublicaVPAID: parseNumber(['EnablePublicaVPAID'], EBool.EBool_FALSE),
  EnableFallbackAds: parseNumber(['EnableFallbackAds'], EBool.EBool_FALSE),
  FallbackAdsAdditionalAds: parseNumber(['FallbackAdsAdditionalAds'], 0),
  FallbackAdsMode: parseNumber(['FallbackAdsMode'], 0),
  FallBackAdsEnableDurationOfPod: parseNumber(
    ['FallBackAdsEnableDurationOfPod'],
    EBool.EBool_FALSE,
  ),
  ChannelPerformanceSortByOptions: parseObject(
    ['ChannelPerformanceSortByOptions'],
    transformChannelPerformanceSortByOptions,
  ),
  IsMarketplaceConnection: parseBoolFalse(['IsMarketplaceConnection']),
  IsReceivingMarketplaceTraffic: parseBoolFalse(['IsReceivingMarketplaceTraffic']),
  EnableIASFirstPartyVASTTracking: parseNumber(
    ['EnableIASFirstPartyVASTTracking'],
    EBool.EBool_FALSE,
  ),
  MeasurementPercentage: parseFloat(['MeasurementPercentage', 'measurementpercentage'], 0),
  EnableDynamicMeasurementStrategy: parseNumber(
    ['EnableDynamicMeasurementStrategy'],
    EBool.EBool_FALSE,
  ),
  ReturnGrossCPMInResponse: parseNumber(['ReturnGrossCPMInResponse'], EBool.EBool_FALSE),
  TotalCount: parseNumber(['TotalCount']),

  // PostBid Overrides
  // Content
  ContentDistribNamePostBidOverride: parseString(['ContentDistribNamePostBidOverride']),
  ContentDistribNamePostBidOverrideIfEmpty: parseNumber(
    ['ContentDistribNamePostBidOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentChannelPostBidOverride: parseString(['ContentChannelPostBidOverride']),
  ContentChannelPostBidOverrideIfEmpty: parseNumber(
    ['ContentChannelPostBidOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentNetworkPostBidOverride: parseString(['ContentNetworkPostBidOverride']),
  ContentNetworkPostBidOverrideIfEmpty: parseNumber(
    ['ContentNetworkPostBidOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentSeriesPostBidOverride: parseString(['ContentSeriesPostBidOverride']),
  ContentSeriesPostBidOverrideIfEmpty: parseNumber(
    ['ContentSeriesPostBidOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentTitlePostBidOverride: parseString(['ContentTitlePostBidOverride']),
  ContentTitlePostBidOverrideIfEmpty: parseNumber(
    ['ContentTitlePostBidOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentCategoryPostBidOverride: parseString(['ContentCategoryPostBidOverride']),
  ContentCategoryPostBidOverrideIfEmpty: parseNumber(
    ['ContentCategoryPostBidOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentGenrePostBidOverride: parseString(['ContentGenrePostBidOverride']),
  ContentGenrePostBidOverrideIfEmpty: parseNumber(
    ['ContentGenrePostBidOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentRatingPostBidOverride: parseString(['ContentRatingPostBidOverride']),
  ContentRatingPostBidOverrideIfEmpty: parseNumber(
    ['ContentRatingPostBidOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentProdqualPostBidOverride: parseString(['ContentProdqualPostBidOverride']),
  ContentProdqualPostBidOverrideIfEmpty: parseNumber(
    ['ContentProdqualPostBidOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentProducerNamePostBidOverride: parseString(['ContentProducerNamePostBidOverride']),
  ContentProducerNamePostBidOverrideIfEmpty: parseNumber(
    ['ContentProducerNamePostBidOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentProducerIDPostBidOverride: parseString(['ContentProducerIDPostBidOverride']),
  ContentProducerIDPostBidOverrideIfEmpty: parseNumber(
    ['ContentProducerIDPostBidOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentKeywordsPostBidOverride: parseString(['ContentKeywordsPostBidOverride']),
  ContentKeywordsPostBidOverrideIfEmpty: parseNumber(
    ['ContentKeywordsPostBidOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  ContentLanguagePostBidOverride: parseString(['ContentLanguagePostBidOverride']),
  ContentLanguagePostBidOverrideIfEmpty: parseNumber(
    ['ContentLanguagePostBidOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  InventoryPartnerDomainOverride: parseString([
    'InventoryPartnerDomainOverride',
    'inventorypartnerdomainoverride',
  ]),
  InventoryPartnerDomainOverrideIfEmpty: parseNumber(
    ['InventoryPartnerDomainOverrideIfEmpty'],
    EBool.EBool_FALSE,
  ),
  EnableAddSchainToBidRequest: parseNumber(['EnableAddSchainToBidRequest'], EBool.EBool_FALSE),
  SchainASI: parseString(['SchainASI']),
  SchainSID: parseString(['SchainSID']),
  SchainName: parseString(['SchainName']),
  SchainDomain: parseString(['SchainDomain']),
  SchainComplete: parseNumber(['SchainComplete']),
  ComscoreSignalEnabled: parseNumber(['ComscoreSignalEnabled']),
  AllowBidsBelowDealFloor: parseNumber(['AllowBidsBelowDealFloor']),
  AllowBidsBelowDealFloorValue: parseFloat(['AllowBidsBelowDealFloorValue']),
  SitesCronSummary: parseObject(['SitesCronSummary'], transformChannelCronSummary),
  EnableRemoveGPPFromBidRequest: parseNumber(['EnableRemoveGPPFromBidRequest']),
  EnableSecondaryRequests: parseNumber(['EnableSecondaryRequests']),
  EnableSecondaryRequestPercentage: parseNumber(['EnableSecondaryRequestPercentage']),
  EnableSecondaryRequestTimeout: parseNumber(['EnableSecondaryRequestTimeout']),
  BypassDedupConfig: toArray(
    ['BypassDedupConfig', 'BypassDeduplicationConfigs'],
    transformHeaderBypassDedupConfig,
  ),
  EnforceAuctionCutoff: parseNumber(['EnforceAuctionCutoff'], EBool.EBool_UNKNOWN),
  // Creative Quality Assurance
  EnableCreativeQualityAssurance: parseNumber(
    ['EnableCreativeQualityAssurance'],
    EBool.EBool_FALSE,
  ),
  CreativeQualityAssuranceAudioBitrate: parseString(['CreativeQualityAssuranceAudioBitrate']),
  CreativeQualityAssuranceCodec: parseString(['CreativeQualityAssuranceCodec']),
  CreativeQualityAssuranceFrameRate: parseString(['CreativeQualityAssuranceFrameRate']),
  CreativeQualityAssuranceSamplingFrequency: parseString([
    'CreativeQualityAssuranceSamplingFrequency',
  ]),
  CreativeQualityAssuranceVideoBitrate: parseString(['CreativeQualityAssuranceVideoBitrate']),
  CreativeQualityAssuranceVideoFormat: parseString(['CreativeQualityAssuranceVideoFormat']),
  CreativeQualityAssuranceVideoResolution: parseString(['CreativeQualityAssuranceVideoResolution']),
  SitePriceExtension: parseNumber(['SitePriceExtension']),

  // EPG Contextual Data
  EnableEPGMetadata: parseNumber(['EnableEPGMetadata'], EBool.EBool_FALSE),
  EnableEPGContentDuration: parseNumber(['EnableEPGContentDuration'], EBool.EBool_FALSE),
  EnableEPGContentGenre: parseNumber(['EnableEPGContentGenre'], EBool.EBool_FALSE),
  EnableEPGContentLanguage: parseNumber(['EnableEPGContentLanguage'], EBool.EBool_FALSE),
  EnableEPGContentRating: parseNumber(['EnableEPGContentRating'], EBool.EBool_FALSE),
  DisableActivationForNonSuperUser: ['DisableActivationForNonSuperUser'],
});

export type SiteActive = string;
export const SiteActiveInactive: SiteActive = '1';
export const SiteActiveActive: SiteActive = '2';

export type AllowBidsBelowDealFloorType = number;
export const AllowBidsBelowDealFloor_DO_NOT_ALLOW: AllowBidsBelowDealFloorType = 0;
export const AllowBidsBelowDealFloor_BY_VALUE: AllowBidsBelowDealFloorType = 1;
export const AllowBidsBelowDealFloor_BY_PERCENT: AllowBidsBelowDealFloorType = 2;

export type SitePriceExtensionType = number;
export const SitePriceExtension_unknown = 0;
export const SitePriceExtension_false = 1;
export const SitePriceExtension_true = 2;

export function allowBidsBelowDealFloorTypeToString(source_type: number) {
  switch (source_type) {
    case AllowBidsBelowDealFloor_DO_NOT_ALLOW:
      return 'Value';
    case AllowBidsBelowDealFloor_BY_VALUE:
      return 'Value';
    case AllowBidsBelowDealFloor_BY_PERCENT:
      return 'Percent';
    default:
      break;
  }
  return 'Value';
}

export function transformSiteWrapper(fromObj: any): ISite {
  return transformSite(fromObj);
}

export function transformBidderWrapper(fromObj: any): IHeaderBidder {
  return fromAPIHeaderBidder(fromObj);
}

export const ReportInactive = 'false';
export const ReportActive = 'true';

export type ReportType = string;
export const REPORTTYPE_UNKNOWN: ReportType = '0';
export const REPORTTYPE_HEADERBIDDER_BYAUCTIONS: ReportType = '2';
export const REPORTTYPE_HEADERBIDDER_BYBUYERS: ReportType = '3';
export const REPORTTYPE_HEADERBIDDER_SSP: ReportType = '4';
export const REPORTTYPE_HEADERBIDDER_CAMPAIGN: ReportType = '5';
export const REPORTTYPE_HEADERBIDDER_AUDIENCE_SEGMENTS: ReportType = '6';
export const REPORTTYPE_STREAM: ReportType = '7';
export const REPORTTYPE_STREAM_USER_ACTIVITY: ReportType = '45';
export const REPORTTYPE_HEADERBIDDER_UNIQUE_USER_REPORT: ReportType = '8';
export const REPORTTYPE_HEADERBIDDER_AUDIENCE_SEGMENTS_SUPER: ReportType = '9';
export const REPORTTYPE_HEADERBIDDER_BYAUCTIONS_FILLRATE: ReportType = '10';
export const REPORTTYPE_HEADERBIDDER_BRAND_SAFETY_TRACKER: ReportType = '13';
export const REPORTTYPE_HEADERBIDDER_DEALS: ReportType = '14';
export const REPORTTYPE_HEADERBIDDER_PUBLICA_DEALS: ReportType = '15';
export const REPORTTYPE_HEADERBIDDER_INVENTORY_ROUTES: ReportType = '17';
export const REPORTTYPE_HEADERBIDDER_DESTINATIONS: ReportType = '18';
export const REPORTTYPE_HEADERBIDDER_CONTEXTUAL_SEGMENTS: ReportType = '19';
export const REPORTTYPE_IAS_THIRD_PARTY: ReportType = '20';
export const REPORTTYPE_IAS_FIRST_PARTY: ReportType = '21';
export const REPORTTYPE_CREATIVE_VALIDATION: ReportType = '22';
export const REPORTTYPE_HEADERBIDDER_ELEA_AI_SCAN_ALL: ReportType = '23';
export const REPORTTYPE_HEADERBIDDER_ELEA_AI_ENRICHMENT: ReportType = '24';
export const REPORTTYPE_IAS_PERFORMANCE: ReportType = '25';
export const REPORTTYPE_IAS_DEVICE: ReportType = '26';
export const REPORTTYPE_IAS_YOUTUBE: ReportType = '27';
export const REPORTTYPE_TTD: ReportType = '28';
export const REPORTTYPE_CREATIVE_REVIEW: ReportType = '29';
export const REPORTTYPE_SHARE_OF_VOICE: ReportType = '30';
export const REPORTTYPE_POD_HOURLY: ReportType = '31';
export const REPORTTYPE_ADVERTISER_RULES: ReportType = '32';
export const REPORTTYPE_BUYERS_APS: ReportType = '33';
export const REPORTTYPE_THIRD_PARTY_TRACKER_DOMAINS: ReportType = '34';
export const REPORTTYPE_ELEA_AI_ENRICHMENT_BILLING: ReportType = '35';
export const REPORTTYPE_HEADERBIDDER_ELEA_AI_LANGUAGE_DETECTION: ReportType = '36';
export const REPORTTYPE_BID_FLOOR_SETTINGS_REPORT: ReportType = '37';
export const REPORTTYPE_BID_SAVER_TRUE_LIFT: ReportType = '38';
export const REPORTTYPE_IAS_CONTENT_LEVEL_PERFORMANCE: ReportType = '52';

export interface IReport {
  ID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;
  UpdatedBy: string;
  StartDate: string;
  EndDate: string;
  DateRange: DateRangeInt;

  IASAnID: string[];
  Name: string;
  SSP: string;
  Active: string;
  ReportType: string;
  SuperOnly: number;
  Timezone: string;
  Subject: string;
  Recipients: string[];
  ShouldUploadToExternalS3: EBool;
  Frequency: string;
  ScheduledDay: string;
  Period: string;
  CurrencyDisplayed: ECurrencyType;
  ExchangeRate: IExchangeRate;
  Countries: string[];
  SiteIDs: string[];
  BidderIDs: string[];
  BidderGroupsIDs: string[];
  CampaignIDs: string[];
  NetworkIDs: string[];
  SegmentIDs: string[];
  AdsourceIDs: string[];
  Positions: string[];
  DeviceEnvs: string[];
  DeviceTypes: string[];
  DeviceSubtypes: string[];
  Adomains: string;
  DealIDs: string;
  DealNames: string[];
  BidderCodes: string[];
  DemandTypes: string[];
  Metrics: string[];
  GroupByOptions: string[];
  ReportLimit: string;
  ReportOrderBy: string;
  DMAs: string;
  States: string[];
  ChannelGroups: IChannelGroup[];
  ChannelGroupIDS: number[];
  StreamTypes: string[];
  User: IUser;
  UserID: number | undefined;
  InventoryRouteIDs: string[];
  DestinationIDs: string[];
  VenueIDs: string;
  IsSiteIDsIncluded: EBool;
  IsBidderIDsIncluded: EBool;
  OrderAliases: string[];
  LineItemAliases: string[];
  BidderTierFilter: string[];
  ContentChannelFilter: string[];
  ContentNetworkFilter: string[];
  OrigContentChannelFilter: string[];
  OrigContentNetworkFilter: string[];

  // --- USE THIS FOR FUTURE FILTERS --- //
  ReportFilterConfig: IReportFilterConfig;
  IASReportFilterConfig: IIASReportFilterConfig;
}

export interface IIASReportFilterConfig {
  [key: string]: IReportConfigFilter;
}

export interface IReportConfigFilter {
  Key: string;
  Selected: string;
}

export interface IReportFilterConfig {
  BidderActiveOnly: boolean; // if true, filters for active bidders
  ChannelActiveOnly: boolean; // if true, filters for active channel/sites
  Livestream?: number;
  CountryGroupIDs: string[];
  ChannelLabelIDs: string[];
  BidderLabelIDs: string[];
  OrderIDs: string[];
  AdvertiserIDs: string[];
  IsPartialAdBreak: string;
  SamsungDMPSegmentPartners: string[];
}

export const defaultReportFilterConfig: IReportFilterConfig = {
  BidderActiveOnly: false,
  ChannelActiveOnly: false,
  Livestream: undefined,
  CountryGroupIDs: [],
  ChannelLabelIDs: [],
  BidderLabelIDs: [],
  OrderIDs: [],
  AdvertiserIDs: [],
  IsPartialAdBreak: '',
  SamsungDMPSegmentPartners: [],
};

const transformReportFilterConfig = transformModel<IReportFilterConfig>({
  BidderActiveOnly: parseBoolTrue(['BidderActiveOnly']),
  ChannelActiveOnly: parseBoolTrue(['ChannelActiveOnly']),
  Livestream: parseNumber(['Livestream']),
  CountryGroupIDs: parseString(['CountryGroupIDs'], ','),
  ChannelLabelIDs: parseString(['ChannelLabelIDs'], ','),
  BidderLabelIDs: parseString(['BidderLabelIDs'], ','),
  OrderIDs: parseString(['OrderIDs'], ','),
  AdvertiserIDs: parseString(['AdvertiserIDs'], ','),
  IsPartialAdBreak: parseString(['IsPartialAdBreak']),
  SamsungDMPSegmentPartners: parseString(['SamsungDMPSegmentPartners'], ','),
});

export const transformReport = transformModel<IReport>({
  ID: parseString(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',
  UpdatedBy: 'UpdatedBy',
  StartDate: 'StartDate',
  EndDate: 'EndDate',
  DateRange: 'DateRange',

  Name: ['Name', 'name'],
  SSP: ['SSP', 'ssp'],
  Active: parseBoolStringTrue(['Active', 'active']),
  ReportType: parseString(['ReportType', 'reporttype']),
  SuperOnly: parseNumber(['SuperOnly', 'superonly']),
  Timezone: parseString(['Timezone', 'timezone']),
  Subject: ['Subject', 'subject'],
  Recipients: parseString(['Recipients', 'recipients'], ','),
  ShouldUploadToExternalS3: parseNumber(['ShouldUploadToExternalS3'], EBool.EBool_FALSE),
  Frequency: parseString(['Frequency', 'frequency']),
  ScheduledDay: parseString(['ScheduledDay', 'scheduledday']),
  Period: parseString(['Period', 'period']),
  CurrencyDisplayed: parseNumber(['CurrencyDisplayed', 'currency_displayed']),
  ExchangeRate: parseObject(['ExchangeRate'], transformExchangeRate),
  Countries: parseString(['Countries', 'countries'], ','),
  SiteIDs: parseString(['SiteIDs', 'siteids'], ','),
  BidderIDs: parseString(['BidderIDs', 'bidderids'], ','),
  BidderGroupsIDs: parseString(['BidderGroupsIDs', 'biddergroupsids'], ','),
  CampaignIDs: parseString(['CampaignIDs', 'campaignids'], ','),
  NetworkIDs: parseString(['NetworkIDs', 'networkids'], ','),
  SegmentIDs: parseString(['SegmentIDs', 'segmentids'], ','),
  AdsourceIDs: parseString(['AdsourceIDs', 'adsourceids'], ','),
  Positions: parseString(['Positions', 'positions'], ','),
  DeviceEnvs: parseString(['DeviceEnvs', 'deviceenvs'], ','),
  DeviceTypes: parseString(['DeviceTypes', 'devicetypes'], ','),
  DeviceSubtypes: parseString(['DeviceSubtypes', 'device_subtypes'], ','),
  Adomains: parseString(['Adomains']),
  DealIDs: parseString(['DealIDs']),
  BidderCodes: parseString(['BidderCodes', 'biddercodes'], ','),
  DemandTypes: parseString(['DemandTypes', 'demandtypes'], ','),
  Metrics: parseString(['Metrics', 'metrics'], ','),
  GroupByOptions: parseString(['GroupByOptions', 'groupbyoptions'], ','),
  ReportLimit: parseNumber(['ReportLimit']),
  ReportOrderBy: parseString(['ReportOrderBy']),
  DMAs: parseString(['DMAs', 'dmas']),
  States: parseString(['States', 'states'], ','),
  ChannelGroups: parseArray(['ChannelGroups', 'channelgroups'], transformChannelGroupWrapper),
  StreamTypes: parseString(['StreamTypes', 'streamtypes'], ','),
  User: parseObject(['User', 'user'], transformUser),
  UserID: parseNumber(['UserID', 'userid']),
  ReportFilterConfig: parseObject(['ReportFilterConfig'], transformReportFilterConfig),
  InventoryRouteIDs: parseString(['InventoryRouteIDs', 'inventoryrouteids'], ','),
  DestinationIDs: parseString(['DestinationIDs', 'destinationids'], ','),
  IsSiteIDsIncluded: parseNumber(['IsSiteIDsIncluded', 'isSiteIDsIncluded'], EBool.EBool_TRUE),
  IsBidderIDsIncluded: parseNumber(
    ['IsBidderIDsIncluded', 'isBidderIDsIncluded'],
    EBool.EBool_TRUE,
  ),
  OrderAliases: parseString(['OrderAliases'], ','),
  LineItemAliases: parseString(['LineItemAliases'], ','),
  BidderTierFilter: parseString(['BidderTierFilter']),
  ContentChannelFilter: parseString(['ContentChannelFilter'], ','),
  ContentNetworkFilter: parseString(['ContentNetworkFilter'], ','),
  OrigContentChannelFilter: parseString(['OrigContentChannelFilter'], ','),
  OrigContentNetworkFilter: parseString(['OrigContentNetworkFilter'], ','),
  IASReportFilterConfig: parseObjectV2(['IASReportFilterConfig']),
});

export const transformReportWrapper = (fromObj: any) => {
  if ('userid' in fromObj && !!fromObj.userid && typeof fromObj.userid !== 'number') {
    fromObj.userid = fromObj.userid.Int64;
  }
  return transformReport(fromObj);
};

export const mapReportTypeToText = (reportType: string) => {
  const reportTypes: { [key: string]: string } = {
    [REPORTTYPE_ADVERTISER_RULES]: 'Advertiser Rules Report',
    [REPORTTYPE_BUYERS_APS]: 'APS Report',
    [REPORTTYPE_BID_SAVER_TRUE_LIFT]: 'Publica Vault True Lift Report',
    [REPORTTYPE_CREATIVE_REVIEW]: 'Creative Review Report',
    [REPORTTYPE_ELEA_AI_ENRICHMENT_BILLING]: 'Elea AI Enrichment Billing Report',
    [REPORTTYPE_HEADERBIDDER_AUDIENCE_SEGMENTS_SUPER]: 'Audience Segment Report For All Publishers',
    [REPORTTYPE_HEADERBIDDER_AUDIENCE_SEGMENTS]: 'Audience Segment Report',
    [REPORTTYPE_HEADERBIDDER_BRAND_SAFETY_TRACKER]: 'Brand Safety Tracker Report',
    [REPORTTYPE_HEADERBIDDER_BYAUCTIONS_FILLRATE]: 'Auction Fill Rate Report',
    [REPORTTYPE_HEADERBIDDER_BYAUCTIONS]: 'Auction Report',
    [REPORTTYPE_HEADERBIDDER_BYBUYERS]: 'Bidder Report',
    [REPORTTYPE_HEADERBIDDER_CAMPAIGN]: 'Campaign Report',
    [REPORTTYPE_HEADERBIDDER_CONTEXTUAL_SEGMENTS]: 'Contextual Segment Report',
    [REPORTTYPE_HEADERBIDDER_DEALS]: 'Deals Report',
    [REPORTTYPE_HEADERBIDDER_DESTINATIONS]: 'Destinations Report',
    [REPORTTYPE_HEADERBIDDER_ELEA_AI_ENRICHMENT]: 'Elea AI Enrichment Report',
    [REPORTTYPE_HEADERBIDDER_ELEA_AI_LANGUAGE_DETECTION]: 'Elea AI Language Detection Report',
    [REPORTTYPE_HEADERBIDDER_ELEA_AI_SCAN_ALL]: 'Elea AI Scan All Report',
    [REPORTTYPE_HEADERBIDDER_INVENTORY_ROUTES]: 'Inventory Routes Report',
    [REPORTTYPE_HEADERBIDDER_PUBLICA_DEALS]: 'Publica Deals Report',
    [REPORTTYPE_HEADERBIDDER_SSP]: 'SSP Report',
    [REPORTTYPE_HEADERBIDDER_UNIQUE_USER_REPORT]: 'Unique User Report',
    [REPORTTYPE_IAS_DEVICE]: 'IAS Publisher Device Report',
    [REPORTTYPE_IAS_FIRST_PARTY]: 'IAS First Party',
    [REPORTTYPE_IAS_PERFORMANCE]: 'IAS Publisher Performance Report',
    [REPORTTYPE_IAS_THIRD_PARTY]: 'IAS Third Party',
    [REPORTTYPE_IAS_YOUTUBE]: 'IAS Publisher Youtube Report',
    [REPORTTYPE_POD_HOURLY]: 'Pod Hourly Report',
    [REPORTTYPE_SHARE_OF_VOICE]: 'Share of Voice Report',
    [REPORTTYPE_STREAM]: 'SSAI Report',
    [REPORTTYPE_STREAM_USER_ACTIVITY]: 'SSAI User Activity Report',
    [REPORTTYPE_TTD]: 'TTD Report',
    [ReportTypeID.AuctionQuickReport]: 'Auction Quick Report',
    [ReportTypeID.CampaignQuickReport]: 'Campaign Quick Report',
    [ReportTypeID.BidderQuickReport]: 'Bidder Quick Report',
    [ReportTypeID.AdomainQuickReport]: 'Adomain Quick Report',
    [ReportTypeID.ContentQuickReport]: 'Content Quick Report',
    [ReportTypeID.AppBundleQuickReport]: 'AppBundle Quick Report',
    [ReportTypeID.ThirdPartyTrackerDomainReport]: 'Third Party Tracker Domain Report',
    [ReportTypeID.CreativeValidationReport]: 'Creative Validation Report',
    [ReportTypeID.IASContentLevelPerfomanceReport]: 'IAS Content Level Performance Report',
  };
  return reportTypes[reportType] || 'Unknown Report';
};

export type SuperOnly = number;
export const SuperOnlyActive: SuperOnly = 2;
export const SuperOnlyInactive: SuperOnly = 1;

export const transformReportToAPI = transformModel({
  ID: parseNumber(['ID', 'id']),
  UpdatedBy: parseString(['UpdatedBy', 'updatedby']),
  StartDate: parseString(['StartDate', 'startdate']),
  EndDate: parseString(['EndDate', 'enddate']),
  DateRange: parseNumber(['DateRange']),

  Name: ['Name', 'name'],
  SSP: ['SSP', 'ssp'],
  Active: parseBoolTrue(['Active', 'active']),
  ReportType: parseNumber(['ReportType', 'reporttype'], 1),
  SuperOnly: parseNumber(['SuperOnly', 'superonly'], 0, (v?: number) => {
    if ((v as number) >= SuperOnlyActive) {
      return SuperOnlyActive;
    }
    return SuperOnlyInactive;
  }),
  Timezone: parseString(['Timezone', 'timezone']),
  Subject: ['Subject', 'subject'],
  Recipients: toString(['Recipients', 'recipients'], ','),
  ShouldUploadToExternalS3: parseNumber(['ShouldUploadToExternalS3']),
  Frequency: parseNumber(['Frequency', 'frequency'], 0),
  ScheduledDay: parseNumber(['ScheduledDay', 'scheduledday'], 0),
  Period: parseNumber(['Period', 'period'], 0),
  CurrencyDisplayed: parseNumber(['CurrencyDisplayed', 'currency']),
  Countries: toString(['Countries', 'countries'], ','),
  SiteIDs: toString(['SiteIDs', 'siteids'], ','),
  BidderIDs: toString(['BidderIDs', 'bidderids'], ','),
  BidderGroupsIDs: toString(['BidderGroupsIDs', 'biddergroupsids'], ','),
  CampaignIDs: toString(['CampaignIDs', 'campaignids'], ','),
  NetworkIDs: parseString(['NetworkIDs', 'networkids'], ','),
  SegmentIDs: toString(['SegmentIDs', 'segmentids'], ','),
  AdsourceIDs: toString(['AdsourceIDs', 'adsourceids'], ','),
  Positions: toString(['Positions', 'positions'], ','),
  DeviceEnvs: toString(['DeviceEnvs', 'deviceenvs'], ','),
  DeviceTypes: toString(['DeviceTypes', 'devicetypes'], ','),
  DeviceSubtypes: toString(['DeviceSubtypes', 'device_subtypes'], ','),
  Adomains: toString(['Adomains']),
  DealIDs: toString(['DealIDs']),
  BidderCodes: toString(['BidderCodes', 'biddercodes'], ','),
  DemandTypes: toString(['DemandTypes', 'demandtypes'], ','),
  Metrics: toString(['Metrics', 'metrics'], ','),
  GroupByOptions: toString(['GroupByOptions', 'groupbyoptions'], ','),
  ReportLimit: parseNumber(['ReportLimit']),
  ReportOrderBy: parseString(['ReportOrderBy']),
  States: toString(['States', 'states'], ','),
  DMAs: toString(['DMAs', 'dmas']),
  ChannelGroupIDS: ['ChannelGroupIDS', 'channelgroupids'],
  StreamTypes: toString(['StreamTypes', 'streamtypes']),
  UserID: parseNumber(['userid']),
  ReportFilterConfig: parseObject(['ReportFilterConfig'], transformReportFilterConfig),
  InventoryRouteIDs: toString(['InventoryRouteIDs', 'inventoryrouteids'], ','),
  DestinationIDs: toString(['DestinationIDs', 'destinationids'], ','),
  VenueIDs: toString(['VenueIDs', 'venue_ids']),
  IsSiteIDsIncluded: parseNumber(['IsSiteIDsIncluded', 'isSiteIDsIncluded']),
  IsBidderIDsIncluded: parseNumber(['IsBidderIDsIncluded', 'isBidderIDsIncluded']),
  OrderAliases: parseString(['OrderAliases'], ','),
  LineItemAliases: parseString(['LineItemAliases'], ','),
  BidderTierFilter: parseString(['BidderTierFilter']),
  ContentChannelFilter: toString(['ContentChannelFilter'], ','),
  ContentNetworkFilter: toString(['ContentNetworkFilter'], ','),
  OrigContentChannelFilter: toString(['OrigContentChannelFilter'], ','),
  OrigContentNetworkFilter: toString(['OrigContentNetworkFilter'], ','),
  IASReportFilterConfig: parseObjectV2(['IASReportFilterConfig']),
});

export interface IPermission {
  ID: string;
  Name: string;
  Description: string;
  Code: number;
}
export const transformPermission = transformModel<IPermission>({
  ID: parseString(['ID', 'id']),
  Name: ['Name', 'name'],
  Description: ['Description', 'description'],
  Code: parseNumber(['ID', 'id'], -1),
});
function transformPermissionWrapper(fromObj: any) {
  return transformPermission(fromObj);
}

export const Roles = {
  ADMIN: 1,
  USER: 2,
  SUPER: 3,
  TECHNICAL: 4,
  RESELLER: 7,
};

export interface IRole {
  ID: string;
  Name: string;
  Permissions: IPermission[];
}
export const transformRole = transformModel<IRole>({
  ID: parseString(['ID', 'id']),
  Name: ['Name', 'name'],
  Permissions: parseObject(['Publisher', 'publisher'], transformPermissionWrapper),
});
function transformRoleWrapper(fromObj: any) {
  return transformRole(fromObj);
}

export interface IAnalytics {
  AbTestingConfigName: string;
  AbTestingTestName: string;
  ActiveBidderTier: number;
  ActiveMargin: number;
  AdblockPct: number;
  AdBreakPosition: number;
  AdComplete: number;
  AdCompleteSS: number;
  AdDuration: number;
  AdFilled: number;
  AdImpression: number;
  AdImpressionSS: number;
  AdFirstQuartile: number;
  AdMidPoint: number;
  AdSourceID: string;
  AdThirdQuartile: number;
  Adomain: string;
  Adomains: string;
  AdsourceName: string;
  AdunitPosition: string;
  AdvertiserName: string;
  AgencyName: string;
  APKBuild: string;
  AppBundleID: string;
  AppliedContextualSegmentIDs: string;
  AppliedContextualSegmentNames: string;
  AppName: string;
  APSBidsSubmitted: number;
  APSKey: string;
  AuctionEnds: number;
  AuctionLatency: number;
  AvgBid: number;
  AvgImpECPM: number;
  AvgImpGrossECPM: number;
  AvgStreamAdFilledLatency: number;
  AvgStreamChunklistResponseLatency: number;
  AvgStreamResponseLatency: number;
  AvgWinningBid: number;
  AvgStreamStartPerSession: number;
  AvgPodRequestPerSession: number;
  AvgPodFilledPerSession: number;
  AvgPodCompletePerSession: number;
  AvgAdFilledPerSession: number;
  AvgAdImpressionPerSession: number;
  AvgAdFirstQuartilePerSession: number;
  AvgAdMidPointPerSession: number;
  AvgAdThirdQuartilePerSession: number;
  AvgAdCompletePerSession: number;
  AvgStreamErrorPerSession: number;
  AvgWatchTimePerSession: number;
  AvgWatchTimePerViewer: number;
  BidAuctions: number;
  BidDealId: string;
  OutboundDealId: string;
  BidDensity: string;
  BidderGroup: string;
  BidderID: string;
  BidderLabelName: string;
  BidderName: string;
  BidderPriority: string;
  BidderTesting: string;
  BidderTier: string;
  BidderSubTier: string;
  BidderType: string;
  BidderTypePlain: string; // this shows the demand source without custom renaming
  BidErrorCode: string;
  BidErrors: number;
  BidErrorRate: number;
  BidLosts: number;
  BidLossRatePrice: number;
  BidLossRateTier: number;
  BidNone: number;
  BidRequests: number;
  BidResponseDealID: string;
  BidResponseRate: number;
  BidResponses: number;
  BidSaverTrueLift: number;
  BidTimeout: number;
  BidTimeouts: number;
  BidTimeoutsRate: number;
  BidWonRejectReason: string;
  BidWons: number;
  BounceRate: number;
  BrandSafetyRuleID: number;
  BuyersLatency: number;
  CampaignComplete: number;
  CampaignFilled: number;
  CampaignImpression: number;
  CampaignName: string;
  CampaignNoFill: number;
  CampaignQuartile1: number;
  CampaignQuartile2: number;
  CampaignQuartile3: number;
  CampaignRequest: number;
  CampaignInventoryRequest: number;
  CampaignStart: number;
  Category: string;
  ChannelGroup: string;
  ChannelLabelName: string;
  City: string;
  ClearRateByPodDuration: number;
  Clicks: number;
  Complete: number;
  CompletionRate: number;
  ContentCat: string;
  ContentChannel: string;
  ContentContext: string;
  ContentDistID: string;
  ContentDistName: string;
  ContentEpisode: number;
  ContentGenre: string;
  ContentID: string;
  ContentKeywords: string;
  ContentLanguage: string;
  ContentLength: number;
  ContentNetwork: string;
  ContentProdQual: string;
  ContentProducerID: string;
  ContentProducerName: string;
  ContentRating: string;
  ContentSeason: string;
  ContentSeries: string;
  ContentTitle: string;
  Coppa: string;
  CostToPublisher: number;
  Country: string;
  CountryGroupName: string;
  CPM: number;
  CPMPerSecond: number;
  CreativeErrors: number;
  CreativeID: number;
  CreativeName: string;
  CreativeTagRequests: number;
  CreativeTagResponses: number;
  Custom1: string;
  Custom2: string;
  Custom3: string;
  Custom4: string;
  Custom5: string;
  Custom6: string;
  Custom7: string;
  Custom8: string;
  Custom9: string;
  Custom10: string;
  Custom11: string;
  Custom12: string;
  Custom13: string;
  Custom14: string;
  Custom15: string;
  Custom16: string;
  Custom17: string;
  Custom18: string;
  CustomTargeting: string;
  DataProvider: string;
  DataType: string;
  Date: string;
  DateLabel: string;
  DealIDs: string;
  DealName: string;
  DedupBypass: boolean;
  DeliveredAds: number;
  DeliveredPodDuration: number;
  DemandPath: string;
  DemandSidePlatformID: number;
  DestinationErrorCode: number | string;
  DestinationID: number;
  DestinationImpressions: number;
  DestinationName: string;
  DestinationSiteID: number;
  DestinationSiteName: string;
  DestinationType: number | string;
  DestinationRequests: number;
  DestinationRequestLatency: number;
  DestinationResponses: number;
  DestinationTimeouts: number;
  DestinationRequestErrors: number;
  DestinationNoFills: number;
  Device: string;
  DeviceMake: string;
  DeviceModel: string;
  DeviceOS: string;
  DeviceEnv: string;
  DeviceGeoRegion: string;
  DeviceType: string;
  DeviceSubType: string;
  DMA: string;
  DoNotTrack: string;
  Duration: number;
  DspName: string;
  DurationExceeded: number;
  EndpointErrorCode: string;
  EndpointLatency: number;
  EndpointRequests: number;
  EndpointRequestECPM: number;
  EventAuctionInits: number;
  EventEndpointBlocked: number;
  EventEndpointBlockedCode: number;
  EventEndpointErrors: number;
  FlightDateCheckpointID: string;
  FlightDateCheckpointName: string;
  FilledAds: number;
  FilledPodDuration: number;
  FilledVMAPDuration: number;
  FillRate: number;
  FillRateByDemandSource: number;
  FwSectionName: string;
  FwSectionIdV2: string;
  FwRenderRate: number;
  FwWinRate: number;
  Gdpr: string;
  GdprConsent: string;
  GlobalMediafileID: number;
  GlobalVastID: number;
  GrossRevenue: string;
  Grouplabel: string;
  HLSReject: number;
  ImpressedBidderType: string;
  ImpressionAdDuration: number;
  ImpressionAdDurationUnpaid: number;
  Impressions: number;
  ImpressionsUnpaid: number;
  InboundDealid: string;
  InboundWseat: string;
  InventoryRouteID: number;
  InventoryRouteName: string;
  InventoryRouteCondition: number | string;
  InventoryRouteErrorCode: number | string;
  InventoryRouteRequests: number;
  InventoryRouteResponses: number;
  InventoryRouteErrors: number;
  InventoryRouteNoFills: number;
  InventoryRouteEndpointLatency: number;
  InventoryRouteResponseDestinationID: number;
  InventoryType: number;
  IPType: string;
  IASContextualInfo: string;
  IrisContext: string;
  IRISContextualInfo: string;
  IrisID: string;
  IsBidSaver: boolean;
  IsFallbackResponse: boolean;
  IsMultiBid: boolean;
  IsPartialAdBreak: boolean;
  IsPoddedRequest: boolean;
  Language: string;
  LineItemAlias: string;
  LineItemCount: number;
  LineItemStatus: string;
  LineItemID: string;
  LiverampCPM: number;
  LiverampRevenue: number;
  LiverampSegmentIDs: string;
  Livestream: string;
  MaxAvailablePodDurationByTier: number;
  MediaFile: string;
  NativeCompanionClick: number;
  NativeCreativeView: number;
  NativeVideoClick: number;
  NeedsCreative: string;
  NetUnitCost: number;
  NetworkID: number;
  NetworkName: string;
  NetworkChannelID: number;
  NetworkChannelName: string;
  NetworkChannelSiteID: number;
  NetworkChannelSiteName: string;
  NoFillReason: string;
  NumberOfAdsRequested: number;
  NumBidderTypes: number;
  NumOfUsers: number;
  NumSites: number;
  OperatingSystem: string;
  ORTBWins: number;
  ORTBSiteName: string;
  OrderAlias: string;
  OrderBuyType: string;
  OrderName: string;
  PaidFilledPodDuration: number;
  RenderedPodFillRate: number;
  PodFillRate: number;
  PodClearRate: number;
  PaidPodClearRate: number;
  PageLoadTime: number;
  PageViews: number;
  PartnerCPM: number;
  PartnerRevenue: number;
  PhiloFallback: string;
  PodStart: number;
  PodStartSS: number;
  PodComplete: number;
  PodCompleteSS: number;
  PodCompleteRate: number;
  PodDuration: number;
  PodFilled: number;
  PodRequest: number;
  PresetAggregateForLastSevenDays: number;
  PresetAggregateForLastThirtyDays: number;
  PresetAggregateForYesterday: number;
  PublicaCPM: number;
  PublicaRevenue: number;
  PublisherID: string;
  PublisherCPM: number;
  PublisherMargin: number;
  PublisherName: string;
  Quartile1: number;
  Quartile2: number;
  Quartile3: number;
  RecognitionResultEnrichedLanguageInput: string;
  RecoveryActive: number;
  RenderRate: number;
  RenderRateByPodDuration: number;
  RenderedRequestedPodDuration: number;
  RequestedPodDuration: number;
  RequestedVMAPDuration: number;
  Requests: number;
  Revenue: number;
  RevenueAgg: number;
  Revenue1: number;
  Revenue2: number;
  Revenue3: number;
  Revenue4: number;
  Revenue5: number;
  RevenueLive: number;
  RokuAdKeys: string;
  SalespersonName: string;
  SamsungModel: string;
  SegmentIDs: string;
  SegmentName: string;
  SessionsPerUser: number;
  SetTargetings: number;
  SlotNumber: number;
  SharedFilledVMAPDuration: number;
  SharedRequestedVMAPDuration: number;
  ShareOfVoice: number;
  ShareOfVoiceType: number;
  ShareType: number;
  SiteID: string;
  SiteName: string;
  SitePage: string;
  Skip: number;
  SkipOffset: number;
  SOVInventoryType: string;
  SOVParallelAuctionsEnabled: number;
  SOVParallelAuctionsBackfillEnabled: number;
  SpotxEndpoint: number;
  SpotxParamChannelID: number;
  StartDate: string;
  State: string;
  EventStart: number;
  StreamAdsFetchTimeout: number;
  StreamChunklistFetch: number;
  StreamAdChunklistResponse: number;
  StreamChunklistTimeout: number;
  StreamError: number;
  StreamErrorCode: string;
  StreamFormatType: string;
  StreamPlaylistTimeout: number;
  StreamStart: number;
  TotalRenderedRequestedPodDuration: number;
  TotalRequestedPodDuration: number;
  TotalFilledPodDuration: number;
  TotalPaidFilledPodDuration: number;
  TotalDeliveredPodDuration: number;
  TotalStreamAdFilledLatency: number;
  SyntheticID: number;
  TargetingDevice: string;
  TopCountries: string;
  TotalImpEcpm: number;
  TrafficType: string;
  TraffickerName: string;
  UniqueSessions: number;
  UniqueUsers: number;
  UniqueViewers: number;
  UsPrivacy: string;
  VastErrorCode: string;
  VastErrors: number;
  VenueID: string;
  ViewsPerSession: number;
  VMAPLatency: number;
  VMAPRequests: number;
  VMAPResponses: number;
  VPAIDErrors: number;
  VPAIDStart: number;
  VPAIDImpressions: number;
  VPAIDQuartile1: number;
  VPAIDQuartile2: number;
  VPAIDQuartile3: number;
  VPAIDComplete: number;
  Zip: string;
  [key: string]: number | string | boolean;

  // IAS First and Third Party Group Bys
  MediaTypeID: string;
  // IAS First and Third Party Metrics
  TrackedAds: number;
  ImpressionRendered: number;
  IVTAds: number;
  IVTRate: number;
  MeasuredAds: number;
  MeasuredRate: number;
  ViewableImpression: number;
  ViewabilityRate: number;
  // IAS First Party Metrics (render rate already exist)
  EligibleAds: number;
  AdBlockCount: number;
  AdBlockRate: number;
  // IAS First Party Group Bys (LineItemID, CreativeID, CreativeName, AdvertiserName, OrderName already exist)
  AdvertiserID: string;
  AdUnitID: string;
  AdSize: string;
  OrderID: string;
  AdUnitPath: string;
  LineItemName: string;
  // IAS Third Party Group Bys
  PublicaChannelID: string;
  PublicaBidderID: string;
  PlacementID: string;
  // IAS Third Party Metrics
  GIVTAds: number;
  GIVTRate: number;
  SIVTAds: number;
  SIVTRate: number;
  URLFailRate: number;
  IVTFailRate: number;
  ValidVideoAds: number;
  Quartile1Rate: number;
  Quartile2Rate: number;
  Quartile3Rate: number;
  OnscreenComplete: number;
  OnscreenCompletionRate: number;
  PassedImps: number;
  PassRate: number;
  FailedImps: number;
  FailedAdRate: number;
  ValidViewableVideoAds: number;
  ViewableQuartile1: number;
  ViewableQuartile2: number;
  ViewableQuartile3: number;
  ViewableComplete: number;
  ViewableQuartile1Rate: number;
  ViewableQuartile2Rate: number;
  ViewableQuartile3Rate: number;
  ViewableCompleteRate: number;
  UnmeasuredImps: number;
  UnmeasuredImpsRate: number;
  NonViewableImpression: number;
  NonViewableImpRate: number;
  AppliedContextualSegmentPartners: string;
  GlobalTimeout: number;
  // IAS Performance Report Metrics
  MrcOutOfViewNETImps: number;
  MrcOutOfViewNEPImps: number;
  MrcOutOfViewNIVImps: number;
  MrcOutOfViewOOFImps: number;
  MrcOutOfViewNSImps: number;
  MrcOutOfViewUNKImps: number;
  BlockedContentImps: number;
  BlockedCustomListImps: number;
  BlockedGeoImps: number;
  BlockedKeywordImps: number;
  BlockedSuitabilityImps: number;
  BlockedSeeThroughImps: number;
  BlockedSuspiciousImps: number;
  BlockedLanguageImps: number;
  FraudGivtImps: number;
  FraudNhtImps: number;
  FraudHaImps: number;
  FraudDsImps: number;
  FraudIbImps: number;
  FraudLspImps: number;
  FraudSivtImps: number;
  RefreshImps: number;
  GroupmFullyInViewVideoImps: number;
  GroupmFullyInViewDisplayImps: number;
  TotalBillableImps: number;
  SittingDuckBotImps: number;
  StandardBotImps: number;
  VolunteerBotImps: number;
  ProfileBotImps: number;
  MaskedBotImps: number;
  NomadicBotImps: number;
  OtherBotImps: number;
  FailedAdultAds: number;
  FailedAlcoholAds: number;
  FailedHateSpeechAds: number;
  FailedIllegalDownloadsAds: number;
  FailedDrugsAds: number;
  FailedOffensiveLanguageAds: number;
  RefreshThresholdEvents: number;
  PublicisFullyInViewDisplayImps: number;
  PublicisFullyInViewVideoImps: number;
  MrcOvrNetImps: number;
  MrcOvrNepImps: number;
  MrcOvrNivImps: number;
  MrcOvrOofImps: number;
  MrcOvrNsImps: number;
  MrcOvrUnkImps: number;
  FailedContentImps: number;
  FailedSuitabilityImps: number;
  FailedKeywordImps: number;
  FailedCustomListImps: number;
  FailedGeoImps: number;
  FailedSuspiciousImps: number;
  FailedSeeThroughImps: number;
  FailedLanguageImps: number;
  BlockedContentAdultImps: number;
  BlockedContentAlcoholImps: number;
  BlockedContentHateImps: number;
  BlockedContentDownloadsImps: number;
  BlockedContentDrugsImps: number;
  BlockedContentOffensiveImps: number;
  BlockedContentViolenceImps: number;
  SophisticatedInvalidTrafficAds: number;
  ReducedValueInventoryAds: number;
  FraudGIVTRate: number;
  FraudNHTRate: number;
  FraudHARate: number;
  FraudDSRate: number;
  BlockedContentRate: number;
  BlockedCustomListRate: number;
  BlockedGeoRate: number;
  BlockedKeywordRate: number;
  BlockedSuitabilityRate: number;
  BlockedSeeThroughRate: number;
  BlockedSuspiciousRate: number;
  BlockedLanguageRate: number;
  RefreshedAdRate: number;
  IASRefreshedAdRate: number;
  PublicisDisplayViewableRate: number;
  PublicisVideoViewableRate: number;
  InViewAtPageOpenRate: number;
  InViewOver5sRate: number;
  InViewOver15sRate: number;
  VideoInViewRate: number;
  VideoOutOfViewRate: number;
  VideoNeverStartedRate: number;
  VideoCompletedRate: number;
  VideoCompletedInViewRate: number;
  VideoMutedRate: number;
  VideoFullScreenRate: number;
  VideoClickThroughRate: number;
  GeneralInvalidTrafficRate: number;
  SophisticatedInvalidTrafficRate: number;
  ReducedValueInventoryRate: number;
  ReviewStatus: string;
  NumberOfBlocks: string;
  Username: string;
  Timestamp: string;
  TotalCount: number;
  PrebidErrors: number;
  PostbidErrors: number;
  BidRejections: number;

  // Elea AI
  EleaAIBidErrors: number;
  EleaAIBidErrorsDifferentAdomain: number;
  EleaAIBidErrorsDifferentAdomainRate: number;
  EleaAIBidLosts: number;
  EleaAIBidLostsDifferentAdomain: number;
  EleaAIBidLostsDifferentAdomainRate: number;
  EleaAIBidResponses: number;
  EleaAIBidResponsesDifferentAdomain: number;
  EleaAIBidResponsesDifferentAdomainRate: number;
  EleaAIBidWons: number;
  EleaAIBidWonsDifferentAdomain: number;
  EleaAIBidWonsDifferentAdomainRate: number;
  EleaAIDetectedLanguage: string;
  EleaAIDetectedLanguageBlockedBids: number;
  EleaAIEnrichedAdomain: string;
  EleaAIImpressions: number;
  EleaAIImpressionsDifferentAdomain: number;
  EleaAIImpressionsDifferentAdomainRate: number;
  EleaAIMediafile: string;
  EleaAIOriginalAdomain: string;
  EleaAIBidWonRate: number;
  EleaAIImpressionRate: number;
  EleaAINumTotalAdomainsBidErrors: number;
  EleaAINumTotalAdomainsBidLosts: number;
  EleaAINumTotalAdomainsBidResponses: number;
  EleaAINumTotalAdomainsBidWons: number;
  EleaAINumTotalAdomainsImpressions: number;
  EleaAINumTotalDetectedLanguagesBidErrors: number;
  EleaAINumTotalDetectedLanguagesBidLosts: number;
  EleaAINumTotalDetectedLanguagesBidResponses: number;
  EleaAINumTotalDetectedLanguagesBidWons: number;
  EleaAINumTotalDetectedLanguagesImpressions: number;
  EleaAINumTotalMediafilesBidErrors: number;
  EleaAINumTotalMediafilesBidLosts: number;
  EleaAINumTotalMediafilesBidResponses: number;
  EleaAINumTotalMediafilesBidWons: number;
  EleaAINumTotalMediafilesImpressions: number;
  EleaAINumUniqueAdomainsBidErrors: number;
  EleaAINumUniqueAdomainsBidLosts: number;
  EleaAINumUniqueAdomainsBidResponses: number;
  EleaAINumUniqueAdomainsBidWons: number;
  EleaAINumUniqueAdomainsImpressions: number;
  EleaAINumUniqueDetectedLanguagesBidErrors: number;
  EleaAINumUniqueDetectedLanguagesBidLosts: number;
  EleaAINumUniqueDetectedLanguagesBidResponses: number;
  EleaAINumUniqueDetectedLanguagesBidWons: number;
  EleaAINumUniqueDetectedLanguagesImpressions: number;
  EleaAINumUniqueMediafilesBidErrors: number;
  EleaAINumUniqueMediafilesBidLosts: number;
  EleaAINumUniqueMediafilesBidResponses: number;
  EleaAINumUniqueMediafilesBidWons: number;
  EleaAINumUniqueMediafilesImpressions: number;

  // Pod Hourly Report
  AdvertiserRuleCpmRev: number;
  AdvertiserRuleCpmsRev: number;
  AdvertiserRulePodLift: number;
  CpmRev: number;
  CpmsRev: number;
  EventPodCompare: number;
  EventPodCompareWithAdvertiserRules: number;
  EventPodCompareWithoutAdvertiserRules: number;
  IsPhiloFallbackAuction: boolean;
  NoBidSaverRev: number;
  NumberOfWinners: number;
  PodComparePodDuration: number;
  PodCompareRevenue: number;
  PodCompareType: number;
  PodDurationBackfilled: number;
  PodDurationOwnedByNonSOVBidder: number;
  PodDurationOwnedBySOVBidder: number;
  PodLevelSOVType: number;
  TTDResponses: number;
  TTDSpend: number;
  TTDWins: number;
  TTDLosses: number;
  TTDLossesDoNotAir: number;
  TTDLossesCompSepSelf: number;
  TTDLossesCompSepOther: number;
  TTDLossesPriceOrTier: number;
  TTDLossesOther: number;
  HasAdvertiserRuleResponses: boolean;
  // Third Party Vast Tracker Domain
  VastTrackerEndpointDomain: string;
  TTDLossesPrice: number;
  TTDLossesTier: number;
  // Bid Floor Settings Report Only
  BidFloor: number;

  // Samsung Pubs only
  HasGooglePalSDKGivnParam: boolean;
  BuyerID: number;
}

export const transformAnalytics = transformModel<IAnalytics>({
  AbTestingConfigName: ['AbTestingConfigName', 'ab_testing_config_name'],
  AbTestingTestName: ['AbTestingTestName', 'ab_testing_test_name'],
  ActiveBidderTier: ['ActiveBidderTier', 'active_bidder_tier'],
  ActiveMargin: ['ActiveMargin', 'active_margin'],
  AdBreakPosition: ['AdBreakPosition', 'ad_break_position'],
  AdComplete: ['AdComplete', 'ad_complete'],
  AdDuration: ['AdDuration', 'ad_duration'],
  AdFilled: ['AdFilled', 'ad_filled'],
  AdImpression: ['AdImpression', 'ad_impression'],
  AdImpressionSS: ['AdImpressionSS', 'ad_impression_ss'],
  AdFirstQuartile: ['AdFirstQuartile', 'ad_first_quartile'],
  AdMidPoint: ['AdMidPoint', 'ad_mid_point'],
  AdSourceID: ['AdSourceID', 'ad_source_id'],
  AdThirdQuartile: ['AdThirdQuartile', 'ad_third_quartile'],
  AdblockPct: ['AdblockPct', 'adblockpct'],
  Adomain: ['Adomain', 'adomain'],
  Adomains: ['Adomains'],
  AdsourceName: ['AdsourceName', 'adsourcename'],
  AdunitPosition: ['AdunitPosition', 'adunitposition'],
  AdvertiserName: ['AdvertiserName', 'advertiser_name'],
  AgencyName: ['AgencyName', 'agency_name'],
  APKBuild: ['APKBuild', 'apk_build'],
  AppBundleID: ['AppBundleID', 'app_bundle_id'],
  AppliedConextualSegmentIDs: ['AppliedConextualSegmentIDs', 'applied_conextual_segment_ids'],
  AppName: ['AppName', 'app_name'],
  APSBidsSubmitted: ['APSBidsSubmitted', 'aps_bids_submitted'],
  APSKey: ['APSKey', 'aps_key'],
  AuctionInits: ['AuctionInits', 'auction_inits'],
  AuctionEnds: ['AuctionEnds', 'auction_ends'],
  AuctionClearRate: ['AuctionClearRate', 'auction_clear_rate'],
  AuctionLatency: ['AuctionLatency', 'auction_latency'],
  AudienceID: ['AudienceID', 'audience_id'],
  AvgBid: ['AvgBid', 'avg_bid'],
  AvgImpECPM: ['AvgImpECPM', 'avg_imp_ecpm'],
  AvgImpGrossECPM: ['AvgImpGrossECPM', 'avg_imp_gross_ecpm'],
  AvgStreamAdFilledLatency: ['AvgStreamAdFilledLatency', 'avg_stream_ad_filled_latency'],
  AvgStreamChunklistResponseLatency: [
    'AvgStreamChunklistResponseLatency',
    'avg_stream_chunklist_response_latency',
  ],
  AvgStreamResponseLatency: ['AvgStreamResponseLatency', 'avg_stream_response_latency'],
  AvgWinningBid: ['AvgWinningBid', 'avg_winning_bid'],
  AvgStreamStartPerSession: ['AvgStreamStartPerSession', 'avg_stream_start_per_session'],
  AvgPodRequestPerSession: ['AvgPodRequestPerSession', 'avg_pod_request_per_session'],
  AvgPodFilledPerSession: ['AvgPodFilledPerSession', 'avg_pod_filled_per_session'],
  AvgPodCompletePerSession: ['AvgPodCompletePerSession', 'avg_pod_complete_per_session'],
  AvgAdFilledPerSession: ['AvgAdFilledPerSession', 'avg_ad_filled_per_session'],
  AvgAdImpressionPerSession: ['AvgAdImpressionPerSession', 'avg_ad_impression_per_session'],
  AvgAdFirstQuartilePerSession: [
    'AvgAdFirstQuartilePerSession',
    'avg_ad_first_quartile_per_session',
  ],
  AvgAdMidPointPerSession: ['AvgAdMidPointPerSession', 'avg_ad_mid_point_per_session'],
  AvgAdThirdQuartilePerSession: [
    'AvgAdThirdQuartilePerSession',
    'avg_ad_third_quartile_per_session',
  ],
  AvgAdCompletePerSession: ['AvgAdCompletePerSession', 'avg_ad_complete_per_session'],
  AvgStreamErrorPerSession: ['AvgStreamErrorPerSession', 'avg_stream_error_per_session'],
  AvgWatchTimePerSession: ['avg_watch_time_per_session'],
  AvgWatchTimePerViewer: ['avg_watch_time_per_viewer'],
  BidAuctions: ['BidAuctions', 'bid_auctions'],
  BidDealId: ['BidDealId', 'bid_deal_id'],
  OutboundDealId: ['OutboundDealId', 'outbound_deal_id'],
  BidDensity: ['BidDensity', 'bid_density'],
  BidErrorCode: ['BidErrorCode', 'bid_error_code'],
  BidErrors: ['BidErrors', 'bid_errors'],
  BidErrorRate: ['BidErrorRate', 'bid_error_rate'],
  BidLosts: ['BidLosts', 'bid_losts'],
  BidLossRatePrice: ['BidLossRatePrice', 'bid_loss_rate_price'],
  BidLossRateTier: ['BidLossRateTier', 'bid_loss_rate_tier'],
  BidNone: ['BidNone', 'bid_none'],
  BidRequests: ['BidRequests', 'bid_requests'],
  BidRequestRate: ['BidRequestRate', 'bid_request_rate'],
  BidResponseDealID: ['BidResponseDealID', 'bid_response_deal_id'],
  BidResponseRate: ['BidResponseRate', 'bid_response_rate'],
  BidResponses: ['BidResponses', 'bid_responses'],
  BidSaverTrueLift: ['BidSaverTrueLift', 'bid_saver_true_lift'],
  BidTimeouts: ['BidTimeouts', 'bid_timeouts'],
  BidTimeoutsRate: ['BidTimeoutsRate', 'bid_timeouts_rate'],
  BidWonRejectReason: ['BidWonRejectReason', 'bid_won_reject_reason'],
  BidWons: ['BidWons', 'bid_wons'],
  BidderGroup: ['BidderGroup', 'bidder_group'],
  BidderID: ['BidderID', 'bidder_id', 'bidderid'],
  BidderLabelName: ['BidderLabelName', 'bidder_label_name'],
  BidderName: ['BidderName', 'biddername'],
  BidderPriority: ['BidderPriority', 'bidder_priority'],
  BidderTesting: ['BidderTesting', 'bidder_testing'],
  BidderTier: ['BidderTier', 'bidder_tier'],
  BidderSubTier: ['BidderSubTier', 'bidder_subtier'],
  BidderType: ['BidderType', 'bidder_type'],
  BidderTypePlain: ['BidderTypePlain', 'bidder_type_plain'],
  BounceRate: ['BounceRate', 'bouncerate'],
  BrandSafetyRuleID: ['BrandSafetyRuleID', 'brand_safety_rule_id'],
  BrandSafetyTrackerName: ['BrandSafetyTrackerName', 'brand_safety_tracker_name'],
  BrandSafetyTrackerType: ['BrandSafetyTrackerType', 'brand_safety_tracker_type'],
  BuyersLatency: ['BuyersLatency', 'buyers_latency'],
  ClearRateByPodDuration: ['ClearRateByPodDuration', 'clear_rate_by_pod_duration'],
  CPM: ['CPM', 'cpm'],
  CPMPerSecond: ['CPMPerSecond', 'cpm_per_second'],
  CampaignComplete: ['CampaignComplete', 'campaign_complete'],
  CampaignFilled: ['CampaignFilled', 'campaign_filled'],
  CampaignImpression: ['CampaignImpression', 'campaign_impression'],
  CampaignName: ['CampaignName', 'campaignname'],
  CampaignNoFill: ['CampaignNoFill', 'campaign_no_fill'],
  CampaignQuartile1: ['CampaignQuartile1', 'campaign_quartile1'],
  CampaignQuartile2: ['CampaignQuartile2', 'campaign_quartile2'],
  CampaignQuartile3: ['CampaignQuartile3', 'campaign_quartile3'],
  CampaignRequest: ['CampaignRequest', 'campaign_request'],
  CampaignInventoryRequest: ['CampaignInventoryRequest', 'campaign_inventory_request'],
  CampaignStart: ['CampaignStart', 'campaign_start'],
  Category: ['Category', 'category'],
  ChannelGroup: ['ChannelGroup', 'channel_group'],
  ChannelList: ['ChannelList', 'channel_list'],
  ChannelLabelName: ['ChannelLabelName', 'channel_label_name'],
  City: ['City', 'city'],
  Clicks: ['Clicks', 'clicks'],
  Complete: ['Complete', 'complete', 'Completes'],
  CompletionRate: ['CompletionRate', 'completion_rate'],
  ContentCat: ['ContentCat', 'content_cat'],
  ContentChannel: ['ContentChannel', 'content_channel'],
  ContentContext: ['ContentContext', 'content_context'],
  ContentDistID: ['ContentDistID', 'content_dist_id'],
  ContentDistName: ['ContentDistName', 'content_dist_name'],
  ContentEpisode: ['ContentEpisode', 'content_episode'],
  ContentGenre: ['ContentGenre', 'content_genre'],
  ContentID: ['ContentID', 'content_id'],
  ContentKeywords: ['ContentKeywords', 'content_keywords'],
  ContentLanguage: ['ContentLanguage', 'content_language'],
  ContentLength: ['ContentLength', 'content_length'],
  ContentNetwork: ['ContentNetwork', 'content_network'],
  ContentProdQual: ['ContentProdQual', 'content_prodqual'],
  ContentProducerID: ['ContentProducerID', 'content_producer_id'],
  ContentProducerName: ['ContentProducerName', 'content_producer_name'],
  ContentRating: ['ContentRating', 'content_rating'],
  ContentSeason: ['ContentSeason', 'content_season'],
  ContentSeries: ['ContentSeries', 'content_series'],
  ContentTitle: ['ContentTitle', 'content_title'],
  Coppa: ['Coppa', 'coppa'],
  CostToPublisher: ['CostToPublisher', 'cost_to_publisher'],
  Country: ['Country', 'country'],
  CountryGroupName: ['CountryGroupName', 'country_group_name'],
  CreativeErrors: ['CreativeErrors', 'creative_errors'],
  CreativeTagRequests: ['CreativeTagRequests', 'creative_tag_requests'],
  CreativeTagResponses: ['CreativeTagResponses', 'creative_tag_responses'],
  CreativeID: ['CreativeID', 'creative_id'],
  CreativeName: ['CreativeName', 'creative_name'],
  CstAdFilled: ['CstAdFilled', 'cst_ad_filled'],
  CstAdImpression: ['CstAdImpression', 'cst_ad_impression'],
  CstRenderRate: ['CstRenderRate', 'cst_render_rate'],
  Custom10: ['Custom10', 'custom10'],
  Custom11: ['Custom11', 'custom11'],
  Custom12: ['Custom12', 'custom12'],
  Custom13: ['Custom13', 'custom13'],
  Custom14: ['Custom14', 'custom14'],
  Custom15: ['Custom15', 'custom15'],
  Custom16: ['Custom16', 'custom16'],
  Custom17: ['Custom17', 'custom17'],
  Custom18: ['Custom18', 'custom18'],
  Custom1: ['Custom1', 'custom1'],
  Custom2: ['Custom2', 'custom2'],
  Custom3: ['Custom3', 'custom3'],
  Custom4: ['Custom4', 'custom4'],
  Custom5: ['Custom5', 'custom5'],
  Custom6: ['Custom6', 'custom6'],
  Custom7: ['Custom7', 'custom7'],
  Custom8: ['Custom8', 'custom8'],
  Custom9: ['Custom9', 'custom9'],
  CustomTargeting: ['CustomTargeting', 'custom_targeting'],
  DMA: ['DMA', 'dma'],
  DataProvider: ['DataProvider', 'data_provider'],
  DataType: ['DataType', 'data_type'],
  Date: ['Date', 'date'],
  DateLabel: ['DateLabel', 'datelabel'],
  DealID: ['DealID', 'deal_id'],
  DealIDs: ['DealIDs'],
  DealName: ['DealName', 'deal_name'],
  DedupBypass: ['DedupBypass', 'dedup_bypass'],
  DeliveredAds: ['DeliveredAds', 'delivered_ads'],
  DeliveredPodDuration: ['DeliveredPodDuration', 'delivered_pod_duration'],
  DemandPath: ['DemandPath', 'demand_path'],
  DemandSidePlatformID: ['DemandSidePlatformID', 'demand_side_platform_id'],
  DemandType: ['DemandType', 'demandtype'],
  DestinationErrorCode: ['DestinationErrorCode', 'destination_error_code'],
  DestinationID: ['DestinationID', 'destination_id'],
  DestinationImpressions: ['DestinationImpressions', 'destination_impressions'],
  DestinationName: ['DestinationName', 'destination_name'],
  DestinationSiteID: ['DestinationSiteID', 'destination_site_id'],
  DestinationSiteName: ['DestinationSiteName', 'destination_site_name'],
  DestinationType: ['DestinationType', 'destination_type'],
  DestinationRequests: ['DestinationRequests', 'destination_requests'],
  DestinationResponses: ['DestinationResponses', 'destination_responses'],
  DestinationTimeouts: ['DestinationTimeouts', 'destination_timeouts'],
  DestinationRequestErrors: ['DestinationRequestErrors', 'destination_request_errors'],
  DestinationNoFills: ['DestinationNoFills', 'destination_no_fills'],
  DestinationRequestLatency: ['DestinationRequestLatency', 'destination_request_latency'],
  Device: ['Device', 'device'],
  DeviceGeoRegion: ['DeviceGeoRegion', 'device_geo_region'],
  DeviceMake: ['DeviceMake', 'device_make'],
  DeviceModel: ['DeviceModel', 'device_model'],
  DeviceOS: ['DeviceOS', 'device_os'],
  DeviceEnv: ['DeviceEnv', 'deviceEnv'],
  DeviceType: ['DeviceType', 'devicetype'],
  DeviceSubType: ['DeviceSubType', 'device_subtype'],
  DoNotTrack: ['DoNotTrack', 'do_not_track'],
  DspName: ['DspName', 'dsp_name'],
  Duration: ['Duration', 'duration'],
  DurationExceeded: ['DurationExceeded', 'duration_exceeded'],
  EndpointErrorCode: ['EndpointErrorCode', 'endpoint_error_code'],
  EndpointLatency: ['EndpointLatency', 'endpoint_latency'],
  EndpointRequests: ['EndpointRequests', 'endpoint_requests'],
  EndpointRequestECPM: ['EndpointRequestECPM', 'endpoint_request_ecpm'],
  EndpointResponses: ['EndpointResponses', 'endpoint_responses'],
  EventAuctionInits: ['EventAuctionInits', 'event_auction_inits'],
  EventEndpointBlocked: ['EventEndpointBlocked', 'event_endpoint_blocked'],
  EventEndpointBlockedCode: ['EventEndpointBlockedCode', 'event_endpoint_blocked_code'],
  EventEndpointErrors: ['EventEndpointErrors', 'event_endpoint_errors'],
  FlightDateCheckpointID: ['FlightDateCheckpointID', 'flight_date_checkpoint_id'],
  FlightDateCheckpointName: ['FlightDateCheckpointName', 'flight_date_checkpoint_name'],
  FilledAds: ['FilledAds', 'filled_ads'],
  FilledPodDuration: ['FilledPodDuration', 'filled_pod_duration'],
  FilledVMAPDuration: ['FilledVMAPDuration', 'filled_vmap_duration'],
  FillRate: ['FillRate', 'fillrate'],
  FillRateByDemandSource: ['FillRateByDemandSource', 'fill_rate_by_demand_source'],
  FwRenderRate: ['FwRenderRate', 'fw_render_rate'],
  FwSectionName: ['FwSectionName', 'fw_section_name'],
  FwSectionIdV2: ['FwSectionIdV2', 'fw_section_id_v2'],
  FwWinRate: ['FwWinRate', 'fw_win_rate'],
  Gdpr: ['Gdpr', 'gdpr'],
  GdprConsent: ['GdprConsent', 'gdpr_consent'],
  GlobalMediafileID: ['GlobalMediafileID', 'global_mediafile_id'],
  GlobalVastID: ['GlobalVastID', 'global_vast_id'],
  Grouplabel: ['Grouplabel', 'grouplabel'],
  GrossRevenue: ['GrossRevenue', 'gross_revenue'],
  HLSReject: ['HLSReject', 'hls_reject'],
  ImpressedBidderType: ['ImpressedBidderType', 'impressed_bidder_type'],
  ImpressionAdDuration: ['ImpressionAdDuration', 'impression_ad_duration'],
  ImpressionAdDurationUnpaid: ['ImpressionAdDurationUnpaid', 'impression_ad_duration_unpaid'],
  Impressions: ['Impressions', 'impressions'],
  ImpressionsUnpaid: ['ImpressionsUnpaid', 'impressions_unpaid'],
  InboundDealId: ['InboundDeal', 'inbound_deal_id'],
  InboundWseat: ['InboundWseat', 'inbound_wseat'],
  InventoryRouteID: ['InventoryRouteID', 'inventory_route_id'],
  InventoryRouteName: ['InventoryRouteName', 'inventory_route_name'],
  InventoryRouteCondition: ['InventoryRouteCondition', 'inventory_route_condition'],
  InventoryRouteErrorCode: ['InventoryRouteErrorCode', 'inventory_route_error_code'],
  InventoryRouteRequests: ['InventoryRouteRequests', 'inventory_route_requests'],
  InventoryRouteResponses: ['InventoryRouteResponses', 'inventory_route_responses'],
  InventoryRouteErrors: ['InventoryRouteErrors', 'inventory_route_errors'],
  InventoryRouteNoFills: ['InventoryRouteNoFills', 'inventory_route_no_fills'],
  InventoryRouteEndpointLatency: [
    'InventoryRouteEndpointLatency',
    'inventory_route_endpoint_latency',
  ],
  InventoryRouteResponseDestinationID: [
    'InventoryRouteResponseDestinationID',
    'inventory_route_response_destination_id',
  ],
  InventoryType: ['InventoryType', 'inventory_type'],
  IPType: ['IPType', 'ip_type'],
  IASContextualInfo: ['IASContextualInfo', 'ias_contextual_info'],
  IrisContext: ['IrisContext', 'iris_context'],
  IRISContextualInfo: ['IRISContextualInfo', 'iris_contextual_info'],
  IrisID: ['IrisID', 'iris_id'],
  IsBidSaver: ['IsBidSaver', 'is_bid_saver'],
  IsFallbackResponse: ['IsFallbackResponse', 'is_fallback_response'],
  IsMultiBid: ['IsMultiBid', 'is_multi_bid'],
  IsPartialAdBreak: ['IsPartialAdBreak', 'is_partial_ad_break'],
  IsPoddedRequest: ['IsPoddedRequest', 'is_podded_request'],
  Language: ['Language', 'language'],
  LineItemAlias: ['LineItemAlias', 'line_item_alias'],
  LineItemCount: ['LineItemCount', 'line_item_count'],
  LineItemStatus: ['LineItemStatus', 'line_item_status'],
  LineItemID: ['LineItemID', 'line_item_id'],
  LiverampCPM: ['LiverampCPM', 'liveramp_cpm'],
  LiverampRevenue: ['LiverampRevenue', 'liveramp_revenue'],
  LiverampSegmentIDs: ['LiverampSegmentIDs', 'liveramp_segment_ids'],
  Livestream: ['Livestream', 'livestream'],
  LRDmProviders: ['LRDmProviders', 'lr_dm_providers'],
  LRDmSegIDs: ['LRDmSegIDs', 'lr_dm_seg_ids'],
  LRDmDataFee: ['LRDmDataFee', 'lr_dm_data_fee'],
  LR1pMatchingFee: ['LR1pMatchingFee', 'lr_1p_matching_fee'],
  LR3pMatchingFee: ['LR3pMatchingFee', 'lr_3p_matching_fee'],
  MaxAvailablePodDurationByTier: [
    'MaxAvailablePodDurationByTier',
    'max_available_pod_duration_by_tier',
  ],
  MediaFile: ['MediaFile', 'media_file'],
  NativeCompanionClick: ['NativeCompanionClick', 'native_companion_click'],
  NativeCreativeView: ['NativeCreativeView', 'native_creative_view'],
  NativeVideoClick: ['NativeVideoClick', 'native_video_click'],
  NeedsCreative: ['NeedsCreative', 'needs_creative'],
  NetUnitCost: ['NetUnitCost', 'net_unit_cost'],
  NetworkID: ['NetworkID', 'network_id'],
  NetworkName: ['NetworkName', 'network_name'],
  NetworkChannelID: ['NetworkChannelID', 'network_channel_id'],
  NetworkChannelName: ['NetworkChannelName', 'network_channel_name'],
  NetworkChannelSiteID: ['NetworkChannelSiteID', 'network_channel_site_id'],
  NetworkChannelSiteName: ['NetworkChannelSiteName', 'network_channel_site_name'],
  NoFillReason: ['NoFillReason', 'no_fill_reason'],
  NonCstAdFilled: ['NonCstAdFilled', 'non_cst_ad_filled'],
  NonCstAdImpression: ['NonCstAdImpression', 'non_cst_ad_impression'],
  NonCstRenderRate: ['NonCstRenderRate', 'non_cst_render_rate'],
  NumberOfAdsRequested: ['NumberOfAdsRequested', 'number_of_ads_requested'],
  NumBidderTypes: ['NumBidderTypes', 'num_bidder_types'],
  NumOfUsers: ['num_of_users'],
  NumSites: ['NumSites', 'num_sites'],
  OperatingSystem: ['OperatingSystem', 'operating_system'],
  OrderAlias: ['OrderAlias', 'order_alias'],
  OrderBuyType: ['OrderBuyType', 'order_buy_type'],
  OrderName: ['OrderName', 'order_name'],
  ORTBWins: ['ORTBWins', 'ortb_wins'],
  ORTBSiteName: ['ORTBSiteName', 'ortb_site_name'],
  PageLoadTime: ['PageLoadTime', 'pageloadtime'],
  PageViews: ['PageViews', 'pageviews'],
  PaidFilledPodDuration: ['PaidFilledPodDuration', 'paid_filled_pod_duration'],
  PaidPodClearRate: ['PaidPodClearRate', 'paid_pod_clear_rate'],
  PartnerCPM: ['PartnerCPM', 'partner_cpm'],
  PartnerRevenue: ['PartnerRevenue', 'partner_revenue'],
  PhiloFallback: ['PhiloFallback', 'philo_fallback'],
  PodClearRate: ['PodClearRate', 'pod_clear_rate'],
  PodStart: ['PodStart', 'pod_start'],
  PodStartSS: ['PodStartSS', 'pod_start_ss'],
  PodComplete: ['PodComplete', 'pod_complete'],
  PodCompleteRate: ['PodCompleteRate', 'pod_complete_rate'],
  PodDuration: ['PodDuration', 'pod_duration'],
  PodFilled: ['PodFilled', 'pod_filled'],
  RenderedPodFillRate: ['RenderedPodFillRate', 'rendered_pod_fill_rate'],
  PodFillRate: ['PodFillRate', 'pod_fill_rate'],
  PodRequest: ['PodRequest', 'pod_request'],
  PresetAggregateForLastSevenDays: [
    'PresetAggregateForLastSevenDays',
    'preset_aggregate_last_seven_days',
  ],
  PresetAggregateForLastThirtyDays: [
    'PresetAggregateForLastThirtyDays',
    'preset_aggregate_last_thirty_days',
  ],
  PresetAggregateForYesterday: ['PresetAggregateForYesterday', 'preset_aggregate_yesterday'],
  PublicaCPM: ['PublicaCPM', 'publica_cpm'],
  PublicaRevenue: ['PublicaRevenue', 'publica_revenue'],
  PublisherCPM: ['PublisherCPM', 'publisher_cpm'],
  PublisherID: ['PublisherID', 'publisher_id'],
  PublisherMargin: ['PublisherMargin', 'publisher_margin'],
  PublisherName: ['PublisherName', 'publisher_name'],
  Quartile1: ['Quartile1', 'quartile1'],
  Quartile2: ['Quartile2', 'quartile2'],
  Quartile3: ['Quartile3', 'quartile3'],
  RecognitionResultEnrichedLanguageInput: [
    'RecognitionResultEnrichedLanguageInput',
    'recognition_result_enriched_language_input',
  ],
  RecoveryActive: ['RecoveryActive', 'recovery_active'],
  RenderRate: ['RenderRate', 'render_rate'],
  RenderRateByPodDuration: ['RenderRateByPodDuration', 'render_rate_by_pod_duration'],
  RenderedRequestedPodDuration: ['RenderedRequestedPodDuration', 'rendered_requested_pod_duration'],
  RequestedPodDuration: ['RequestedPodDuration', 'requested_pod_duration'],
  RequestedVMAPDuration: ['RequestedVMAPDuration', 'requested_vmap_duration'],
  Requests: ['Requests', 'requests'],
  Revenue: ['Revenue', 'revenue'],
  RokuAdKeys: ['RokuAdKeys', 'roku_ad_keys'],
  SalespersonName: ['SalespersonName', 'salesperson_name'],
  SamsungModel: ['SamsungModel', 'samsung_model'],
  SegmentIDs: ['SegmentIDs', 'segmentids'],
  SegmentName: ['SegmentName', 'segmentname'],
  SessionsPerUser: ['sessions_per_user'],
  SetTargetings: ['SetTargetings', 'set_targetings'],
  SlotNumber: ['SlotNumber', 'slot_number'],
  SharedRequestedVMAPDuration: ['SharedRequestedVMAPDuration', 'shared_requested_vmap_duration'],
  SharedFilledVMAPDuration: ['SharedFilledVMAPDuration', 'shared_filled_vmap_duration'],
  ShareOfVoice: ['ShareOfVoice', 'share_of_voice'],
  ShareOfVoiceType: ['ShareOfVoiceType', 'share_of_voice_type'],
  ShareType: ['ShareType', 'share_type'],
  SiteID: ['SiteID', 'siteid'],
  SiteName: ['SiteName', 'sitename'],
  SitePage: ['SitePage', 'site_page'],
  Skip: ['Skip', 'skip'],
  SkipOffset: ['SkipOffset', 'skip_offset'],
  SOVInventoryType: ['SOVInventoryType', 'sov_inventory_type'],
  SOVParallelAuctionsEnabled: ['SOVParallelAuctionsEnabled', 'sov_parallel_auctions_enabled'],
  SOVParallelAuctionsBackfillEnabled: [
    'SOVParallelAuctionsBackfillEnabled',
    'sov_parallel_auctions_backfill_enabled',
  ],
  SpotxEndpoint: ['SpotxEndpoint', 'spotx_endpoint'],
  SpotxParamChannelID: ['SpotxParamChannelID', 'spotx_param_channel_id'],
  StartDate: ['StartDate', 'start_date'],
  State: ['State', 'state'],
  EventStart: ['event_start'],
  StreamAdsFetchTimeout: ['StreamAdsFetchTimeout', 'stream_ads_fetch_timeout'],
  StreamChunklistFetch: ['StreamChunklistFetch', 'stream_chunklist_fetch'],
  StreamAdChunklistResponse: ['StreamAdChunklistResponse', 'stream_ad_chunklist_response'],
  StreamChunklistTimeout: ['StreamChunklistTimeout', 'stream_chunklist_timeout'],
  StreamError: ['StreamError', 'stream_error'],
  StreamErrorCode: ['StreamErrorCode', 'stream_error_code'],
  StreamFormatType: ['StreamFormatType', 'stream_format_type'],
  StreamPlaylistTimeout: ['StreamPlaylistTimeout', 'stream_playlist_timeout'],
  StreamStart: ['StreamStart', 'stream_start'],
  TotalCount: ['TotalCount', 'total_count'],
  TotalRenderedRequestedPodDuration: [
    'TotalRenderedRequestedPodDuration',
    'total_rendered_requested_pod_duration',
  ],
  TotalRequestedPodDuration: ['TotalRequestedPodDuration', 'total_requested_pod_duration'],
  TotalFilledPodDuration: ['TotalFilledPodDuration', 'total_filled_pod_duration'],
  TotalPaidFilledPodDuration: ['TotalPaidFilledPodDuration', 'total_paid_filled_pod_duration'],
  TotalDeliveredPodDuration: ['TotalDeliveredPodDuration', 'total_delivered_pod_duration'],
  TotalStreamAdFilledLatency: ['TotalStreamAdFilledLatency', 'total_stream_ad_filled_latency'],
  SyntheticID: ['SyntheticID', 'synthetic_id'],
  TargetingDevice: ['TargetingDevice', 'targeting_device'],
  TopCountries: ['TopCountries', 'topcountries'],
  TotalImpEcpm: ['TotalImpEcpm', 'total_imp_ecpm'],
  TrafficType: ['TrafficType', 'traffictype'],
  TraffickerName: ['TraffickerName', 'trafficker_name'],
  UniqueSessions: ['UniqueSessions', 'uniquesessions'],
  UniqueUsers: ['UniqueUsers', 'unique_users'],
  UniqueViewers: ['unique_viewers'],
  UsPrivacy: ['UsPrivacy', 'us_privacy'],
  VMAPLatency: ['VMAPLatency', 'vmap_latency'],
  VMAPRequests: ['VMAPRequests', 'vmap_requests'],
  VMAPResponses: ['VMAPResponses', 'vmap_responses'],
  VastErrorCode: ['VastErrorCode', 'vast_error_code'],
  VastErrors: ['VastErrors', 'vast_errors'],
  VenueID: ['VenueID', 'venue_id'],
  ViewsPerSession: ['ViewsPerSession', 'viewspersession'],
  VPAIDErrors: ['VPAIDErrors', 'vpaid_errors'],
  VPAIDStart: ['VPAIDStart', 'vpaid_start'],
  VPAIDImpressions: ['VPAIDImpressions', 'vpaid_impressions'],
  VPAIDQuartile1: ['VPAIDQuartile1', 'vpaid_quartile1'],
  VPAIDQuartile2: ['VPAIDQuartile2', 'vpaid_quartile2'],
  VPAIDQuartile3: ['VPAIDQuartile3', 'vpaid_quartile3'],
  VPAIDComplete: ['VPAIDComplete', 'vpaid_complete'],
  WinRate: ['WinRate', 'win_rate'],
  Zip: ['Zip', 'zip'],

  // IAS First and Third Party Group Bys
  MediaTypeID: ['MediaTypeID'],
  // IAS First and Third Party Metrics
  TrackedAds: ['TrackedAds'],
  ImpressionRendered: ['RenderedImpressions'],
  IVTAds: ['IVTAds'],
  IVTRate: ['IVTRate'],
  MeasuredAds: ['MeasuredAds'],
  MeasuredRate: ['MeasuredRate'],
  ViewableImpression: ['ViewableImpressions'],
  ViewabilityRate: ['ViewabilityRate'],
  // IAS First Party Metrics (render rate already exist)
  EligibleAds: ['EligibleAds'],
  AdBlockCount: ['AdBlockCount'],
  AdBlockRate: ['AdBlockRate'],
  // IAS First Party Group Bys (LineItemID, CreativeID, CreativeName, AdvertiserName, OrderName already exist)
  AdvertiserID: ['AdvertiserID', 'advertiser_id'],
  AdUnitID: ['AdUnitID'],
  AdSize: ['AdSize'],
  OrderID: ['OrderID', 'order_id'],
  AdUnitPath: ['AdUnitPath'],
  LineItemName: ['LineItemName', 'line_item_name'],
  // IAS Third Party Group Bys
  PublicaChannelID: ['PublicaChannelID'],
  PublicaBidderID: ['PublicaBidderID'],
  PlacementID: ['PlacementID'],
  // IAS Third Party Metrics
  GIVTAds: ['GIVTAds'],
  GIVTRate: ['GIVTRate'],
  SIVTAds: ['SIVTAds'],
  SIVTRate: ['SIVTRate'],
  URLFailRate: ['URLFailRate'],
  IVTFailRate: ['IVTFailRate'],
  ValidVideoAds: ['ValidVideoAds'],
  Quartile1Rate: ['Quartile1Rate'],
  Quartile2Rate: ['Quartile2Rate'],
  Quartile3Rate: ['Quartile3Rate'],
  OnscreenComplete: ['OnScreenCompletes'],
  OnscreenCompletionRate: ['OnScreenCompletionRate'],
  PassedImps: ['PassedImps'],
  PassRate: ['PassRate'],
  FailedImps: ['FailedImps'],
  FailedAdRate: ['FailedAdRate'],
  ValidViewableVideoAds: ['ValidViewableVideoAds'],
  ViewableQuartile1: ['ViewableQuartile1'],
  ViewableQuartile2: ['ViewableQuartile2'],
  ViewableQuartile3: ['ViewableQuartile3'],
  ViewableComplete: ['ViewableCompletes'],
  ViewableQuartile1Rate: ['ViewableQuartile1Rate'],
  ViewableQuartile2Rate: ['ViewableQuartile2Rate'],
  ViewableQuartile3Rate: ['ViewableQuartile3Rate'],
  ViewableCompleteRate: ['ViewableCompleteRate'],
  UnmeasuredImps: ['UnmeasuredImps'],
  UnmeasuredImpsRate: ['UnmeasuredImpsRate'],
  NonViewableImpression: ['NonViewableImpression'],
  NonViewableImpRate: ['NonViewableImpRate'],
  AppliedContextualSegmentPartners: ['applied_contextual_segment_partners'],
  AppliedContextualSegmentNames: ['applied_contextual_segment_names'],
  AppliedContextualSegmentIDs: ['applied_contextual_segment_ids'],
  GlobalTimeout: ['global_timeout'],

  // EleaAI Scan All
  EleaAIBidErrors: ['elea_ai_bid_errors'],
  EleaAIBidErrorsDifferentAdomain: ['elea_ai_bid_errors_different_adomain'],
  EleaAIBidErrorsDifferentAdomainRate: ['elea_ai_bid_errors_different_adomain_rate'],
  EleaAIBidLosts: ['elea_ai_bid_losts'],
  EleaAIBidLostsDifferentAdomain: ['elea_ai_bid_losts_different_adomain'],
  EleaAIBidLostsDifferentAdomainRate: ['elea_ai_bid_losts_different_adomain_rate'],
  EleaAIBidResponses: ['elea_ai_bid_responses'],
  EleaAIBidResponsesDifferentAdomain: ['elea_ai_bid_responses_different_adomain'],
  EleaAIBidResponsesDifferentAdomainRate: ['elea_ai_bid_responses_different_adomain_rate'],
  EleaAIBidWons: ['elea_ai_bid_wons'],
  EleaAIBidWonsDifferentAdomain: ['elea_ai_bid_wons_different_adomain'],
  EleaAIBidWonsDifferentAdomainRate: ['elea_ai_bid_wons_different_adomain_rate'],
  EleaAIDetectedLanguage: ['elea_ai_detected_language'],
  EleaAIEnrichedAdomain: ['elea_ai_enriched_adomain'],
  EleaAIImpressions: ['elea_ai_impressions'],
  EleaAIImpressionsDifferentAdomain: ['elea_ai_impressions_different_adomain'],
  EleaAIImpressionsDifferentAdomainRate: ['elea_ai_impressions_different_adomain_rate'],
  EleaAIMediafile: ['elea_ai_mediafile'],
  EleaAIOriginalAdomain: ['elea_ai_original_adomain'],
  EleaAIDetectedLanguageBlockedBids: ['elea_ai_detected_language_blocked_bids'],
  // EleaAI Enrichment
  EleaAIBidWonRate: ['elea_ai_bid_won_rate'],
  EleaAIImpressionRate: ['elea_ai_impression_rate'],
  EleaAINumTotalAdomainsBidErrors: ['elea_ai_num_total_adomains_bid_errors'],
  EleaAINumTotalAdomainsBidLosts: ['elea_ai_num_total_adomains_bid_losts'],
  EleaAINumTotalAdomainsBidResponses: ['elea_ai_num_total_adomains_bid_responses'],
  EleaAINumTotalAdomainsBidWons: ['elea_ai_num_total_adomains_bid_wons'],
  EleaAINumTotalAdomainsImpressions: ['elea_ai_num_total_adomains_impressions'],
  EleaAINumTotalDetectedLanguagesBidErrors: ['elea_ai_num_total_detected_languages_bid_errors'],
  EleaAINumTotalDetectedLanguagesBidLosts: ['elea_ai_num_total_detected_languages_bid_losts'],
  EleaAINumTotalDetectedLanguagesBidResponses: [
    'elea_ai_num_total_detected_languages_bid_responses',
  ],
  EleaAINumTotalDetectedLanguagesBidWons: ['elea_ai_num_total_detected_languages_bid_wons'],
  EleaAINumTotalDetectedLanguagesImpressions: ['elea_ai_num_total_detected_languages_impressions'],
  EleaAINumTotalMediafilesBidErrors: ['elea_ai_num_total_mediafiles_bid_errors'],
  EleaAINumTotalMediafilesBidLosts: ['elea_ai_num_total_mediafiles_bid_losts'],
  EleaAINumTotalMediafilesBidResponses: ['elea_ai_num_total_mediafiles_bid_responses'],
  EleaAINumTotalMediafilesBidWons: ['elea_ai_num_total_mediafiles_bid_wons'],
  EleaAINumTotalMediafilesImpressions: ['elea_ai_num_total_mediafiles_impressions'],
  EleaAINumUniqueAdomainsBidErrors: ['elea_ai_num_unique_adomains_bid_errors'],
  EleaAINumUniqueAdomainsBidLosts: ['elea_ai_num_unique_adomains_bid_losts'],
  EleaAINumUniqueAdomainsBidResponses: ['elea_ai_num_unique_adomains_bid_responses'],
  EleaAINumUniqueAdomainsBidWons: ['elea_ai_num_unique_adomains_bid_wons'],
  EleaAINumUniqueAdomainsImpressions: ['elea_ai_num_unique_adomains_impressions'],
  EleaAINumUniqueDetectedLanguagesBidErrors: ['elea_ai_num_unique_detected_languages_bid_errors'],
  EleaAINumUniqueDetectedLanguagesBidLosts: ['elea_ai_num_unique_detected_languages_bid_losts'],
  EleaAINumUniqueDetectedLanguagesBidResponses: [
    'elea_ai_num_unique_detected_languages_bid_responses',
  ],
  EleaAINumUniqueDetectedLanguagesBidWons: ['elea_ai_num_unique_detected_languages_bid_wons'],
  EleaAINumUniqueDetectedLanguagesImpressions: [
    'elea_ai_num_unique_detected_languages_impressions',
  ],
  EleaAINumUniqueMediafilesBidErrors: ['elea_ai_num_unique_mediafiles_bid_errors'],
  EleaAINumUniqueMediafilesBidLosts: ['elea_ai_num_unique_mediafiles_bid_losts'],
  EleaAINumUniqueMediafilesBidResponses: ['elea_ai_num_unique_mediafiles_bid_responses'],
  EleaAINumUniqueMediafilesBidWons: ['elea_ai_num_unique_mediafiles_bid_wons'],
  EleaAINumUniqueMediafilesImpressions: ['elea_ai_num_unique_mediafiles_impressions'],
  // IAS Performance Report
  // Summary
  TotalImps: ['TotalImps'],
  IvtAds: ['IvtAds'],
  IvtRate: ['IvtRate'],
  // GroupM Viewability
  GroupmFullyInViewDisplayImps: ['GroupmFullyInViewDisplayImps'],
  GroupmFullyInViewVideoImps: ['GroupmFullyInViewVideoImps'],
  TotalBillableImps: ['TotalBillableImps'],
  GroupmDisplayViewableRate: ['GroupmDisplayViewableRate'],
  GroupmVideoViewableRate: ['GroupmVideoViewableRate'],
  // Invalid Traffic
  SittingDuckBotImps: ['SittingDuckBotImps'],
  StandardBotImps: ['StandardBotImps'],
  VolunteerBotImps: ['VolunteerBotImps'],
  ProfileBotImps: ['ProfileBotImps'],
  MaskedBotImps: ['MaskedBotImps'],
  NomadicBotImps: ['NomadicBotImps'],
  OtherBotImps: ['OtherBotImps'],
  // Failure Risk Categories
  FailedAdultAds: ['FailedContentAdultAds'],
  FailedAlcoholAds: ['FailedContentAlcoholAds'],
  FailedHateSpeechAds: ['FailedContentHateSpeechAds'],
  FailedIllegalDownloadsAds: ['FailedContentDownloadsAds'],
  FailedDrugsAds: ['FailedContentDrugsAds'],
  FailedOffensiveLanguageAds: ['FailedContentOffensiveAds'],
  BlockedContentImps: ['BlockedContentImps'],
  // Ad Refresh
  RefreshImps: ['RefreshImps'],
  RefreshThresholdEvents: ['RefreshThresholdEvents'],
  RefreshedAdRate: ['RefreshedAdRate'],
  IASRefreshedAdRate: ['IASRefreshedAdRate'],
  // Publicis Viewability
  PublicisFullyInViewDisplayImps: ['PublicisFullyInViewDisplayImps'],
  PublicisFullyInViewVideoImps: ['PublicisFullyInViewVideoImps'],
  PublicisDisplayViewableRate: ['PublicisDisplayViewableRate'],
  PublicisVideoViewableRate: ['PublicisVideoViewableRate'],
  // Blocked Reasons
  BlockedSuitabilityImps: ['BlockedSuitabilityImps'],
  BlockedKeywordImps: ['BlockedKeywordImps'],
  BlockedCustomListImps: ['BlockedCustomListImps'],
  BlockedGeoImps: ['BlockedGeoImps'],
  BlockedSuspiciousImps: ['BlockedSuspiciousImps'],
  BlockedSeeThroughImps: ['BlockedSeeThroughImps'],
  BlockedLanguageImps: ['BlockedLanguageImps'],
  // Exposure
  InViewAtPageOpenRate: ['InViewAtPageOpenRate'],
  InViewOver5sRate: ['InViewOver5sRate'],
  InViewOver15sRate: ['InViewOver15sRate'],
  // Out Of View
  MrcOvrNetImps: ['MrcOvrNetImps'],
  MrcOvrNepImps: ['MrcOvrNepImps'],
  MrcOvrNivImps: ['MrcOvrNivImps'],
  MrcOvrOofImps: ['MrcOvrOofImps'],
  MrcOvrNsImps: ['MrcOvrNsImps'],
  MrcOvrUnkImps: ['MrcOvrUnkImps'],
  // Failure Reasons
  FailedContentImps: ['FailedContentImps'],
  FailedSuitabilityImps: ['FailedSuitabilityImps'],
  FailedKeywordImps: ['FailedKeywordImps'],
  FailedCustomListImps: ['FailedCustomListImps'],
  FailedGeoImps: ['FailedGeoImps'],
  FailedSuspiciousImps: ['FailedSuspiciousImps'],
  FailedSeeThroughImps: ['FailedSeeThroughImps'],
  FailedLanguageImps: ['FailedLanguageImps'],
  // Blocked Risk Categories
  BlockedContentAdultImps: ['BlockedContentAdultImps'],
  BlockedContentAlcoholImps: ['BlockedContentAlcoholImps'],
  BlockedContentHateImps: ['BlockedContentHateImps'],
  BlockedContentDownloadsImps: ['BlockedContentDownloadsImps'],
  BlockedContentDrugsImps: ['BlockedContentDrugsImps'],
  BlockedContentOffensiveImps: ['BlockedContentOffensiveImps'],
  BlockedContentViolenceImps: ['BlockedContentViolenceImps'],
  // Video
  VideoInViewRate: ['VideoInViewRate'],
  VideoOutOfViewRate: ['VideoOutOfViewRate'],
  VideoNeverStartedRate: ['VideoNeverStartedRate'],
  VideoCompletedRate: ['VideoCompletedRate'],
  VideoCompletedInViewRate: ['VideoCompletedInViewRate'],
  VideoMutedRate: ['VideoMutedRate'],
  VideoFullScreenRate: ['VideoFullScreenRate'],
  VideoClickThroughRate: ['VideoClickThroughRate'],
  // Invalid Traffic by Type
  FraudGivtImps: ['FraudGivtImps'],
  FraudNhtImps: ['FraudNhtImps'],
  FraudHaImps: ['FraudHaImps'],
  FraudDsImps: ['FraudDsImps'],
  FraudIbImps: ['FraudIbImps'],
  FraudLspImps: ['FraudLspImps'],
  SophisticatedInvalidTrafficAds: ['SophisticatedInvalidTrafficAds'],
  ReducedValueInventoryAds: ['ReducedValueInventoryAds'],
  GeneralInvalidTrafficRate: ['GeneralInvalidTrafficRate'],
  SophisticatedInvalidTrafficRate: ['SophisticatedInvalidTrafficRate'],
  ReducedValueInventoryRate: ['ReducedValueInventoryRate'],
  ReviewStatus: ['ReviewStatus', 'review_status'],
  NumberOfBlocks: ['NumberOfBlocks', 'number_of_blocks'],
  Username: ['Username', 'username'],
  Timestamp: ['Timestamp', 'timestamp'],
  PrebidErrors: ['PrebidErrors', 'prebid_errors'],
  PostbidErrors: ['PostbidErrors', 'postbid_errors'],
  BidRejections: ['BidRejections', 'bid_rejections'],

  // Pod Hourly Report
  AdvertiserRuleCpmRev: ['advertiser_rule_cpm_rev'],
  AdvertiserRuleCpmsRev: ['advertiser_rule_cpms_rev'],
  AdvertiserRulePodLift: ['advertiser_rule_pod_lift'],
  CpmRev: ['cpm_rev'],
  CpmsRev: ['cpms_rev'],
  EventPodCompare: ['event_pod_compare'],
  EventPodCompareWithAdvertiserRules: ['event_pod_compare_with_advertiser_rules'],
  EventPodCompareWithoutAdvertiserRules: ['event_pod_compare_without_advertiser_rules'],
  IsPhiloFallbackAuction: ['is_philo_fallback_auction'],
  NoBidSaverRev: ['no_bid_saver_rev'],
  NumberOfWinners: ['number_of_winners'],
  PodComparePodDuration: ['pod_compare_pod_duration'],
  PodCompareRevenue: ['pod_compare_revenue'],
  PodCompareType: ['pod_compare_type'],
  PodDurationBackfilled: ['pod_duration_backfilled'],
  PodDurationOwnedByNonSOVBidder: ['pod_duration_owned_by_non_sov_bidder'],
  PodDurationOwnedBySOVBidder: ['pod_duration_owned_by_sov_bidder'],
  PodLevelSOVType: ['pod_level_sov_type'],
  TTDResponses: ['ttd_responses'],
  TTDSpend: ['ttd_spend'],
  TTDWins: ['ttd_wins'],
  TTDLosses: ['ttd_losses'],
  TTDLossesDoNotAir: ['ttd_losses_do_not_air'],
  TTDLossesCompSepSelf: ['ttd_losses_comp_sep_self'],
  TTDLossesCompSepOther: ['ttd_losses_comp_sep_other'],
  TTDLossesPriceOrTier: ['ttd_losses_price_or_tier'],
  TTDLossesOther: ['ttd_losses_other'],
  HasAdvertiserRuleResponses: ['has_advertiser_rule_responses'],
  // Third Party Vast Tracker Domain
  VastTrackerEndpointDomain: ['VastTrackerEndpointDomain', 'vast_tracker_endpoint_domain'],
  TTDLossesPrice: ['ttd_losses_price'],
  TTDLossesTier: ['ttd_losses_tier'],
  // Bid Floor Settings Report Only
  BidFloor: ['BidFloor', 'bid_floor'],

  // Samsung Pubs only
  HasGooglePalSDKGivnParam: ['HasGooglePalSDKGivnParam', 'has_google_pal_sdk_givn_param'],
  BuyerID: ['BuyerID', 'buyer_id'],

  // SmartFloors
  ControlBidRequests: ['ControlBidRequests', 'control_bid_requests'],
  OptimizedBidRequests: ['OptimizedBidRequests', 'optimized_bid_requests'],
  LowBidRequests: ['LowBidRequests', 'low_bid_requests'],
  HighBidRequests: ['HighBidRequests', 'high_bid_requests'],
  ControlBidResponses: ['ControlBidResponses', 'control_bid_responses'],
  OptimizedBidResponses: ['OptimizedBidResponses', 'optimized_bid_responses'],
  LowBidResponses: ['LowBidResponses', 'low_bid_responses'],
  HighBidResponses: ['HighBidResponses', 'high_bid_responses'],
  ControlBidWons: ['ControlBidWons', 'control_bid_wons'],
  OptimizedBidWons: ['OptimizedBidWons', 'optimized_bid_wons'],
  LowBidWons: ['LowBidWons', 'low_bid_wons'],
  HighBidWons: ['HighBidWons', 'high_bid_wons'],
  ControlAvgWinningBid: ['ControlAvgWinningBid', 'control_avg_winning_bid'],
  OptimizedAvgWinningBid: ['OptimizedAvgWinningBid', 'optimized_avg_winning_bid'],
  LowAvgWinningBid: ['LowAvgWinningBid', 'low_avg_winning_bid'],
  HighAvgWinningBid: ['HighAvgWinningBid', 'high_avg_winning_bid'],
  ControlBidFloor: ['ControlBidFloor', 'control_bid_floor'],
  OptimizedBidFloor: ['OptimizedBidFloor', 'optimized_bid_floor'],
  LowBidFloor: ['LowBidFloor', 'low_bid_floor'],
  HighBidFloor: ['HighBidFloor', 'high_bid_floor'],

  // Traffic Shaping
  TrafficShapingOptimizedBidRequests: [
    'TrafficShapingOptimizedBidRequests',
    'traffic_shaping_optimized_bid_requests',
  ],
  TrafficShapingControlBidRequests: [
    'TrafficShapingControlBidRequests',
    'traffic_shaping_control_bid_requests',
  ],
  TrafficShapingOptimizedBidResponses: [
    'TrafficShapingOptimizedBidResponses',
    'traffic_shaping_optimized_bid_responses',
  ],
  TrafficShapingControlBidResponses: [
    'TrafficShapingControlBidResponses',
    'traffic_shaping_control_bid_responses',
  ],
  TrafficShapingOptimizedBidResponse_rate: [
    'TrafficShapingOptimizedBidResponse_rate',
    'traffic_shaping_optimized_bid_response_rate',
  ],
  TrafficShapingControlBidResponse_rate: [
    'TrafficShapingControlBidResponse_rate',
    'traffic_shaping_control_bid_response_rate',
  ],
  TrafficShapingOptimizedBidImpressions: [
    'TrafficShapingOptimizedBidImpressions',
    'traffic_shaping_optimized_bid_impressions',
  ],
  TrafficShapingControlBidImpressions: [
    'TrafficShapingControlBidImpressions',
    'traffic_shaping_control_bid_impressions',
  ],
  TrafficShapingOptimizedBidRevenue: [
    'TrafficShapingOptimizedBidRevenue',
    'traffic_shaping_optimized_bid_revenue',
  ],
  TrafficShapingControlBidRevenue: [
    'TrafficShapingControlBidRevenue',
    'traffic_shaping_control_bid_revenue',
  ],
  TrafficShapingBidRequestDifference: [
    'TrafficShapingBidRequestDifference',
    'traffic_shaping_bid_request_difference',
  ],
  TrafficShapingBidResponseDifference: [
    'TrafficShapingBidResponseDifference',
    'traffic_shaping_bid_response_difference',
  ],
  TrafficShapingBidImpressionsDifference: [
    'TrafficShapingBidImpressionsDifference',
    'traffic_shaping_bid_impressions_difference',
  ],
  TrafficShapingBidResponseRateDifference: [
    'TrafficShapingBidResponseRateDifference',
    'traffic_shaping_bid_response_rate_difference',
  ],
  TrafficShapingRevenueDifference: [
    'TrafficShapingRevenueDifference',
    'traffic_shaping_revenue_difference',
  ],
  RequestsOverRevenue: ['RequestsOverRevenue', 'requests_over_revenue'],
});

export interface ISiteAdserverCampaignAssociation {
  ID: number;
  SiteID: number;
  AdserverCampaignID: number;
  SiteWeight: number;
}

export interface IChannelGroupAdserverCampaignAssociation {
  ID: number;
  ChannelGroupID: number;
  AdserverCampaignID: number;
  SiteWeight: number;
}

export interface IChannelListAdserverCampaignAssociation {
  ID: number;
  ChannelListID: number;
  AdserverCampaignID: number;
}

export type CampaignType = string;
export const CampaignTypeDirect: CampaignType = '1';
export const CampaignTypeHouse: CampaignType = '2';

export type CampaignFrequencyActive = string;
export const CampaignFrequencyActiveInactive: CampaignFrequencyActive = '1';
export const CampaignFrequencyActiveActive: CampaignFrequencyActive = '2';

export interface IAdserverCampaignTracker {
  ID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  Active: CampaignTrackerActive;
  Type: string;
  URL: string;
  CreativeID: number;
}

export type CampaignTrackerActive = string;
export const CampaignTrackerActiveInactive: CampaignTrackerActive = '1';
export const CampaignTrackerActiveActive: CampaignTrackerActive = '2';

export const transformAdserverCampaignTracker = transformModel<IAdserverCampaignTracker>({
  ID: parseString(['ID']),
  CreatedAt: parseString(['CreatedAt']),
  UpdatedAt: parseString(['UpdatedAt']),
  DeletedAt: parseString(['DeletedAt']),

  Active: parseString(['Active']),
  Type: parseString(['Type']),
  URL: parseString(['URL']),
  CreativeID: parseNumber(['CreativeID']),
});

export function transformAdserverCampaignTrackerWrapper(o: any) {
  return transformAdserverCampaignTracker(o);
}

export const marshalAdserverCampaignTracker = transformModel({
  ID: parseString(['ID']),

  Active: parseString(['Active']),
  Type: parseString(['Type']),
  URL: parseString(['URL']),
  CreativeID: parseNumber(['CreativeID']),
});

export function marshalAdserverCampaignTrackerWrapper(o: any) {
  return marshalAdserverCampaignTracker(o);
}

export enum EAdserverCreativeType {
  Unknown = 0,
  Video = 1,
  Banner = 2,
}

export enum EBannerRank {
  NotBanner = -1,
  Primary,
  Secondary,
}

export interface IAdserverCreative {
  ID: string;
  UUID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  StartDate: string;
  EndDate: string;
  Name: string;
  Weight: string;
  VAST: string;
  AdserverGlobalMediafileIDs: string[];
  AdserverCampaignID: string;
  AdserverGlobalMediafiles?: IAdserverGlobalMediafile[];
  AdserverGlobalVastID?: number;
  _CreativeType?: string;
  CreativeType?: EAdserverCreativeType;
  ClickThroughURL?: string;
  BannerRank?: EBannerRank;
  GlobalMediafileList?: IMenuList;
  GlobalVastList?: IMenuList;
}

export const transformAdserverCreative = transformModel<IAdserverCreative>({
  ID: parseString(['ID']),
  UUID: ['UUID'],
  CreatedAt: ['CreatedAt'],
  UpdatedAt: ['UpdatedAt'],
  DeletedAt: ['DeletedAt'],

  StartDate: parseString(['StartDate']),
  EndDate: parseString(['EndDate']),
  Name: parseString(['Name', 'name']),
  VAST: ['VAST'],
  Weight: parseString(['Weight']),
  AdserverGlobalMediafileIDs: parseArray(
    ['AdserverGlobalMediafiles'],
    (v: number | string | IAdserverGlobalMediafile) => {
      if (typeof v === 'object') {
        return '' + v.ID;
      }
      return '' + v;
    },
  ),
  AdserverGlobalMediafiles: parseArray(
    ['AdserverGlobalMediafiles'],
    transformAdserverGlobalMediafileWrapper,
  ),
  AdserverGlobalVastID: parseNumber(['AdserverGlobalVastID']),
  CreativeType: parseNumber(['CreativeType']),
  ClickThroughURL: parseString(['ClickThroughURL']),
});

export function transformAdserverCreativeWrapper(o: any) {
  return transformAdserverCreative(o);
}

export const marshalAdserverCreative = transformModel({
  ID: parseNumber(['ID']),
  UUID: parseString(['UUID']),

  StartDate: parseString(['StartDate']),
  EndDate: parseString(['EndDate']),
  Name: parseString(['Name', 'name']),
  VAST: parseString(['VAST']),
  Weight: parseNumber(['Weight']),
  AdserverCampaignID: parseNumber(['AdserverCampaignID']),
  AdserverGlobalMediafileIDs: ['AdserverGlobalMediafileIDs'],
  AdserverGlobalMediafiles: parseArray(
    ['AdserverGlobalMediafiles'],
    transformAdserverGlobalMediafileWrapper,
  ),
  AdserverGlobalVastID: parseNumber(['AdserverGlobalVastID']),
  CreativeType: parseNumber(['CreativeType']),
  ClickThroughURL: parseString(['ClickThroughURL']),
});

export function marshalAdserverCreativeWrapper(o: any) {
  return marshalAdserverCreative(o);
}

export interface IAdserverCreativeFlightDatesSubgroup {
  ID: string;
  AdserverCreatives: IAdserverCreative[];
  TotalWeight: number;
  StartDate: string;
  EndDate: string;
  errors: string; // holds error messages for checkpoint dates
}

export interface IVariantMediafile {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  Active: EBool;
  AdserverGlobalMediafileID: number;
  AdserverCreativeID: number;
  URL: string;
  prettyURL?: string;
  Type: string;
  Delivery: string;
  Width: number;
  Height: number;
  Bitrate: number;
  Duration: number;
  Scalable: string;
  MaintainAspectRatio: string;
  Name: string;
}

export const transformVariantMediafile = transformModel<IVariantMediafile>({
  ID: parseNumber(['ID']),
  CreatedAt: parseString(['CreatedAt']),
  UpdatedAt: parseString(['UpdatedAt']),
  DeletedAt: parseString(['DeletedAt']),

  Active: parseNumber(['Active']),
  AdserverGlobalMediafileID: parseNumber(['AdserverGlobalMediafileID']),
  AdserverCreativeID: parseNumber(['AdserverCreativeID']),
  URL: parseString(['URL']),
  Type: parseString(['Type']),
  Delivery: parseString(['Delivery']),
  Width: parseNumber(['Width']),
  Height: parseNumber(['Height']),
  Bitrate: parseNumber(['Bitrate']),
  Duration: parseNumber(['Duration']),
  Scalable: parseString(['Scalable']),
  MaintainAspectRatio: parseString(['MaintainAspectRatio']),
  Name: parseString(['Name']),
});

export interface IAdserverGlobalMediafile {
  ID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  PublisherID: string;
  AdserverCreativeID: string;
  URL: string;
  prettyURL?: string;
  Type: string;
  Delivery: string;
  Width: number;
  Height: number;
  Bitrate: string;
  Duration: number;
  Scalable: string;
  MaintainAspectRatio: string;
  CampaignIDS: number[];
  CurrentlyUploading: boolean;
  Name: string;
  CreativeIDS: number[];
  VariantMediafiles: IVariantMediafile[];
  AdvertiserID: number;
}

export const transformAdserverGlobalMediafile = transformModel<IAdserverGlobalMediafile>({
  ID: parseString(['ID']),
  CreatedAt: parseString(['CreatedAt']),
  UpdatedAt: parseString(['UpdatedAt']),
  DeletedAt: parseString(['DeletedAt']),

  PublisherID: parseString(['PublisherID']),
  AdserverCreativeID: parseString(['AdserverCreativeID']),
  URL: parseString(['URL']),
  Type: parseString(['Type']),
  Delivery: parseString(['Delivery']),
  Width: parseNumber(['Width']),
  Height: parseNumber(['Height']),
  Bitrate: parseString(['Bitrate']),
  Duration: parseNumber(['Duration']),
  Scalable: parseString(['Scalable']),
  MaintainAspectRatio: parseString(['MaintainAspectRatio']),
  CampaignIDS: parseArray(['CampaignIDS', 'campaign_ids'], (obj: any) => {
    return obj;
  }),
  CreativeIDS: parseArray(['CreativeIDS'], (obj: any) => {
    return obj;
  }),
  Name: parseString(['Name']),
  VariantMediafiles: parseArray(['VariantMediafiles'], transformVariantMediafile),
  AdvertiserID: parseNumber(['AdvertiserID']),
});

function transformAdserverGlobalMediafileWrapper(o: any) {
  const m = transformAdserverGlobalMediafile(o);
  m.CurrentlyUploading = false;
  return m;
}

export type MediafileScalable = string;
export const MediafileScalableOff: MediafileScalable = '1';
export const MediafileScalableOn: MediafileScalable = '2';

export type MediafileMaintainAspectRatio = string;
export const MediafileMaintainAspectRatioOff: MediafileMaintainAspectRatio = '1';
export const MediafileMaintainAspectRatioOn: MediafileMaintainAspectRatio = '2';

export interface IForecast {
  Success: boolean;
  Forecast: number;
  RenderRate: number;
  Notes: string;
  Query: string;
}

export interface IContextualSegmentsForecast {
  Success: boolean;
  ForecastNotes: string;
  BidderImpressions: number;
  CPM: number;
  EndpointRequests: number;
  Revenue: number;
}

export const transformContextualSegmentsForecast = transformModel<IContextualSegmentsForecast>({
  Success: ['Success', 'success'],
  ForecastNotes: parseString(['ForecastNotes', 'forecast_notes']),
  BidderImpressions: parseNumber(['BidderImpressions', 'bidder_impressions']),
  CPM: parseFloat(['CPM', 'cpm']),
  EndpointRequests: parseNumber(['EndpointRequests', 'endpoint_requests']),
  Revenue: parseFloat(['Revenue', 'revenue']),
});

export const transformForecast = transformModel<IForecast>({
  Success: ['Success', 'success'],
  Forecast: parseNumber(['Forecast', 'forecast']),
  RenderRate: parseFloat(['RenderRate', 'render_rate']),
  FrequencyCap: parseNumber(['FrequencyCap', 'frequency_cap']),
  Notes: parseString(['Notes', 'notes']),
  Query: parseString(['Query', 'query']),
});

export interface ISegmentForecast {
  Success: boolean;
  UniqueQuery: string;
  SegmentQuery: string;
  UniqueUsersForSegmentHLLQuery: string;
  Message: string;
  EndpointRequests: number;
  PublisherUniqueDeviceIDs: number;
  SegmentUniqueDeviceIDs: number;
  Impressions: number;
  Revenue: number;
  AvgImpECPM: number;
}

export interface ISuccess {
  status: boolean;
  message: string;
}

export const transformSegmentForecast = transformModel<ISegmentForecast>({
  Success: ['Success', 'success'],
  UniqueQuery: ['SegmentQuery', 'unique_query'],
  UniqueUsersForSegmentHLLQuery: [
    'UniqueUsersForSegmentHLLQuery',
    'unique_users_for_segment_hll_query',
  ],
  SegmentQuery: ['UniqueQuery', 'segment_query'],
  Message: ['Message', 'message'],
  EndpointRequests: parseNumber(['EndpointRequests', 'endpoint_requests']),
  PublisherUniqueDeviceIDs: parseNumber([
    'PublisherUniqueDeviceIDs',
    'publisher_unique_device_ids',
  ]),
  SegmentUniqueDeviceIDs: parseNumber(['SegmentUniqueDeviceIDs', 'segment_unique_device_ids']),
  Impressions: parseNumber(['Impressions', 'impressions']),
  Revenue: parseFloat(['Revenue', 'revenue']),
  AvgImpECPM: parseFloat(['AvgImpECPM', 'avg_imp_ecpm']),
});

// Audience Manager Models
export interface IAudienceSegment {
  ID: string;
  UUID: string;
  Active: string;
  Name: string;
  URL: string;
  IsFirstParty: string;
  ThirdPartySegmentIDs: string;
  Logic: string;
  LogicQuery: string;
  LogicQueryJson: string;
  AudienceSegmentType: string;
  LiverampOTTHubPrefix: string;
  LiverampCPM: number;
  DataProvider: string;
  Partner: string;
  CadentFilePath: string;
  CadentMeta: string;
  UpdatedAt: string;
  CreatedAt: string;
  DataSize: string;
  UniqueUserCount: string;
}

export interface ISamsungDMPAudienceSegmentPartner {
  ID: string;
  CreatedAt: string;
  PublisherID: number;
  SegmentPartner: string;
  SegmentIDs: string;
}

export const transformAudienceSegment = transformModel<IAudienceSegment>({
  ID: parseString(['ID']),
  UUID: ['UUID'],
  Active: parseString(['Active']),
  Name: ['Name'],
  URL: ['URL'],
  IsFirstParty: parseString(['IsFirstParty']),
  ThirdPartySegmentIDs: parseString(['ThirdPartySegmentIDs']),
  Logic: parseString(['Logic']),
  LogicQuery: parseString(['LogicQuery']),
  LogicQueryJson: parseString(['LogicQueryJson']),
  AudienceSegmentType: parseString(['AudienceSegmentType']),
  LiverampOTTHubPrefix: parseString(['LiverampOTTHubPrefix']),
  LiverampCPM: ['LiverampCPM'],
  DataProvider: ['DataProvider'],
  Partner: ['Partner'],
  CadentFilePath: ['CadentFilePath'],
  CadentMeta: ['CadentMeta'],
  UpdatedAt: ['UpdatedAt'],
  CreatedAt: ['CreatedAt'],
  DataSize: parseString(['DataSize']),
  UniqueUserCount: parseString(['UniqueUserCount']),
});

export const transformSamsungDMPAudienceSegmentPartner =
  transformModel<ISamsungDMPAudienceSegmentPartner>({
    ID: parseString(['ID']),
    CreatedAt: ['CreatedAt'],
    PublisherID: parseNumber(['PublisherID']),
    SegmentPartner: parseString(['SegmentPartner']),
    SegmentIDs: parseString(['SegmentIDs']),
  });

export type AudienceSegmentActive = string;
export const AudienceSegmentActiveUnknown: AudienceSegmentActive = '0';
export const AudienceSegmentActiveInactive: AudienceSegmentActive = '1';
export const AudienceSegmentActiveActive: AudienceSegmentActive = '2';

export type AudienceSegmentIsFirstParty = string;
export const AudienceSegmentIsFirstPartyUnknown: AudienceSegmentIsFirstParty = '0';
export const AudienceSegmentIsFirstPartyFalse: AudienceSegmentIsFirstParty = '1';
export const AudienceSegmentIsFirstPartyTrue: AudienceSegmentIsFirstParty = '2';

export type AudienceSegmentThirdPartyPartner = string;
export const AudienceSegmentThirdPartyPartnerCadent: AudienceSegmentThirdPartyPartner = 'Cadent';
export const AudienceSegmentThirdPartyPartnerLiveRamp: AudienceSegmentThirdPartyPartner =
  'LiveRamp';
export const AudienceSegmentThirdPartyPartnerTransUnion: AudienceSegmentThirdPartyPartner =
  'TransUnion';
export const AudienceSegmentPartnerSamsungDMP: AudienceSegmentThirdPartyPartner = 'SamsungDMP';
export const AudienceSegmentPartnerOnePlusXDMP: AudienceSegmentThirdPartyPartner = '1PlusXDMP';
export const AudienceSegmentPartnerUnknown: AudienceSegmentThirdPartyPartner = 'Unknown';

export type AudienceSegmentLogic = string;
export const AudienceSegmentLogicUnknown: AudienceSegmentLogic = '0';
export const AudienceSegmentLogicOr: AudienceSegmentLogic = '1';
export const AudienceSegmentLogicAnd: AudienceSegmentLogic = '2';
export const AudienceSegmentLogicNot: AudienceSegmentLogic = '3';

export type AudienceSegmentType = string;
export const AudienceSegmentTypeUnknown: AudienceSegmentType = '0';
export const AudienceSegmentTypeFirstParty: AudienceSegmentType = '1';
export const AudienceSegmentTypeLiverampDataStore: AudienceSegmentType = '2';
export const AudienceSegmentTypeLiverampOTTHub: AudienceSegmentType = '3';
export const AudienceSegmentTypeTransunion: AudienceSegmentType = '4';
export const AudienceSegmentTypeSamsungDMP: AudienceSegmentType = '5';
export const AudienceSegmentTypeOnePlusXDMP: AudienceSegmentType = '6';
export const AudienceSegmentTypeFirstPartyAutoUpload: AudienceSegmentType = '7';

export interface IAudienceDeal {
  ID: string;
  SegmentID: string;
  BidderID: string;
  DealID: string;
}

export const transformAudienceDeal = transformModel<IAudienceDeal>({
  ID: parseString(['ID']),
  SegmentID: parseString(['SegmentID']),
  BidderID: parseString(['BidderID']),
  DealID: parseString(['DealID']),
});

export interface IChannelLabel {
  ID: string;
  Name: string;
  Sites: ISite[];
  SiteIDs: string[];
}

export const transformChannelLabel = transformModel<IChannelLabel>({
  ID: parseString(['ID']),
  Name: parseString(['Name']),
  Sites: parseArray(['Sites', 'sites'], transformSiteWrapper),
  SiteIDs: ['SiteIDs', 'site_ids'],
});

function transformChannelLabelWrapper(fromObj: any): IChannelLabel {
  return transformChannelLabel(fromObj);
}

export interface IBidderLabel {
  ID: string;
  Name: string;
  Bidders: IHeaderBidder[];
}

export const transformBidderLabelFromAPI = transformModel<IBidderLabel>({
  ID: parseString(['ID']),
  Name: parseString(['Name']),
  Bidders: parseArray(['Bidders', 'bidders'], transformBidderWrapper),
});

function transformBidderLabelWrapperFromAPI(fromObj: any): IBidderLabel {
  return transformBidderLabelFromAPI(fromObj);
}

export const transformBidderLabelToAPI = transformModel<IBidderLabel>({
  ID: parseNumber(['ID']),
  Name: parseString(['Name']),
  Bidders: parseArray(['Bidders', 'bidders'], transformBidderWrapper),
});

function transformBidderLabelWrapperToAPI(fromObj: any): IBidderLabel {
  return transformBidderLabelToAPI(fromObj);
}

export interface ILocalization {
  ID: string;
  Name: string;
  DateFormat: string;
}

export const transformLocalization = transformModel<ILocalization>({
  ID: parseString(['ID']),
  Name: parseString(['Name', 'name']),
  DateFormat: parseString(['DateFormat', 'date_format']),
});

function transformLocalizationWrapper(fromObj: any): ILocalization {
  return transformLocalization(fromObj);
}

export function getSiteID(site?: ISite) {
  if (site) {
    return site.ID;
  }
  return -1;
}

const uploadMediafileFactory = uploadResourceFactory(`${getApiDomain()}/v1`);

export const getOAuthToken = apiFactory({
  method: 'POST',
  headers,
  body: {
    core_section: 'oauth',
    core_action: 'tokens',
  },
  transformBody: appendResellerID,
  transformResponse: Auth.saveToken,
});

export const getMasqueradeToken = apiFactory({
  method: 'POST',
  headers,
  body: {
    core_section: 'oauth',
    core_action: 'masqueradeTokens',
  },
  transformBody: [
    (body) => Auth.appendToken(body) as Record<string, string | number>,
    (body) => appendResellerID(body) as Record<string, string | number>,
  ],
  transformResponse: (err, json: Record<string, string | number>) =>
    json.redirect !== undefined ? json : MasqueradeAuth.saveToken(err, json),
});

export function retryFunc(cb: (ok: boolean) => any) {
  const token = getToken();

  if (token && token.refresh_token) {
    getOAuthToken({
      query: {
        grant_type: 'refresh_token',
        refresh_token: token.refresh_token,
      },
    }).then((json: any) => {
      if (json.error) {
        cb(false);
      } else {
        cb(true);
      }
    });
  } else {
    cb(false);
  }
}

export const getOAuthIntrospect = apiFactory({
  method: 'POST',
  headers,
  body: {
    core_section: 'oauth',
    core_action: 'introspect',
  },
  retryCount: 1,
  retryFunc,
});

export const getSession = apiFactory<{
  user: IUser;
  publisher: IPublisher;
  demandSourceNameMap: IDemandSourceNameMap;
}>({
  method: 'POST',
  headers,
  body: {
    core_section: 'session',
    core_action: 'get_session_info',
  },
  retryCount: 1,
  retryFunc,
  timeout: 60000,
  isAbortable: false,
  transformBody: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return {
        user: undefined,
        publisher: undefined,
        bidderGroups: [],
        channelGroups: [],
      };
    }

    const publisher = json.user.publisher;
    delete json.user.publisher;
    const user = transformUser(json.user);
    const bidderGroups = (publisher.BidderGroups || []).map((bidderGroup: any) => {
      delete bidderGroup.publisher;
      return transformBidderGroup(bidderGroup);
    });
    delete publisher.BidderGroups;
    const channelGroups = (publisher.ChannelGroups || []).map((channelGroup: any) => {
      delete channelGroup.publisher;
      return transformChannelGroup(channelGroup);
    });
    delete publisher.ChannelGroups;
    const demandSourceNameMap = {} as IDemandSourceNameMap;
    for (const demandSourceUIToDBName of json.DemandSourceUIToDBNames) {
      const transformed = transformDemandSourceUIToDBName(demandSourceUIToDBName);
      demandSourceNameMap[transformed.DBName] = transformed.UIName;
    }

    return {
      user,
      publisher: transformPublisher(publisher),
      bidderGroups,
      channelGroups,
      demandSourceNameMap,
    };
  },
});

export const getUsers = apiFactory<{
  users: IUser[];
  usersInvite: IUserInvite[];
}>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'users',
  },
  retryCount: 1,
  retryFunc,
  timeout: 60000,
  isAbortable: false,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return {
      users: (json.users || []).map(transformUser),
      usersInvite: (json.usersInvite || []).map(transformUserInvite),
    };
  },
});

export async function getUsersFromUserToPublisherAssociation(publisherID: string): Promise<{
  usersFromUserToPublisherAssociation: IUserFromUserToPublisherAssociation[];
  usersInvite: IUserInvite[];
}> {
  const reqUrl = createUrl(`/get_users_from_user_to_publisher_association`, publisherID);
  return await restGet<IUserFromUserToPublisherAssociation>(reqUrl).then(
    (val: IHttpResponse<any>) => {
      if (!val.parsedBody) {
        return {
          usersFromUserToPublisherAssociation: [],
          usersInvite: [],
        };
      }

      return {
        usersFromUserToPublisherAssociation: val.parsedBody.usersFromUserToPublisherAssociation.map(
          transformUsersFromUserToPublisherAssociation,
        ),
        usersInvite: val.parsedBody.usersInvite.map(transformUserInvite),
      };
    },
  );
}

export const inviteUser = apiFactory<IUser[]>({
  method: 'POST',
  headers,
  body: {
    core_section: 'signup',
    core_action: 'invite_user',
  },
  retryCount: 1,
  retryFunc,
  timeout: 60000,
  isAbortable: false,
  transformBody: [Auth.appendToken, appendResellerID],
});

export const getPublisher = apiFactory<IPublisher>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'publisher',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    const publisher = json.publisher;
    return transformPublisher(publisher);
  },
});

export const getPublisherGroup = apiFactory<IPublisher[]>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'publisher_group',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.publishers.map(transformPublisher);
  },
});

export const queryPublishers = apiFactory<IPublisher[]>({
  method: 'GET',
  headers,
  body: {
    core_section: 'publishers',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.publishers.map(transformPublisher);
  },
});

export const getAllPrebidPublishers = apiFactory<IPublisher[]>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'publishers_prebid/all',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.publishers.map(transformPublisher);
  },
});

export const sortSites = (a: ISite, b: ISite): number => {
  if (a.Name.toLowerCase() > b.Name.toLowerCase()) {
    return 1;
  }
  if (b.Name.toLowerCase() > a.Name.toLowerCase()) {
    return -1;
  }
  return 0;
};

export const getSites = apiFactory2<ISite[]>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'sites',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [Auth.appendToken, appendResellerID],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.sites.map(transformSite);
  },
});

export const getSuperSites = apiFactory<ISite[]>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'super_sites',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.sites.map(transformSite);
  },
});

export const getSite = apiFactory<ISite>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'get_site',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformSite(json.site);
  },
});

export const getPrebidSites = apiFactory<ISite[]>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'hb-sites',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.sites.map(transformSite);
  },
});

export const getFWPublisherIDs = apiFactory<number[]>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'fw_publisher_ids',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.publisherIDs;
  },
});

export async function getPublisherAdQualityStatus(
  publisherID: number,
): Promise<[boolean, boolean]> {
  const reqUrl = createUrl('/publisher_ad_quality_status', publisherID.toString());

  try {
    const ret = (await restGet<any>(reqUrl, {})).parsedBody;

    return [!!ret.hasPublisherEnabledBrandRecognition, !!ret.hasPublisherEnabledLanguageDetection];
  } catch (e) {
    console.error(e);
    return [false, false];
  }
}

export const getChannelIDs = apiFactory<number[]>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'channel_ids',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.channelIDs;
  },
});

export const querySites = apiFactory<ISite[]>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'query_sites',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.sites.map(transformSite);
  },
});

export type DateGroup = 'hour' | 'half_hour' | 'day' | 'week' | 'week_of_year' | 'month';
export const DateGroupHours: DateGroup = 'hour';
export const DateGroupHalfHour: DateGroup = 'half_hour';
export const DateGroupDays: DateGroup = 'day';
export const DateGroupWeeks: DateGroup = 'week';
export const DateGroupMonths: DateGroup = 'month';
export const DateGroupWeekOfYear: DateGroup = 'week_of_year';

export function isDateGroupHoursEnabled(
  start: number | Date | moment.Moment,
  end: number | Date | moment.Moment,
) {
  return moment(end).diff(moment(start), 'days') < 10;
}
export function isDateGroupDaysEnabled(
  start: number | Date | moment.Moment,
  end: number | Date | moment.Moment,
) {
  return moment(end).diff(moment(start), 'days') < 60;
}
export function getValidDateGroup(
  val: DateGroup,
  start: number | Date | moment.Moment,
  end: number | Date | moment.Moment,
): DateGroup {
  if (isDateGroup(val, DateGroupHours)) {
    if (isDateGroupHoursEnabled(start, end)) {
      return DateGroupHours;
    }
    return DateGroupDays;
  }
  if (isDateGroup(val, DateGroupDays)) {
    if (isDateGroupDaysEnabled(start, end)) {
      return DateGroupDays;
    }
    return DateGroupWeeks;
  }
  if (isDateGroup(val, DateGroupWeeks)) {
    return DateGroupWeeks;
  }
  if (isDateGroup(val, DateGroupMonths)) {
    return DateGroupMonths;
  }
  if (isDateGroup(val, DateGroupHalfHour)) {
    return DateGroupHalfHour;
  }
  return DateGroupDays;
}
export function isDateGroup(v: DateGroup | number, g: DateGroup) {
  return v === g || parseInt('' + v, 10) === getDateGroupNumber(g);
}
export function getDateGroupNumber(grouping: DateGroup | number): number {
  if (isDateGroup(grouping, DateGroupHours)) {
    return 0;
  }
  if (isDateGroup(grouping, DateGroupWeeks)) {
    return 2;
  }
  if (isDateGroup(grouping, DateGroupMonths)) {
    return 3;
  }
  if (isDateGroup(grouping, DateGroupWeekOfYear)) {
    return 4;
  }
  if (isDateGroup(grouping, DateGroupHalfHour)) {
    return 5;
  }
  return 1;
}

export const queryAnalytics = apiFactory<IAnalytics[]>({
  method: 'POST',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'discovery',
    action_id: 'get_data',
  },
  timeout: 86400000,
  retryCount: 1,
  retryFunc,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      query.grouping = getDateGroupNumber(query.grouping);
      return query;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    if (json.data === null) {
      return [];
    }
    return json.data.map(transformAnalytics);
  },
});

export const queryAnalyticsWithSize = apiFactory<[IAnalytics[], number, string]>({
  method: 'POST',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'discovery',
    action_id: 'get_data',
  },
  timeout: 86400000,
  retryCount: 1,
  retryFunc,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      query.grouping = getDateGroupNumber(query.grouping);
      return query;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    const rawQuery = json?.RawQuery;
    if (json.data === null) {
      return [[], 0, rawQuery];
    }
    return [json.data.map(transformAnalytics), JSON.stringify(json).length, rawQuery];
  },
});

export const queryAnalyticsWithSizeV2 = apiFactory<[IAnalytics[], number, string]>({
  method: 'POST',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'discovery',
    action_id: 'preview_report',
  },
  timeout: 86400000,
  retryCount: 1,
  retryFunc,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      query.grouping = getDateGroupNumber(query.grouping);
      return query;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    const rawQuery = json?.RawQuery;
    if (json.data === null) {
      return [[], 0, rawQuery];
    }
    return [json.data.map(transformAnalytics), JSON.stringify(json).length, rawQuery];
  },
});

export const getLastUpdateTimestampByAuctions = apiFactory<{
  LastUpdateTimestampByAuctions: number;
}>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'last_update_timestamp_by_auctions',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getLastUpdateTimestampByBuyers = apiFactory<{
  LastUpdateTimestampByBuyers: number;
}>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'last_update_timestamp_by_buyers',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getLastUpdateTimestampByAuctionsFillRate = apiFactory<{
  LastUpdateTimestampByAuctionsFillRate: number;
}>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'last_update_timestamp_by_auctions_fill_rate',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getLastUpdateTimestampByUniqueUserReport = apiFactory<{
  LastUpdateTimestamp: number;
}>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'last_update_timestamp_by_unique_user_report',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getLastUpdateTimestampBySSAI = apiFactory<{
  LastUpdateTimestamp: number;
}>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'last_update_timestamp_by_ssai',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getLastUpdateTimestampByCampaign = apiFactory<{
  LastUpdateTimestampByCampaign: number;
}>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'last_update_timestamp_by_campaign',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getLastUpdateTimestampBySegments = apiFactory<{
  LastUpdateTimestampBySegments: number;
}>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'last_update_timestamp_by_segments',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getLastUpdateTimestampByBrandSafetyTrackerReport = apiFactory<{
  LastUpdateTimestamp: number;
}>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'last_update_timestamp_by_brand_safety_tracker',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getLastUpdateTimestampByDeals = apiFactory<{
  LastUpdateTimestamp: number;
}>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'last_update_timestamp_by_deals',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getLastUpdateTimestampByEleaAIScanAll = apiFactory<{
  LastUpdateTimestamp: number;
}>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'last_update_timestamp_by_recognition_scan_all',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getLastUpdateTimestampByEleaAIEnrichment = apiFactory<{
  LastUpdateTimestamp: number;
}>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'last_update_timestamp_by_recognition_enrichment',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getLastUpdateTimestampByContextualSegments = apiFactory<{
  LastUpdateTimestamp: number;
}>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'last_update_timestamp_by_contextual_segments',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getLastUpdateTimestampByShareOfVoice = apiFactory<{
  LastUpdateTimestamp: number;
}>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'last_update_timestamp_by_share_of_voice',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getLastUpdateTimestampByPodHourly = apiFactory<{
  LastUpdateTimestamp: number;
}>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'last_update_timestamp_by_pod_hourly',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getLastUpdateTimestampBuyersAPS = apiFactory<{
  LastUpdateTimestamp: number;
}>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'last_update_timestamp_by_buyers_aps',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getLastUpdateTimestampByPublicaDeals = apiFactory<{
  LastUpdateTimestamp: number;
}>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'last_update_timestamp_by_publica_deals',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getLastUpdateTimestampByInventoryRoutes = apiFactory<{
  LastUpdateTimestamp: number;
}>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'last_update_timestamp_by_inventory_routes',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const queryReports = apiFactory<IReport[]>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'reports',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.reports.map(transformReportWrapper);
  },
});

export const querySSPReports = apiFactory<IReport[]>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'ssp-reports',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.reports.map(transformReport);
  },
});

export const createReport = apiFactory<IReport>({
  method: 'POST',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'report',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      query.report = transformReportToAPI(query.report);
      return query;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformReportWrapper(json.report);
  },
});

export const updateReport = apiFactory<IReport>({
  method: 'PATCH',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'report',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      query.report = transformReportToAPI(query.report);
      return query;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformReportWrapper(json.report);
  },
});

export const deleteReport = apiFactory<IReport>({
  method: 'DELETE',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'report',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getReport = apiFactory<IReport>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'report',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformReportWrapper(json.report);
  },
});

export const queryHeaderBidders = apiFactory({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'bidders',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.headerbidders
      .map((hb: IHeaderBidder) => {
        return fromAPIHeaderBidder(hb);
      })
      .sort(sortHeaderBidders);
  },
});

export const getHeaderBidder = apiFactory({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'bidder',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && (json.msg || json.error)) || 'request failed');
    }
    return fromAPIHeaderBidder(json.headerbidder);
  },
});

export async function createHeaderBidder(
  headerBidder: IHeaderBidder,
  publisherID: number,
  siteIDS: string[],
  campaignIDS: string[],
  orderIDS: string[],
  channelGroupIDS: string[],
  countryGroupIDs: string[],
  contextualSegmentIDS: string[],
  channelListIDS: string[],
  presetIDs: string[],
): Promise<[IHeaderBidder?, Error?]> {
  let reqUrl = createUrl('/bidder', publisherID.toString());
  if (siteIDS.length > 0) {
    reqUrl += '&site_ids=' + siteIDS.join(',');
  }
  if (campaignIDS.length > 0) {
    reqUrl += '&campaign_ids=' + campaignIDS.join(',');
  }
  if (orderIDS.length > 0) {
    reqUrl += '&order_ids=' + orderIDS.join(',');
  }
  if (channelGroupIDS.length > 0) {
    reqUrl += '&channel_group_ids=' + channelGroupIDS.join(',');
  }
  if (channelListIDS.length > 0) {
    reqUrl += '&channel_list_ids=' + channelListIDS.join(',');
  }
  if (countryGroupIDs.length > 0) {
    reqUrl += '&country_group_ids=' + countryGroupIDs.join(',');
  }
  if (contextualSegmentIDS.length > 0) {
    reqUrl += '&contextual_segment_ids=' + contextualSegmentIDS.join(',');
  }
  if (presetIDs.length > 0) {
    reqUrl += '&preset_ids=' + presetIDs.join(',');
  }

  const parsedHB = toAPIHeaderBidder(headerBidder);
  // campaign ids will be sent in header
  // @ts-ignore
  delete parsedHB.CampaignIDS;
  // @ts-ignore
  delete parsedHB.OrderIDS;

  try {
    const ret = (await restPost<IHeaderBidder>(reqUrl, parsedHB)).parsedBody;
    return [fromAPIHeaderBidder(ret), undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export const updateHeaderBidder = apiFactory({
  method: 'PATCH',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'bidder',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformBody: [
    (body: any): any => {
      if (!body.ignore_missing_fields) {
        body.headerbidder = toAPIHeaderBidder(body.headerbidder);
      }
      return body;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return fromAPIHeaderBidder(json.headerbidder);
  },
});

export const deleteHeaderBidder = apiFactory({
  method: 'DELETE',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'bidder',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getHeaderBidderParamOptions = apiFactory({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'bidder_params',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any): Record<string, IHeaderBidderParamOption[]> | Error => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    Object.keys(json.headerbidderParams).forEach((key: string) => {
      json.headerbidderParams[key] = json.headerbidderParams[key].map(
        (option: IHeaderBidderParamOption) => {
          return transformHeaderBidderParamOptions(option);
        },
      );
    });
    return json.headerbidderParams;
  },
});

export async function createABTest(
  test: IABTesting,
  publisherID: string,
  bidderID: string,
): Promise<[IABTesting?, Error?]> {
  let reqUrl = createUrl('/ab_testing', publisherID);
  reqUrl += '&bidder_id=' + bidderID;

  try {
    const ret = (await restPost<IABTesting>(reqUrl, test)).parsedBody;

    return [transformABTesting(ret), undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export const disableABTest = apiFactory({
  method: 'DELETE',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'ab_testing/active',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json;
  },
});

export const sendEmail = apiFactory({
  method: 'POST',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'discovery',
    action_id: 'email_report',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const updatePriceIncrements = apiFactory({
  method: 'PATCH',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'site_price_increments',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      query.priceincrements = query.priceincrements.map((item: any) => {
        return transformHeaderBidderPriceIncrement(item);
      });
      return query;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.priceincrements.map((item: any) => {
      return transformHeaderBidderPriceIncrement(item);
    });
  },
});

export const updateBypassDedupConfig = apiFactory({
  method: 'PATCH',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'site_bypass_dedup_config',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      query.bypass_dedup_configs = query.bypass_dedup_configs.map((item: any) => {
        return transformHeaderBypassDedupConfig(item);
      });
      return query;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.BypassDedupConfigs.map((item: any) => {
      return transformHeaderBypassDedupConfig(item);
    });
  },
});

interface IBodySite {
  site: ISite;
  core_section: string;
  core_action: string;
}

interface ISiteBody {
  site: ISiteTransformed;
}

interface ISiteTransformed
  extends Omit<
    ISite,
    | 'AppNameOverride'
    | 'AppBundleOverride'
    | 'AppIDOverride'
    | 'AppDomainOverride'
    | 'SiteDomainOverride'
    | 'SiteNameOverride'
  > {
  AppNameOverride: string;
  AppBundleOverride: string;
  AppIDOverride: string;
  AppDomainOverride: string;
  SiteDomainOverride: string;
  SiteNameOverride: string;
}

export const updateSite = apiFactory({
  method: 'POST',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'update_site',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      return query;
    },
  ],
  transformBody: [
    (body: IBodySite): ISiteBody => {
      const transformedSite: ISiteTransformed = {
        ...body.site,
        AppNameOverride: JSON.stringify(body.site.AppNameOverride),
        AppBundleOverride: JSON.stringify(body.site.AppBundleOverride),
        AppIDOverride: JSON.stringify(body.site.AppIDOverride),
        AppDomainOverride: JSON.stringify(body.site.AppDomainOverride),
        SiteDomainOverride: JSON.stringify(body.site.SiteDomainOverride),
        SiteNameOverride: JSON.stringify(body.site.SiteNameOverride),
      };
      return {
        ...body,
        site: transformedSite,
      };
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformSite(json.site);
  },
});

export const updateSiteEndpointTimeout = apiFactory({
  method: 'PATCH',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'site_endpoint_timeout',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const updatePublisher = apiFactory({
  method: 'POST',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'update_publisher',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      if (query.publisher.IASDataSharing && Array.isArray(query.publisher.IASDataSharing)) {
        query.publisher.IASDataSharing = query.publisher.IASDataSharing.sort().join(',');
      }
      if (
        query.publisher.DoubleVerifyDataSharing &&
        Array.isArray(query.publisher.DoubleVerifyDataSharing)
      ) {
        query.publisher.DoubleVerifyDataSharing =
          query.publisher.DoubleVerifyDataSharing.sort().join(',');
      }
      return query;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return {
      publisher: transformPublisher(json.publisher),
    };
  },
});

export async function updatePublishersActive(
  PublishersToDeactivate: number[],
  PublishersToActivate: number[],
): Promise<IHttpResponse<{ status: boolean }>> {
  const url = addDomainAndTokenToUrl(`/update_publishers_active`);
  return await restPatch<{ status: boolean }>(url, {
    PublishersToDeactivate,
    PublishersToActivate,
  });
}

export const recoverPassword = apiFactory({
  method: 'POST',
  headers,
  body: {
    core_section: 'passwordrecovery',
    core_action: 'newrecovery',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: appendResellerID,
});

export const updatePasswordWithToken = apiFactory({
  method: 'POST',
  headers,
  body: {
    core_section: 'passwordrecovery',
    core_action: 'updatepassword',
  },
  transformQuery: appendResellerID,
  retryCount: 1,
  retryFunc,
});

export const updatePassword = apiFactory({
  method: 'POST',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'update_password',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [Auth.appendToken],
});

export interface IEndpointParams {
  Type: string;
  IntegrationType: string;
  AdServer: string;
  AdBreakType: string;
  PodDuration: string[];
  Params: string[];
  EndpointMap: { [key: string]: string };
}

export const signupUser = apiFactory<{
  user: IUser;
  publisher: IPublisher;
  sites: ISite[];
}>({
  method: 'POST',
  headers,
  body: {
    core_section: 'signup',
    core_action: 'create_user',
  },
  retryCount: 0,
  timeout: 60000,
  isAbortable: false,
  transformBody: [
    Auth.appendToken,
    appendResellerID,
    (query: any): any => {
      const q: { [k: string]: any } = {};
      Object.entries(query).forEach(([k, v]: [string, any]) => {
        if (v !== undefined && v !== null) {
          q[k] = v;
        }
      });
      return q;
    },
  ],
});

export const signupUserManualResellerID = apiFactory<{
  user: IUser;
  publisher: IPublisher;
  sites: ISite[];
}>({
  method: 'POST',
  headers,
  body: {
    core_section: 'signup',
    core_action: 'create_user',
  },
  retryCount: 0,
  timeout: 60000,
  isAbortable: false,
  transformBody: [
    Auth.appendToken,
    (query: any): any => {
      const q: { [k: string]: any } = {};
      Object.entries(query).forEach(([k, v]: [string, any]) => {
        if (v !== undefined && v !== null) {
          q[k] = v;
        }
      });
      return q;
    },
  ],
});

export const signupUserWithToken = apiFactory<{
  user: IUser;
  publisher: IPublisher;
  sites: ISite[];
}>({
  method: 'POST',
  headers,
  body: {
    core_section: 'signup',
    core_action: 'create_user_with_token',
  },
  retryCount: 0,
  timeout: 60000,
  isAbortable: false,
  transformBody: [
    Auth.appendToken,
    appendResellerID,
    (query: any): any => {
      const q: { [k: string]: any } = {};
      Object.entries(query).forEach(([k, v]: [string, any]) => {
        if (v !== undefined && v !== null) {
          q[k] = v;
        }
      });
      return q;
    },
  ],
});

export const createSite = apiFactory<{ site: ISite }>({
  method: 'POST',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'create_site',
  },
  retryCount: 0,
  transformBody: [
    Auth.appendToken,
    (query: any): any => {
      const q: { [k: string]: any } = {};
      Object.entries(query).forEach(([k, v]: [string, any]) => {
        if (v !== undefined && v !== null) {
          q[k] = v;
        }
      });
      return q;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return {
      site: transformSite(json.site),
    };
  },
});

export const deleteSite = apiFactory<{}>({
  method: 'DELETE',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'site',
  },
  retryCount: 0,
  transformQuery: [Auth.appendToken],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json;
  },
});

export const cloneSite = apiFactory<{ site: ISite }>({
  method: 'POST',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'clone_site',
  },
  retryCount: 0,
  transformBody: [
    Auth.appendToken,
    (query: any): any => {
      const q: { [k: string]: any } = {};
      Object.entries(query).forEach(([k, v]: [string, any]) => {
        if (v !== undefined && v !== null) {
          q[k] = v;
        }
      });
      return q;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return {
      site: transformSite(json.site),
    };
  },
});

export const getCampaigns = apiFactory<IAdserverCampaign[]>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'campaigns',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.campaigns.map(transformCampaign);
  },
});

export const getCampaign = apiFactory<IAdserverCampaign>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'campaign',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformCampaign(json.campaign);
  },
});

export interface IAdserverAlias {
  Name: string;
}

export async function getUniqueLineItemAliases(publisherID: string): Promise<IAdserverAlias[]> {
  const reqUrl = addDomainAndTokenToUrl(`/publishers/${publisherID}/campaigns_unique_aliases`);
  return await restGet<any>(reqUrl, {}).then((val: IHttpResponse<any>) => {
    const uniqueLineItemAliases = [];
    if (val.parsedBody) {
      for (const item of val.parsedBody) {
        uniqueLineItemAliases.push({ Name: item });
      }
    }
    return uniqueLineItemAliases;
  });
}

export const createCampaign = apiFactory<IAdserverCampaign>({
  method: 'POST',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'campaign',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformBody: [
    (body: any): any => {
      // CRONQuantityCount should not be updated from ui
      delete body.campaign.CRONQuantityCount;
      body.campaign = marshalAdserverCampaign(body.campaign);
      return body;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformCampaign(json.campaign);
  },
});

export const createCreative = apiFactory<IAdserverCreative>({
  method: 'POST',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'creative',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      query.creative = marshalAdserverCreativeWrapper(query.creative);
      return query;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformAdserverCreativeWrapper(json.creative);
  },
});

export async function deleteCreativesWithGlobalVastID(
  creativeIDs: string,
  globalVastID: string,
  publisherID: string,
): Promise<IHttpResponse<IAdserverCreative>> {
  const reqUrl =
    addDomainAndTokenToUrl(`/publishers/${publisherID}/delete_creatives_with_global_vast_id`) +
    `&creative_ids=${creativeIDs}&global_vast_id=${globalVastID}`;
  return await restDelete<IAdserverCreative>(reqUrl, undefined);
}

export const updateCampaign = apiFactory<IAdserverCampaign>({
  method: 'PATCH',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'campaign',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformBody: [
    (body: any): any => {
      // CRONQuantityCount should not be updated from ui
      delete body.campaign.CRONQuantityCount;
      body.campaign = marshalAdserverCampaign(body.campaign);
      return body;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformCampaign(json.campaign);
  },
});

export const deleteCampaign = apiFactory<{ success: boolean }>({
  method: 'DELETE',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'campaign',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json;
  },
});

export const addGlobalMediafilesToLineItems = apiFactory<{ success: boolean }>({
  method: 'POST',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'add_global_mediafiles_to_line_items',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json;
  },
});

export const getGlobalMedia = apiFactory<IAdserverGlobalMediafile[]>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'global_media',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.map((file: any) => transformAdserverGlobalMediafileWrapper(file));
  },
});

export const updateGlobalMedia = apiFactory<IAdserverGlobalMediafile>({
  method: 'PATCH',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'global_media',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      return query;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformAdserverGlobalMediafileWrapper(json);
  },
});

export const deleteGlobalMedia = apiFactory<{ success: boolean }>({
  method: 'DELETE',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'global_media',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json;
  },
});

export const deleteGlobalMediaToCreativeAssociation = apiFactory<{
  success: boolean;
}>({
  method: 'DELETE',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'global_mediafile_to_creative_association',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json;
  },
});

export const updateVariantMediafile = apiFactory<IVariantMediafile>({
  method: 'PATCH',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'variant_mediafile',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      return query;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformVariantMediafile(json);
  },
});

export const createMediafileFromURL = apiFactory<IAdserverGlobalMediafile>({
  method: 'POST',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'global_media_url',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      return query;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.error) || 'request failed');
    }
    return transformAdserverGlobalMediafileWrapper(json);
  },
});

export const uploadGlobalMediafile = uploadMediafileFactory('settings/global_media');

export const uploadGlobalBanner = uploadMediafileFactory('settings/global_banner');

export const uploadBulkuploaderCSVfile = uploadMediafileFactory('settings/upload_bulkupload_csv');

export const uploadHeaderbidderCSVfile = uploadMediafileFactory('settings/upload_bulkupload_csv');

export const uploadMonitorCSVfile = uploadMediafileFactory('settings/upload_monitor_csv');

export const uploadSiteAdomainCSVfile = uploadMediafileFactory('settings/upload_site_adomain_csv');

export const uploadNetworkSlatefile = uploadMediafileFactory('settings/network/slate_media');

export const uploadSSAIHouseAd = uploadMediafileFactory('settings/ssai_house_ad');

export const uploadSSAIPadderAd = uploadMediafileFactory('settings/ssai_padder_ad');

export const uploadVastCSVfile = uploadMediafileFactory('settings/upload_vast_csv');

export const uploadAPSCSVfile = uploadMediafileFactory('settings/upload_aps_csv');

export const uploadDOOHCSVfile = uploadMediafileFactory('settings/upload_dooh_csv');

export const uploadCollectionDataCSVfile = uploadMediafileFactory(
  'settings/upload_collection_data_csv',
);

export const uploadEPGChannelCSVfile = uploadMediafileFactory('settings/upload_epg_channel_csv');

export const uploadGlobalAppBundleBlockListfile = uploadMediafileFactory(
  'settings/upload_global_app_bundle_block_list_csv',
);

export function genExportCampaignWithCampaignID(siteID: string, campaignID: string): string {
  return `${getPbsUS()}/direct?site_id=${siteID}&campaign_id=${campaignID}&ip=[%IP%]&did=[%DID%]&ua=[%UA%]&app_bundle=[%APP_BUNDLE%]&app_name=[%APP_NAME%]&app_store_url=[%APP_STORE_URL%]&content_cat=[%CONTENT_CAT%]&content_id=[%CONTENT_ID%]&content_title=[%CONTENT_TITLE%]&content_genre=[%CONTENT_GENRE%]&content_series=[%CONTENT_SERIES%]&cb=[CACHEBUSTER]`;
}

export function genExportCampaignWithoutCampaignID(siteID: string): string {
  return `${getPbsUS()}/direct?site_id=${siteID}&ip=[%IP%]&did=[%DID%]&ua=[%UA%]&app_bundle=[%APP_BUNDLE%]&app_name=[%APP_NAME%]&app_store_url=[%APP_STORE_URL%]&content_cat=[%CONTENT_CAT%]&content_id=[%CONTENT_ID%]&content_title=[%CONTENT_TITLE%]&content_genre=[%CONTENT_GENRE%]&content_series=[%CONTENT_SERIES%]&cb=[CACHEBUSTER]`;
}

export async function checkForecastV2(
  forecastBody: { [key: string]: any },
  publisherID: string,
): Promise<IForecast> {
  const reqUrl = createAnalyticsUrl('discovery/get_new_forecast_v2', publisherID);
  return await restPost<IForecast>(reqUrl, forecastBody).then((val: IHttpResponse<any>) => {
    return transformForecast(val.parsedBody);
  });
}

export async function runContextualSegmentsForecast(
  forecastBody: { [key: string]: any },
  publisherID: string,
): Promise<[IContextualSegmentsForecast?, Error?]> {
  const requestUrl = createAnalyticsUrl('discovery/get_contextual_segments_forecast', publisherID);
  try {
    const response = (await restPost<IContextualSegmentsForecast>(requestUrl, forecastBody))
      .parsedBody;
    return [transformContextualSegmentsForecast(response), undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export async function scheduleContextualSegmentsForecast(
  forecastBody: { [key: string]: any },
  publisherID: string,
): Promise<[IContextualSegmentsForecast?, Error?]> {
  const requestUrl = createUrl('/email_contextual_segments_forecast', publisherID);
  try {
    const response = (await restPost<IContextualSegmentsForecast>(requestUrl, forecastBody))
      .parsedBody;
    return [transformContextualSegmentsForecast(response), undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export async function scheduleCampaignForecastV2(
  forecastBody: {
    [key: string]: any;
  },
  publisherID: string,
): Promise<IForecast> {
  const reqUrl =
    addDomainAndTokenToUrl('/email_campaign_forecast_v2') + '&publisher_id=' + publisherID;
  return await restPost<IForecast>(reqUrl, forecastBody).then((val: IHttpResponse<any>) => {
    return transformForecast(val.parsedBody);
  });
}

export const checkSegmentForecast = apiFactory<ISegmentForecast>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'discovery/get_segment_forecast',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformSegmentForecast(json);
  },
});

export const runNewSegmentForecast = apiFactory<ISegmentForecast>({
  method: 'GET',
  headers,
  body: {
    core_section: 'analytics',
    core_action: 'discovery/get_segment_forecast_new',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformSegmentForecast(json);
  },
});

export const scheduleSegmentForecast = apiFactory({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'email_segment_forecast',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const genHLSURL = apiFactory<IAudienceSegment[]>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'hls_url',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export const getAudienceSegments = apiFactory<IAudienceSegment[]>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'segments',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.segments.map(transformAudienceSegment);
  },
});

export const getAudienceSegment = apiFactory<IAudienceSegment>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'segment',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformAudienceSegment(json.segment);
  },
});

// export const getActiveAudienceSegments = apiFactory<IAudienceSegment[]>({
//   method: 'GET',
//   headers,
//   body: {
//     core_section: 'settings',
//     core_action: 'get_active_segments',
//   },
//   retryCount: 1,
//   retryFunc,
//   transformQuery: Auth.appendToken,
//   transformResponse: (err: any, json: any) => {
//     if (err) {
//       return new Error((json && json.msg) || 'request failed');
//     }
//     return json.segments.map(transformAudienceSegment);
//   },
// });

export const createAudienceSegment = apiFactory<IAudienceSegment>({
  method: 'POST',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'segment',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      query.segment = transformAudienceSegment(query.segment);
      return query;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformAudienceSegment(json.segment);
  },
});

export const updateAudienceSegment = apiFactory<IAudienceSegment>({
  method: 'PATCH',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'segment',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformBody: [
    (body: any): any => {
      body.segment = transformAudienceSegment(body.segment);
      return body;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformAudienceSegment(json.segment);
  },
});

export const deleteAudienceSegment = apiFactory<{ success: boolean }>({
  method: 'DELETE',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'segment',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json;
  },
});

export const uploadSegmentMediafile = uploadMediafileFactory('settings/segment/csv');
export const uploadIABCategoryFile = uploadMediafileFactory('settings/iab-category/csv');

export const updateDeals = apiFactory<IAudienceDeal[]>({
  method: 'PATCH',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'segment/deals',
  },
  retryCount: 1,
  retryFunc,
  transformBody: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.deals.map(transformAudienceDeal);
  },
});

export interface IReseller {
  ID: string;
  UUID: string;
  Name: string;
  LogoURL: string;
  SSP: string;
}

export const transformReseller = transformModel<IReseller>({
  ID: parseString(['ID', 'id']),
  UUID: parseString(['UUID', 'uuid']),
  Name: parseString(['Name', 'name']),
  LogoURL: parseString(['LogoURL', 'logo_url']),
  SSP: parseString(['SSP', 'ssp']),
});

function transformResellerWrapper(fromObj: any): IReseller {
  return transformReseller(fromObj);
}

export const createReseller = apiFactory<IReseller>({
  method: 'POST',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'reseller',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      query.reseller = transformReseller(query.reseller);
      return query;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformReseller(json.reseller);
  },
});

export const updateReseller = apiFactory<IReseller>({
  method: 'PATCH',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'reseller',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [Auth.appendToken, appendResellerID],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformReseller(json.reseller);
  },
});

export const getResellers = apiFactory<IReseller[]>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'resellers',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.resellers.map(transformReseller);
  },
});

export const getReseller = apiFactory<IReseller>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'reseller',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [Auth.appendToken, appendResellerID],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformReseller(json.reseller);
  },
});

export const getResellerByDomain = apiFactory<IReseller>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'reseller_by_domain',
  },
  retryCount: 1,
  retryFunc,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return transformReseller(json.reseller);
  },
});

export const deleteReseller = apiFactory<IReseller>({
  method: 'DELETE',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'reseller',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [Auth.appendToken, appendResellerID],
});

export const transformUINotice = transformModel<IUINotice>({
  ID: parseString(['ID']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',

  Name: parseString(['Name']),
  Description: parseString(['Description']),
  Detail: parseString(['Detail']),
  Active: parseString(['Active']),
});

export interface IUINotice {
  ID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  Name: string;
  Description: string;
  Detail: string;
  Active: UINoticeActive;
}

export type UINoticeActive = string;
export const UINoticeActiveUnkown: UINoticeActive = '0';
export const UINoticeActiveFalse: UINoticeActive = '1';
export const UINoticeActiveTrue: UINoticeActive = '2';

export const getUINotices = apiFactory<IUINotice[]>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'ui_notices',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json.ui_notices.map((uiNotice: any) => {
      return transformUINotice(uiNotice);
    });
  },
});

// Update the user's timezone (PUT /timezone/user)
export async function updateTimezoneForUser(
  timezone: string,
  userID: number,
  publisherID: number,
): Promise<[IRole | undefined, string | undefined]> {
  const reqUrl = createUrl('/timezone/user', publisherID.toString());
  try {
    return [
      (
        await restPut<IRole>(reqUrl, {
          timezone,
          userID,
        })
      ).parsedBody as IRole | undefined,
      undefined,
    ];
  } catch (e) {
    return [undefined, e];
  }
}

export interface IRealtimeAnalytics {
  Date: string;
  DateExtended?: string;
  DateTooltip?: string;
  Impressions: number;
  ImpressionsUnpaid: number;
  ImpressionAdDuration: number;
  ImpressionAdDurationUnpaid: number;
  EndpointRequests: number;
  EndpointResponses: number;
  EventAuctionInits: number;
  GlobalMediafileID: number;
  GlobalVastID: number;
  Revenue: number;
  WinningBids: number;
  CPM: number;
  CPMUnpaid: number;
  CPMPerSecond: number;
  EndpointRequestECPM: number;
  EndpointLatency: number;
  AuctionLatency: number;
  BidderLatency: number;
  RenderRate: number;
  BidResponseRate: number;
  FillRate: number;
  ClearRateByPodDuration: number;
  MaxAvailablePodDurationByTier: number;
  RenderedPodFillRate: number;
  PodFillRate: number;
  PodClearRate: number;
  PaidPodClearRate: number;
  AuctionEnd: number;
  BidResponses: number;
  BidRequests: number;
  BidError: number;
  BidErrorRate: number;
  BidTimeout: number;
  BidTimeoutsRate: number;
  BidWons: number;
  FilledPodDuration: number;
  PaidFilledPodDuration: number;
  RenderedRequestedPodDuration: number;
  RequestedPodDuration: number;
  DeliveredPodDuration: number;
  // Native Ads
  NativeCompanionClick: number;
  NativeCreativeView: number;
  NativeVideoClick: number;
  // stream
  StreamAdFilled?: number;
  StreamPodRequest?: number;
  StreamPodFilled?: number;
  StreamAdImpression?: number;
  StreamAdComplete?: number;
  StreamAdFetchTimeout?: number;
  StreamChunklistFetch?: number;
  StreamChunklistResponse?: number;
  StreamError?: number;
  StreamPlaylistTimeout?: number;
  StreamStart?: number;
  StreamAdChunklistFetch?: number;
  StreamAdChunklistResponse?: number;
  StreamAdFilledLatency?: number;
  StreamFetchTimeout?: number;
  StreamChunklistTimeout?: number;
  StreamTranscodeMiss?: number;
  PodCompleteRate?: number;
  StreamTranscodeMissError?: number;
  StreamCachedAdMiss?: number;
  StreamAdsFetchError?: number;
  StreamPlaylistError?: number;
  StreamChunklistError?: number;
  StreamCacheReadMissError?: number;
  // campaign
  AdserverRequest?: number;
  AdserverInventoryRequest?: number;
  AdserverFilled?: number;
  AdserverImpression?: number;
  AdserverComplete?: number;
  AdserverImpressionTest?: number;
  AdserverPacedRequestTest?: number;
  AdserverIdealImpressionCount?: number;
  // raw data
  EndpointLatencyRaw?: number;
  AuctionLatencyRaw?: number;
  BidderLatencyRaw?: number;
  CPMRaw: number;
  // Inventory Routing
  DestinationErrors?: number;
  DestinationImpressions?: number;
  DestinationLatency?: number;
  DestinationNoFills?: number;
  DestinationRequests?: number;
  DestinationResponses?: number;
  DestinationTimeouts?: number;
  DestinationWins?: number;
  InventoryRouteEndpointLatency: number;
  InventoryRouteErrors: number;
  InventoryRouteNoFills: number;
  InventoryRouteRequests: number;
  InventoryRouteResponses: number;

  ABTestName?: string;

  // Automated Bid Floor
  BidFloorControl: number;
  BidFloorOptimized: number;
  BidFloorLow: number;
  BidFloorHigh: number;
  WinningBidsControl: number;
  WinningBidsOptimized: number;
  WinningBidsLow: number;
  WinningBidsHigh: number;
  CPMControl: number;
  CPMOptimized: number;
  CPMLow: number;
  CPMHigh: number;
  BidResponsesControl: number;
  BidResponsesOptimized: number;
  BidResponsesLow: number;
  BidResponsesHigh: number;
  BidRequestsControl: number;
  BidRequestsOptimized: number;
  BidRequestsLow: number;
  BidRequestsHigh: number;
  EstimatedRevenueControl: number;
  EstimatedRevenueOptimized: number;
  EstimatedRevenueLow: number;
  EstimatedRevenueHigh: number;

  // allows for stacked bar charts to add realtimeAnalytic keys with name and item id
  [key: string]: number | string | undefined;
}

export interface IRealtimeAnalyticsData {
  Dimensions: IDimensions;
}

export interface IDimensions {
  [key: string]: IItems;
}

export interface IItems {
  [key: string]: IItem;
}

export interface IItem {
  metrics: IItemMetrics;
}

export interface IItemMetrics {
  [key: string]: IItemMetricsData;
}

export interface IItemMetricsData {
  data: number[] | null;
}

export const getChannelLabels = apiFactory<{ channel_labels: IChannelLabel[] }>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'channel_label',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    const channel_labels = json ? json.map(transformChannelLabel) : [];

    return {
      channel_labels,
    };
  },
});

export const getBidderLabels = apiFactory<{ bidder_labels: IBidderLabel[] }>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'bidder_label',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    const bidder_labels = json ? json.map(transformBidderLabelFromAPI) : [];

    return {
      bidder_labels,
    };
  },
});

export const getLocalizations = apiFactory<{ localizations: ILocalization[] }>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'localization',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    const localizations = json.map(transformLocalization);

    return {
      localizations,
    };
  },
});

export const updatePublisherLocalization = apiFactory<IPublisher>({
  method: 'PATCH',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'publisher_localization',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return {
      publisher: transformPublisher(json),
    };
  },
});

export const deleteUserInvite = apiFactory({
  method: 'DELETE',
  headers,
  body: {
    core_section: 'signup',
    core_action: 'user_invites',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
});

export async function bulkUpdateBidders(
  bidderIDs: string,
  publisherID: string,
  updates: { [key: string]: any },
  keys: string,
): Promise<any> {
  const url =
    addDomainAndTokenToUrl(`/publishers/${publisherID}/bulk-update-bidders`) +
    `&bidder_ids=${bidderIDs}&keys=${keys}`;
  return await restPatch(url, toAPIHeaderBidder(updates));
}

export const bulkUpdateChannel = apiFactory({
  method: 'POST',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'bulk_update_channel',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [Auth.appendToken],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    if (!json || json.success !== true) {
      return new Error((json && json.error) || 'bad response');
    }
    return true; // success
  },
});

export const bulkUpdateChannelGroup = apiFactory({
  method: 'PATCH',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'bulk_update_channel_group',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [Auth.appendToken],
  transformResponse: (err: any, json: any) => {
    if (err) {
      json = { ...(json || {}), err };
    }
    return json;
  },
});

// route like https://api.getpublica.com/v1/settings{route}
export async function v1SettingsPost(
  route: string,
  body: { [key: string]: any },
  queryParams: { [key: string]: any },
): Promise<[any?, Error?]> {
  const requestURL = addDomainAndTokenToUrl(route);
  const requestURLWithParams = addQueryParams(requestURL, queryParams);
  try {
    const response = await restPost<any>(requestURLWithParams, body);
    return [response.parsedBody, undefined];
  } catch (err) {
    console.error(err);
    return [undefined, err];
  }
}

export interface IAIAssistantSuccessResponse {
  success: true;
  rowCount: number;
  data: string | Array<{ [key: string]: string }>;
  query: string;
}

export interface IAIAssistantErrorResponse {
  success: false;
  rowCount: 0;
  error: string;
}

export type IAIAssistantResponse = IAIAssistantSuccessResponse | IAIAssistantErrorResponse;

export async function aiAssistantQuery(
  question: string,
  publisherId: number,
): Promise<[IAIAssistantResponse?, Error?]> {
  const requestURL = addDomainAndTokenToUrl('/aiassistant');
  try {
    const response = await restPost<IAIAssistantResponse>(requestURL, {
      question,
      publisher_id: publisherId,
    });
    return [response.parsedBody, undefined];
  } catch (err) {
    console.error(err);
    return [undefined, err];
  }
}

export const transformSAMLIdentityProvider = transformModel<ISAMLIdentityProvider>({
  ID: parseString(['ID', 'id']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',
  PublisherID: parseString(['PublisherID', 'publisherid']),
  DisplayName: parseString(['DisplayName', 'displayname']),
  IdentityProviderSSOURL: parseString(['IdentityProviderSSOURL', 'identityproviderssourl']),
  IdentityProviderIssuer: parseString(['IdentityProviderIssuer', 'identityproviderissuer']),
  Certificate: parseString(['Certificate', 'certificate']),
  Autoprovision: parseNumber(['Autoprovision', 'autoprovision']),
  PermissionsRoleID: parseNumber(['PermissionsRoleID', 'permissionsroleid']),
  UUID: parseString(['UUID', 'uuid']),
  Enabled: parseNumber(['Enabled', 'enabled']),
});

export interface ISAMLIdentityProvider {
  ID: string;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;
  PublisherID: string;
  DisplayName: string;
  IdentityProviderSSOURL: string;
  IdentityProviderIssuer: string;
  Certificate: string;
  Autoprovision: EBool;
  PermissionsRoleID: number;
  UUID: string;
  Enabled: EBool;
}

export const createSAMLIdentityProvider = apiFactory({
  method: 'POST',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'create_saml_identity_provider',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      return query;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return {
      login_method: transformSAMLIdentityProvider(json),
    };
  },
});

export const deleteSAMLIdentityProvider = apiFactory({
  method: 'DELETE',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'delete_saml_identity_provider',
  },
  retryCount: 1,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      return query;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json;
  },
});

export const updateSAMLIdentityProvider = apiFactory({
  method: 'PATCH',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'update_saml_identity_provider',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      return query;
    },
  ],
  transformBody: [
    (body: any): any => {
      return body;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return {
      login_method: transformSAMLIdentityProvider(json),
    };
  },
});

export const updateSAMLIdentityProviderEnabledOnly = apiFactory({
  method: 'PATCH',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'update_saml_identity_provider_enabled_only',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: [
    Auth.appendToken,
    (query: any): any => {
      return query;
    },
  ],
  transformBody: [
    (body: any): any => {
      return body;
    },
  ],
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return {
      login_method: transformSAMLIdentityProvider(json),
    };
  },
});

export const getSAMLIdentityProvider = apiFactory<ISAMLIdentityProvider[]>({
  method: 'GET',
  headers,
  body: {
    core_section: 'settings',
    core_action: 'get_saml_identity_provider',
  },
  retryCount: 1,
  retryFunc,
  transformQuery: Auth.appendToken,
  transformResponse: (err: any, json: any) => {
    if (err) {
      return new Error((json && json.msg) || 'request failed');
    }
    return json;
  },
});

export async function getUniqueLineItemAliasesMenuList(
  session: ISessionState,
): Promise<Record<string, IMenuList>> {
  const reqUrl = addDomainAndTokenToUrl(
    `/publishers/${session.publisher.ID}/campaigns_unique_aliases`,
  );
  return await restGet<any>(reqUrl, {})
    .then((val: IHttpResponse<any>) => {
      const uniqueLineItemAliases = val.parsedBody?.map((item: IAdserverAlias) => {
        return { Name: item };
      });
      return { line_item_aliases: generateLineItemAliasesMenuList(uniqueLineItemAliases) };
    })
    .catch((e) => {
      console.error(e);
      return {
        line_item_aliases: {},
      };
    });
}

function generateLineItemAliasesMenuList(aliases: IAdserverAlias[]): IMenuList {
  const lineItemAliasesAsIMenuList: IMenuList = {};

  aliases.forEach((alias) => {
    lineItemAliasesAsIMenuList[alias.Name] = {
      ID: alias.Name,
      selected: false,
      title: alias.Name,
    };
  });

  return lineItemAliasesAsIMenuList;
}

export enum TargetingType {
  PresetDisabled = 0,
  PresetEnabled,
}

export const deleteLiverampSegment = async (
  publisherID: string,
  segmentID: string,
): Promise<{ status: boolean } | undefined> => {
  const reqUrl = createUrlWithParam(
    '/delete_liveramp_segments_from_distribution_manager',
    publisherID,
    segmentID,
    'segment_ids',
  );
  try {
    const res = await restDelete<any>(reqUrl, {});
    return res.parsedBody;
  } catch (err) {
    throw err;
  }
};

export interface IPublicaVaultConfig {
  EnableByDefault: boolean;
  ExcludedDemandSources: string;
  ExcludedBidderIDs: string;
  DeviceId: true;
  SiteId: true;
  IP: true;
  ContentRating: true;
  ContentGenre: true;
  TTL: number;
  ContentId?: boolean;
  AppBundle?: boolean;
  StoreBelowFloorBids: boolean;
  MaxUnderFloorType?: number;
  MaxUnderFloorValue?: number;
  MinBidFloor?: number;
}

export const DEFAULT_PUBLICA_VAULT_CONFIG: IPublicaVaultConfig = {
  EnableByDefault: false,
  ExcludedDemandSources: '',
  ExcludedBidderIDs: '',
  DeviceId: true, // cannot be changed
  SiteId: true, // cannot be changed
  IP: true, // cannot be changed
  ContentGenre: true, // cannot be changed
  ContentRating: true, // cannot be changed
  ContentId: false,
  AppBundle: false,
  TTL: 60,
  StoreBelowFloorBids: false,
  MaxUnderFloorType: 0,
  MaxUnderFloorValue: 0,
  MinBidFloor: 0,
};
